import {TranslationTree} from '../types';

const en: TranslationTree = {
    premiumRoyalties: {
        "accrued": "Accrued this month",
        "accruedNote": "Estimate before caps and review; not available to withdraw.",
        "held": "Awaiting review",
        "heldNote": "Includes earlier months. Held amounts require approval.",
        "adjustments": "Monthly adjustments",
        "adjustmentsNote": "Reductions from caps and funding changes.",
        "settlementNote": "Royalties are credited after month end and approval, then released after the configured settlement delay.",
        "pending": "Pending royalties",
        "externalNotice": "Only viewing inside VocalCentric can qualify for royalties. Opening or downloading this file does not qualify.",
        "offlineNotice": "Royalties require a live connection. Offline playback is not counted.",
        "internalNote": "Internal-song royalties follow the artist/partner payout process.",
        "available": "Available Balance"
},
    live: {
        "director": "Director",
        "directorControls": "Director controls",
        "directorHint": "These controls mute microphones for everyone in the room and stop recording those microphones. Singers can turn their own microphone back on.",
        "muteSection": "Mute section",
        "muteForEveryone": "Mute for everyone",
        "endForEveryone": "End for everyone",
        "endConfirm": "End this rehearsal for everyone? Any active take will stop and everyone will leave the call.",
        "notifyPending": "You are connected. Member notifications are pending; we’ll retry automatically.",
        "liveNow": "Live now",
        "liveNowJoin": "Live now · Join",

        "openRoom": "Open live rehearsal",
        "viewRoom": "View call",
        "beforeJoin": "Before you join",
        "you": "You",
        "muted": "Microphone off",
        "closePreview": "Close preview",
        "mixer": "Your mix",
        "joining": "Joining…",
        "ended": "This rehearsal has ended.",
        "noOtherParticipants": "Other singers will appear here when they join.",
        "noTakes": "No recordings yet.",
        "captureUnavailable": "Recording is unavailable. You can still rehearse live.",
        "armed": "Capture armed",
        "timing": "Clock estimate (± ms)",
        "timingHint": "Recording alignment is estimated. Review each take before publishing; device and audio latency still need calibration.",
        "publishHint": "Publishing updates the selected voice parts in this song section.",
        "quality": "Audio connection",
        "excellent": "Excellent",
        "good": "Good",
        "poor": "Poor",
        "lost": "Lost",
        "unknown": "Measuring",
        "title": "Live rehearsal",
        "start": "Start live rehearsal",
        "join": "Join rehearsal",
        "headphones": "I’m using wired headphones.",
        "headphonesWarning": "Wired headphones give the best timing. Speakers may cause echo or feedback, and Bluetooth may add delay. Keep speaker volume low and mute when you’re not singing.",
        "consent": "Allow this rehearsal to record my microphone for Custom Harmony. Turning my microphone off stops recording.",
        "waiting": "Waiting for a director to start the rehearsal.",
        "reopen": "Reopen rehearsal",
        "connected": "Connected",
        "recording": "Recording microphone",
        "monitorHint": "These mixer controls change only what you hear. Other singers and recordings are unaffected.",
        "volume": "Volume",
        "mute": "Mute",
        "solo": "Solo",
        "soprano": "Soprano",
        "alto": "Alto",
        "tenor": "Tenor",
        "bass": "Bass",
        "unassigned": "Unassigned",
        "micOff": "Turn microphone off",
        "micOn": "Turn microphone on",
        "retryUploads": "Retry saved uploads",
        "leave": "Leave rehearsal",
        "song": "Song",
        "section": "Song section",
        "record": "Record take",
        "stop": "Stop take",
        "end": "End rehearsal",
        "refreshTakes": "Refresh takes",
        "takes": "Recorded takes",
        "selectTake": "Select take",
        "publish": "Create Custom Harmony",
        "joinFailed": "Could not join. Check microphone permission and try again.",
        "commandFailed": "The rehearsal changed or the command failed. Refresh and try again.",
        "connectionLost": "Connection interrupted. Recording may continue locally while the app remains active.",
        "captureFailed": "Recording could not continue. Saved audio is retained. Rejoin to record the next take.",
        "uploadPending": "Audio is saved on this device. Retry uploads when connected.",
        "published": "Custom Harmony created. Open the song’s Harmony player to practise.",
        "publishFailed": "Select one ready recording per voice part from the same take, then try again."
},
    common: {
        login: 'Login',
        register: 'Register',
        signUp: 'Sign Up',
        proceed: 'Proceed',
        confirm: 'Confirm',
        verify: 'Verify',
        forgotPassword: 'Forgot Password',
        settings: 'Settings',
        logout: 'Logout',
        profile: 'Profile',
        myApplications: 'My Applications',
        myInvites: 'My Invites',
        operationSuccessful: 'Operation Successful',
        yourEmail: 'your email',
        resendOtp: 'Resend OTP',
        resendOtpIn: 'Resend OTP in {{time}}',
        preferences: 'Preferences',
        preferredCurrency: 'Preferred Currency',
        preferredLanguage: 'Preferred Language',
        saveCurrency: 'Save Currency',
        saveLanguage: 'Save Language',
        save: 'Save',
        tryAgain: 'Try again',
        share: 'Share',
        cancel: 'Cancel',
        close: 'Close',
        delete: 'Delete',
        loading: 'Loading...',
    },
    auth: {
        welcomeBack: 'Welcome Back!',
        loginSubtitle: 'Enter your details to have access to your dashboard',
        email: 'Email',
        password: 'Password',
        enterYourEmail: 'Enter your email',
        enterYourPassword: 'Enter your password',
        noAccount: "Don't have an account?",
        haveAccount: 'Have an account?',
        alreadyHaveAccount: 'Already have an account?',
        forgotPasswordPrompt: 'Please enter your email address',
        welcomeToVocalcentric: 'Welcome to VocalCentric!',
        registerSubtitle: 'Enter your details to create an account',
        registerSubtitleMinimal: 'Create your account in seconds',
        selectPart: 'Select part',
        firstName: 'First Name',
        lastName: 'Last Name',
        username: 'Username',
        phoneNumber: 'Phone Number',
        firstNamePlaceholder: 'First name',
        lastNamePlaceholder: 'Last name',
        usernamePlaceholder: 'Username',
        phonePlaceholder: 'Phone',
        confirmPassword: 'Confirm Password',
        confirmPasswordPlaceholder: 'Confirm your password',
        referral: 'Referral',
        confirmOtp: 'Confirm OTP',
        otpPrompt: 'Please enter the OTP sent to {{email}}',
        forgotPasswordTitle: 'Forgot Password',
        newPassword: 'New Password',
        newPasswordSubtitle: 'Enter your preferred password',
    },
    modals: {
        mobileAppRequired: {
            afterInstall: 'After installing, reopen this link and tap “Open app” to continue where you left off.',
            title: 'Continue in the app',
            description: 'For the best mobile experience, open the app.',
            openApp: 'Open app',
            playStore: 'Google Play',
            appStore: 'App Store',
            continueWeb: 'Continue on web',
            close: 'Close mobile app prompt',
        },
        manageServiceTypes: {
            title: 'Manage Service Types',
            description: 'Create, update, or remove the custom service types this choir uses for scheduling.',
            loading: 'Loading service types...',
            noDescription: 'No description',
            empty: 'No custom service types have been added for this choir yet.',
            emptyHint: 'Use the form on the right to add the first one.',
            edit: 'Edit',
            delete: 'Delete',
            editServiceType: 'Edit Service Type',
            createServiceType: 'Create Service Type',
            editHint: 'Update the service type details below.',
            createHint: 'Add a new custom service type for this choir.',
            name: 'Name',
            namePlaceholder: 'e.g. Sunday Service',
            descriptionLabel: 'Description',
            descriptionPlaceholder: 'Optional note about what this service type is for.',
            recurrence: 'Recurrence',
            weekday: 'Weekday',
            firstServiceDate: 'First Service Date',
            monthlyPattern: 'Monthly Pattern',
            dayOfMonth: 'Day of Month',
            weekOfMonth: 'Week of Month',
            month: 'Month',
            day: 'Day',
            serviceDate: 'Service Date',
            recurrencePreview: 'Recurrence Preview',
            monthlyModesHint: 'Use “Nth weekday in month” for schedules like 1st Thursday, 2nd Thursday, or 5th Thursday.',
            saving: 'Saving...',
            creating: 'Creating...',
            saveChanges: 'Save Changes',
            confirmDeleteDescription: 'Deleting this service type will also remove any day assignments using it. Do you want to continue?',
            updateAction: 'update',
            createAction: 'create',
            weekdays: {
                sunday: 'Sunday',
                monday: 'Monday',
                tuesday: 'Tuesday',
                wednesday: 'Wednesday',
                thursday: 'Thursday',
                friday: 'Friday',
                saturday: 'Saturday',
            },
            months: {
                january: 'January',
                february: 'February',
                march: 'March',
                april: 'April',
                may: 'May',
                june: 'June',
                july: 'July',
                august: 'August',
                september: 'September',
                october: 'October',
                november: 'November',
                december: 'December',
            },
            weekOrdinals: {
                first: '1st',
                second: '2nd',
                third: '3rd',
                fourth: '4th',
                fifth: '5th',
            },
            recurrenceTypes: {
                weekly: 'Weekly',
                biweekly: 'Bi-weekly',
                monthly: 'Monthly',
                annual: 'Annual',
                oneTime: 'One-time',
            },
            monthlyModes: {
                dayOfMonth: 'Fixed day of month',
                nthWeekday: 'Nth weekday in month',
            },
            summary: {
                weeklyOn: 'Weekly on {{weekday}}',
                biweeklyOn: 'Bi-weekly on {{weekday}}',
                biweeklyFrom: 'Bi-weekly on {{weekday}}{{suffix}}',
                fromDate: ' from {{date}}',
                monthlyNthWeekday: 'Monthly on the {{ordinal}} {{weekday}}',
                monthlyDay: 'Monthly on day {{day}}',
                annualOn: 'Annual on {{month}} {{day}}',
                oneTimeOn: 'One-time on {{date}}',
                oneTimeNotSet: 'One-time service date not set',
                noRecurrence: 'No recurrence configured',
            },
            validation: {
                enterName: 'Please enter a service type name',
                chooseWeekday: 'Please choose a weekday',
                chooseFirstServiceDate: 'Please choose the first service date',
                chooseDayOfMonth: 'Please choose the day of month',
                chooseWeekOfMonth: 'Please choose the week of month',
                chooseMonthAndDay: 'Please choose the month and day',
                chooseServiceDate: 'Please choose the service date',
            },
            toast: {
                loadFailed: 'Failed to load service types',
                updated: 'Service type updated',
                created: 'Service type created',
                saveFailed: 'Failed to {{action}} service type',
                deleted: 'Service type deleted',
                deleteFailed: 'Failed to delete service type',
            },
        },
        whitelist: {
            status: {
                approvedTitle: 'Application Approved',
                pendingTitle: 'Application Pending',
                rejectedTitle: 'Application Rejected',
                applyTitle: 'Apply to be Whitelisted',
                reapplyTitle: 'Re-apply for Whitelist',
                approvedMessage: 'Your whitelist application has been approved. You can now access professional profile tools.',
                pendingMessage: 'Your application is pending. An admin will review it within the next 48 hours.',
                rejectedMessage: 'Your previous application was not approved. You can update your details and re-apply now.',
            },
            applicationStatus: 'Application status',
            fillToApply: 'Fill appropriately to apply.',
            closeAria: 'Close whitelist application modal',
            loadingStatus: 'Loading application status...',
            uploadHint: 'Upload a video of yourself showing your skillset and competence level.',
            socialPlaceholder: 'Enter Social Media Profile *',
            socialLabel: 'Social Media Handle',
            reasonPlaceholder: 'Why do you want to be whitelisted? *',
            reasonLabel: 'Reason',
            websitePlaceholder: 'https://***.com',
            websiteLabel: 'Website Link (Optional)',
            submitting: 'Submitting...',
            apply: 'Apply',
            reapply: 'Re-apply',
            unknownState: 'Unable to determine current whitelist application state.',
            toast: {
                submitted: 'Whitelist Application submitted successfully',
                submitFailed: 'Unable to submit whitelist application',
            },
        },
        director: {
            status: {
                approvedTitle: 'Application Approved',
                pendingTitle: 'Application Pending',
                rejectedTitle: 'Application Rejected',
                applyTitle: 'Apply to be Director',
                reapplyTitle: 'Re-apply for Director Role',
                approvedMessage: 'Your director application has been approved. You can now create and manage choirs.',
                pendingMessage: 'Your application is pending. An admin will review it within the next 48 hours.',
                rejectedMessage: 'Your previous application was not approved. You can update your details and re-apply now.',
            },
            applicationStatus: 'Application status',
            fillToApply: 'Fill appropriately to apply.',
            closeAria: 'Close director application modal',
            loadingStatus: 'Loading application status...',
            uploadHint: 'Upload a video of yourself showing your leadership and music direction skillset.',
            socialPlaceholder: 'Enter Social Media Profile *',
            socialLabel: 'Social Media Handle',
            reasonPlaceholder: 'Why do you want to be a director? *',
            reasonLabel: 'Reason',
            websitePlaceholder: 'Website Link',
            websiteLabel: 'Website Link (Optional)',
            apply: 'Apply',
            reapply: 'Re-apply',
            unknownState: 'Unable to determine current director application state.',
            toast: {
                submitted: 'Director Application submitted successfully',
                submitFailed: 'Unable to submit director application',
            },
        },
        shareSetlist: {
            title: 'Share "{{name}}"',
            description: 'Manage visibility, selected songs, and copy a public share link for this setlist.',
            visibility: 'Visibility',
            makePublic: 'Make Setlist Public',
            renameLabel: 'Setlist name',
            renamePlaceholder: 'Enter setlist name',
            saveName: 'Save name',
            savingName: 'Saving...',
            nameRequired: 'Enter a setlist name',
            nameLength: 'Setlist name must be 2-100 characters',
            publishTitle: 'Confirm public name',
            publishDescription: 'This name will be shown anywhere the setlist is shared publicly.',
            confirmPublish: 'Make public',
            cancelPublish: 'Cancel',
            publishing: 'Publishing...',
            selectedSongs: 'Selected Songs',
            remove: 'Remove',
            moveSongUp: 'Move song up',
            moveSongDown: 'Move song down',
            emptySongs: 'This setlist has no songs.',
            addMode: 'Add Mode',
            manualEntry: 'Manual',
            importFromText: 'Import from Text',
            importTextLabel: 'WhatsApp Setlist Text',
            importTextPlaceholder: 'Paste a WhatsApp-style setlist message here...',
            songSource: 'Song Source',
            search: 'Search',
            manualYoutube: 'Manual (YouTube)',
            publicDomain: 'Public Domain',
            searchPlaceholder: 'Search for songs...',
            selectLabelPlaceholder: 'Select a label for the song',
            addSongWithLabel: 'Add Song with Label',
            addExternalSong: 'Add External Song',
            addPublicDomainSong: 'Add Public Domain Song',
            previewImport: 'Preview Import',
            previewingImport: 'Previewing...',
            importPreview: 'Import Preview',
            importPreviewSummary: '{{count}} songs ready to import',
            importToSetlist: 'Import to Setlist',
            importingSetlist: 'Importing...',
            showAddSongs: 'Show add songs',
            hideAddSongs: 'Hide add songs',
            shareLink: 'Share Link',
            copyLink: 'Copy Link',
            public: 'Public',
            private: 'Private',
            toast: {
                linkCopied: 'Link copied to clipboard!',
                visibilityUpdated: 'Setlist is now {{visibility}}',
                updateVisibilityFailed: 'Failed to update visibility',
                renameSuccess: 'Setlist name updated',
                renameFailed: 'Failed to update setlist name',
                noSongSelected: 'No song selected',
                songAlreadyExists: 'Song already exists in setlist',
                selectLabel: 'Please select a label for the song',
                songAdded: 'Song added to setlist',
                errorOccurred: 'An error occurred',
                songRemoved: 'Song removed from setlist',
                removeSongFailed: 'Failed to remove song',
                reorderSongFailed: 'Failed to reorder songs',
                enterImportText: 'Paste setlist text before previewing',
                importPreviewReady: 'Import preview is ready',
                importPreviewFailed: 'Failed to preview imported setlist',
                nothingNewToImport: 'All parsed songs are already in this setlist',
                importSuccess: '{{count}} songs imported into the setlist',
                importFailed: 'Failed to import songs into the setlist',
            },
        },
        addSongToRehearsal: {
            title: 'Add Song to Rehearsal',
            description: 'Choose a song source or import from raw text, then add it to this rehearsal.',
            songSource: 'Song Source',
            search: 'Search',
            manualYoutube: 'Manual (YouTube)',
            publicDomain: 'Public Domain',
            searchPlaceholder: 'Search for songs...',
            cancel: 'Cancel',
            adding: 'Adding...',
            importing: 'Importing...',
            addToRehearsal: 'Add to Rehearsal',
            fallbackArtist: 'Unknown Artist',
            fallbackGenre: 'Unknown',
            destination: 'Rehearsal section',
            additionalSongs: 'Additional songs',
            untitledSetlist: 'Untitled setlist',
            toast: {
                selectSongFirst: 'Please select a song first',
                songAdded: 'Song added to rehearsal',
                addFailed: 'Failed to add song to rehearsal',
                enterImportText: 'Paste setlist text before previewing',
                importPreviewReady: 'Import preview is ready',
                importPreviewFailed: 'Failed to preview imported songs',
                importSuccess: '{{count}} songs added to rehearsal',
                importFailed: 'Failed to add imported songs to rehearsal',
            },
        },
        shareSetlistWithChoir: {
            title: 'Share "{{name}}"',
            description: 'Choose a choir and optionally override the shared setlist name or schedule.',
            chooseChoir: 'Choose a choir',
            loadingChoirs: 'Loading your choirs...',
            searchPlaceholder: 'Search by name, ID, or UUID',
            loading: 'Loading...',
            noMatches: 'No matches',
            nameOptional: 'Name (optional)',
            defaultNamePlaceholder: 'Sunday Service',
            scheduledForOptional: 'Scheduled For (optional)',
            cancel: 'Cancel',
            sharing: 'Sharing...',
            shareToChoir: 'Share to Choir',
            toast: {
                loadChoirsFailed: 'Could not load your choirs',
                sharedToChoir: 'Shared to {{name}}',
                shareFailed: 'Failed to share setlist',
            },
        },
        confirmation: {
            title: 'Confirmation',
            description: 'Do you wish to continue',
            confirm: 'Confirm',
            cancel: 'Cancel',
        },
        addChoirSeats: {
            title: 'Add Seats to Choir',
            description: 'Choose how many additional seats you want to add to this choir.',
            seats: 'Seats',
            seatsOption: '{{count}} Seats',
            cancel: 'Cancel',
            adding: 'Adding...',
            addSeats: 'Add Seats',
            toast: {
                chooseSeats: 'Please choose the number of seats',
                completePaymentInNewTab: 'Please complete the payment in the new tab',
                addFailed: 'Failed to add more seats to choir',
            },
        },
        manageChoirEntitlements: {
            title: 'Premium Seat Sharing',
            sourceDescription: 'Allocate seats from this choir’s Premium subscription or create a secure redemption code.',
            recipientDescription: 'Redeem a secure code supplied by the choir sharing Premium seats with you.',
            directTitle: 'Allocate to a choir',
            recipientChoir: 'Recipient choir ID',
            recipientChoirPlaceholder: 'Choir UUID',
            seats: 'Seats',
            allocate: 'Allocate seats',
            codeTitle: 'Create redemption code',
            codeDescription: 'The code reserves seats immediately and can be redeemed by one choir.',
            generateCode: 'Create code',
            generatedCode: 'Share this code securely',
            allocations: 'Seat allocations',
            noAllocations: 'No seat allocations yet.',
            unredeemedCode: 'Unredeemed code',
            additionalSeats: 'Additional seats',
            increase: 'Increase',
            code: 'Redemption code',
            codePlaceholder: 'VC-...',
            redeemCode: 'Redeem code',
            toast: {
                loadFailed: 'Failed to load seat allocations',
                actionFailed: 'Could not update Premium seat sharing',
                allocated: 'Premium seats allocated',
                codeGenerated: 'Premium seat code created',
                increased: 'Premium seat allocation increased',
                redeemed: 'Premium seat code redeemed',
            },
        },
        createSchedule: {
            title: 'Create New Schedule',
            description: 'Create a schedule window and set the assignment options for this choir.',
            scheduleLabel: 'Schedule Label',
            scheduleLabelPlaceholder: 'July Schedule',
            startDate: 'Start Date',
            endDate: 'End Date',
            autoAssignMembers: 'Auto Assign Members',
            perPartCount: 'Per Part Count',
            cancel: 'Cancel',
            creating: 'Creating...',
            createSchedule: 'Create Schedule',
            toast: {
                fillAllFields: 'Please fill in all fields',
                created: 'Schedule created!',
                createFailed: 'Failed to create schedule',
            },
        },
        inviteChoirMember: {
            title: 'Invite Choir Member',
            description: 'Send an invitation and assign a default part for the new choir member.',
            email: 'Email',
            emailPlaceholder: 'user@example.com',
            assignPart: 'Assign Part',
            addCustomPart: '+ Add Custom Part',
            cancel: 'Cancel',
            sendInvitation: 'Send Invitation',
            linkDescription: 'Copy one choir invitation link and send it to multiple people.',
            copyInvitationLink: 'Copy Invitation Link',
            copyingLink: 'Copying...',
            toast: {
                provideEmailAndPart: 'Please provide email and part',
                invitationSent: 'Invitation sent',
                sendFailed: 'Failed to send invitation',
                linkCopied: 'Invitation link copied',
                linkFailed: 'Failed to copy invitation link',
            },
        },
        generateShareLink: {
            title: 'Generate Share Link',
            description: 'Choose how long the public harmony link should remain active before copying it.',
            expiration: 'Expiration',
            cancel: 'Cancel',
            generating: 'Generating...',
            generateLink: 'Generate Link',
            options: {
                oneDay: { label: '1 Day', hint: 'Expires after 24 hours' },
                sevenDays: { label: '7 Days', hint: 'Good for short review cycles' },
                thirtyDays: { label: '30 Days', hint: 'Recommended for rehearsal sharing' },
                ninetyDays: { label: '90 Days', hint: 'Longer-lived rehearsal access' },
                never: { label: 'Never', hint: 'Link will not expire' },
            },
        },
        renameHarmonyPart: {
            title: 'Rename harmony part',
            description: 'Rename or reassign this transposed harmony part without changing its source recording.',
            partName: 'Part name',
            save: 'Save name',
            saving: 'Saving...',
        },
        serviceDays: {
            title: 'Configure Service Days',
            description: 'Assign a service type or mark no service for each day of the week.',
            empty: 'No service types are available yet, so there is nothing to assign to each day.',
            emptyHint: 'Create a non-recurring weekly service type first, then come back to configure the weekly service days.',
            createServiceType: 'Create Service Type',
            noServiceOption: '-- No Service --',
            cancel: 'Cancel',
            saving: 'Saving...',
            saveChanges: 'Save Changes',
            weekdays: {
                sunday: 'Sunday',
                monday: 'Monday',
                tuesday: 'Tuesday',
                wednesday: 'Wednesday',
                thursday: 'Thursday',
                friday: 'Friday',
                saturday: 'Saturday',
            },
            toast: {
                loadServiceTypesFailed: 'Failed to load service types',
                loadExistingDaysFailed: 'Failed to load existing service days',
                updated: 'Service days updated',
                errorOccurred: 'An error occurred',
            },
        },
        shareHarmonyLink: {
            title: 'Generate Share Link',
            description: 'Choose how long the harmony share link should remain active.',
            closeAria: 'Close share link setup',
            expiration: 'Expiration',
            linkExpiry: 'Link expiry:',
            cancel: 'Cancel',
            generating: 'Generating...',
            generateLink: 'Generate Link',
            options: {
                oneDay: '1 day',
                sevenDays: '7 days',
                thirtyDays: '30 days',
                ninetyDays: '90 days',
                never: 'Never',
            },
        },
        reportContribution: {
            title: 'Report Contribution',
            description: 'Choose the reason that best describes why this contribution should be reviewed.',
            cancel: 'Cancel',
            submitReport: 'Submit Report',
            reasons: {
                wrongPart: 'Wrong part',
                wrongSection: 'Wrong section',
                lowQualityAudio: 'Low quality audio',
                poorPerformance: 'Poor performance',
                spamOrIrrelevant: 'Spam or irrelevant',
                offensiveContent: 'Offensive content',
            },
        },
        reportMarketplaceSong: {
            title: 'Report Marketplace Song',
            description: 'Choose the reason that best explains why this song should be reviewed.',
            detailsPlaceholder: 'Add optional context',
            cancel: 'Cancel',
            submitReport: 'Submit Report',
            reasons: {
                plagiarizedSong: 'Plagiarized song',
                copyrightIssue: 'Copyright issue',
                misleadingMetadata: 'Misleading metadata',
                poorAudioQuality: 'Poor audio quality',
                offensiveContent: 'Offensive content',
                spamOrIrrelevant: 'Spam or irrelevant',
                other: 'Other',
            },
        },
        requestContribution: {
            title: 'Request a Contribution',
            selectedSong: 'Selected: {{title}} - {{artist}}',
            noSongSelected: 'No song selected. Please search for a song first.',
            searchPlaceholder: 'Search for a song (internal or external)',
            selectPart: 'Select Part',
            section: 'Section',
            songKeyOptional: 'Key (optional)',
            bountyXpOptional: 'Bounty XP (optional)',
            additionalNotesOptional: 'Additional Notes (optional)',
            cancel: 'Cancel',
            submitting: 'Submitting...',
            submitRequest: 'Submit Request',
            toast: {
                selectSongAndPartType: 'Please select song and part type',
                submitted: 'Contribution request submitted',
                submitFailed: 'Failed to submit request',
            },
        },
        submitContribution: {
            title: 'Submit a Contribution',
            selectedSong: 'Selected: {{title}} - {{artist}}',
            noSongSelected: 'No song selected. Please search for a song first.',
            searchPlaceholder: 'Search for a song (internal or external)',
            selectPart: 'Select Part',
            sectionPlaceholder: 'Section (e.g. Chorus, Bridge)',
            uploadMode: 'Upload Mode:',
            file: 'File',
            record: 'Record',
            uploadYourAudio: 'Upload your audio',
            cancel: 'Cancel',
            submitting: 'Submitting...',
            submitContribution: 'Submit Contribution',
            toast: {
                recordingUploadFailed: 'Recording upload failed',
                completeAllFields: 'Please complete all fields before submitting.',
                submittedSuccessfully: 'Your contribution was submitted successfully!',
                submissionFailed: 'Submission failed',
            },
        },
        submitChallengeEntry: {
            title: 'Submit Entry',
            description: 'Upload your challenge performance and submit it to the leaderboard.',
            uploadVideo: 'Upload Challenge Entry Video',
            captionPlaceholder: 'Tell people about your entry',
            cancel: 'Cancel',
            submitting: 'Submitting...',
            submitEntry: 'Submit Entry',
        },
        updateDefaultPart: {
            title: 'Choose your default part and username!',
            description: 'What part would you like to sing or play? This will be used as your default part.',
            selectDefaultPart: 'Select default part',
            chooseUsername: 'Choose a username',
            usernameValidation: 'Username must be 3-15 characters long and can only contain letters, numbers, and underscores.',
            chooseDifferentUsername: 'Choose a different username to continue.',
            usernameTaken: 'This username is already taken.',
            loading: '...Loading',
            setDefaultPart: 'Set Default Part',
            toast: {
                profileUpdated: 'Profile updated successfully',
            },
        },
        challengeVote: {
            title: 'Vote',
            description: 'Enter your review',
            commentPlaceholder: 'Comment',
            score: 'Score {{score}}%',
            disqualify: 'Disqualify',
            cancel: 'Cancel',
            submitting: 'Submitting...',
            submitVote: 'Submit Vote',
        },
        deleteHarmonyConfirm: {
            cancel: 'Cancel',
            delete: 'Delete',
            deleting: 'Deleting...',
        },
        shareSchedule: {
            title: 'Share Schedule',
            description: 'Generate a public schedule link for sharing, or download the current schedule as a PDF.',
            closeAria: 'Close schedule share modal',
            publicScheduleLink: 'Public schedule link',
            publicScheduleHint: 'Choose how long the public link should remain active before you generate it.',
            linkExpiry: 'Link expiry',
            generating: 'Generating...',
            generateLink: 'Generate Link',
            regenerateLink: 'Regenerate Link',
            generatedLink: 'Generated link',
            readyToShare: 'Ready to share',
            copyLink: 'Copy Link',
            openLink: 'Open Link',
            generateToReveal: 'Generate a link to reveal the shareable public schedule URL here.',
            pdfDownload: 'PDF download',
            pdfDescription: 'Download the current schedule as a printable PDF.',
            pdfHint: 'Use this when the director wants a fixed printable copy instead of a live public page.',
            preparingPdf: 'Preparing PDF...',
            downloadPdf: 'Download PDF',
            options: {
                oneDay: '1 day',
                sevenDays: '7 days',
                thirtyDays: '30 days',
                ninetyDays: '90 days',
                never: 'Never',
            },
            toast: {
                linkCopied: 'Link copied to clipboard',
            },
        },
        fullScreen: {
            confirmationIllustrationAlt: 'confirmation illustration',
            passwordResetSuccess: 'Congratulations your password has been reset.',
            login: 'Login',
        },
        postSignupOnboarding: {
            welcomeToVocalcentric: 'Welcome to VocalCentric',
            skipForNow: 'Skip for now',
            back: 'Back',
            continue: 'Continue',
            steps: {
                welcome: {
                    title: "Let's get you set up fast",
                    description: 'VocalCentric helps you practice your part, rehearse smarter with your choir, discover songs and stems, and grow through community participation.',
                    items: {
                        practiceWithStems: 'Practice with stems and isolated parts',
                        prepareRehearsals: 'Prepare rehearsals and organize setlists',
                        collaborate: 'Collaborate in choirs, projects, and communities',
                        buildConsistency: 'Build consistency with structured music workflows',
                    },
                },
                interests: {
                    title: 'What brings you here?',
                    description: 'Pick one or more to personalize your start.',
                },
                role: {
                    title: 'How are you using VocalCentric?',
                    description: 'Optional, but it helps us route you better.',
                },
                recommendation: {
                    title: 'Recommended starting point',
                    description: 'Based on your selections, this is the best first place to begin.',
                    recommendedLabel: 'Recommended',
                    exploreEverythingInstead: 'Explore everything instead',
                },
            },
            toast: {
                saveFailed: 'Unable to save onboarding preferences',
            },
        },
        personalizedOnboarding: {
            title: 'Personalized setup',
            skipForNow: 'Skip for now',
            back: 'Back',
            continue: 'Continue',
            finish: 'Finish setup',
            saveFailed: 'Unable to save onboarding progress',
            intentTitle: 'What brings you here?',
            intentDescription: 'Pick one or more to personalize your start.',
            roleTitle: 'How are you using VocalCentric?',
            roleDescription: 'Optional, but it helps us route you better.',
            profileTitle: 'Set up your starting path',
            profileDescription: 'Quick profile choices now will personalize your next step.',
            usernameStepTitle: 'Choose your username',
            usernameStepDescription: 'This helps collaborators find you and keeps your profile consistent.',
            partStepTitle: 'Pick your role',
            partStepDescription: 'Choose your primary role (tenor, alto, pianist, and more) for better guidance.',
            nameStepTitle: 'Add your name',
            nameStepDescription: 'Use your real first and last name so members can recognize you.',
            recommendationTitle: 'Your best next action',
            recommendationDescription: 'Start here to reach your first meaningful action quickly.',
            recommendedLabel: 'Recommended',
            exploreInstead: 'Explore Home instead',
            selectPart: 'Select role',
            usernameLabel: 'Username',
            usernamePlaceholder: 'Choose a username',
            usernameRequired: 'Username is required',
            usernameValidation: 'Username must be 3-30 characters and use letters, numbers, or underscores.',
            usernameTaken: 'This username is already taken.',
            usernameValidationUnavailable: 'Unable to validate username right now.',
            firstNameLabel: 'First Name',
            lastNameLabel: 'Last Name',
            firstNamePlaceholder: 'First name',
            lastNamePlaceholder: 'Last name',
            firstNameRequired: 'First name is required.',
            lastNameRequired: 'Last name is required.',
            defaultNameValidation: 'Please replace the default New User name.',
            roleOptions: {
                choir_member: {
                    label: 'Choir Member',
                    description: 'I mostly rehearse and perform as a choir participant.',
                },
                vocalist: {
                    label: 'Solo Vocalist / Artist',
                    description: 'I want tools to strengthen my personal performance workflow.',
                },
                choir_director: {
                    label: 'Choir Director/Admin',
                    description: 'I want to organize rehearsals, people, and choir flow.',
                },
                vocal_coach: {
                    label: 'Vocal Coach / Teacher',
                    description: 'I want to teach students and organize courses, cohorts, and assignments.',
                },
                producer: {
                    label: 'Producer',
                    description: 'I contribute songs and stems to the marketplace.',
                },
                project_owner: {
                    label: 'Project Owner',
                    description: 'I create projects and invite others to collaborate.',
                },
                collaborator: {
                    label: 'Collaborator',
                    description: 'I explore gigs/projects and keep my profile ready for opportunities.',
                },
                exploring: {
                    label: 'Just Exploring',
                    description: 'I want the fastest guided path and can refine later.',
                },
            },
            intentOptions: {
                practice_part: {
                    label: 'Practice my part',
                    description: 'Use stems and focused playback to lock in your section.',
                },
                join_manage_choir: {
                    label: 'Join or manage a choir',
                    description: 'Work with members, parts, and choir activities in one place.',
                },
                prepare_rehearsals: {
                    label: 'Prepare rehearsals',
                    description: 'Get rehearsal-ready with setlists, schedules, and song organization.',
                },
                discover_songs_stems: {
                    label: 'Discover songs and stems',
                    description: 'Browse songs, artists, and musical resources for rehearsal.',
                },
                improve_vocal_skills: {
                    label: 'Improve my vocal skills',
                    description: 'Build consistency with structured practice and performance focus.',
                },
                teach_students: {
                    label: 'Teach students',
                    description: 'Organize a curriculum, assign exercises, and track student progress.',
                },
                explore_community_challenges: {
                    label: 'Explore community and challenges',
                    description: 'Join contributions, reels, and challenge-driven engagement.',
                },
                work_on_projects: {
                    label: 'Work on projects',
                    description: 'Find collaboration opportunities and manage project workflows.',
                },
                just_explore: {
                    label: 'Just explore',
                    description: 'I want to look around and discover what fits best.',
                },
                contribute_stems: {
                    label: 'Contribute songs and stems',
                    description: 'Upload marketplace-ready content and maintain your catalog.',
                },
                recruit_collaborators: {
                    label: 'Recruit collaborators',
                    description: 'Invite qualified contributors to active opportunities.',
                },
            },
            recommendations: {
                director_first_choir: {
                    title: 'Create your first choir',
                    description: 'Start with a choir workspace first, then set up rehearsals inside it.',
                    ctaLabel: 'Create choir',
                },
                coach_first_workspace: {
                    title: 'Create your teaching workspace',
                    description: 'Start a standalone teaching space, then add courses and student cohorts.',
                    ctaLabel: 'Start teaching',
                },
                producer_upload_song: {
                    title: 'Upload your first song',
                    description: 'Upload flows are available for whitelisted accounts. Continue to account uploads to get started.',
                    ctaLabel: 'Open uploads',
                },
                project_owner_first_project: {
                    title: 'Create your first project',
                    description: 'Create a project first, then invite collaborators to start active work.',
                    ctaLabel: 'Create project',
                },
                collaborator_discover_projects: {
                    title: 'Explore active projects',
                    description: 'Discover open gigs and collaboration opportunities that match your profile.',
                    ctaLabel: 'Open projects',
                },
                projects_first_step: {
                    title: 'Start your first project flow',
                    description: 'Open projects to discover active collaboration opportunities quickly.',
                    ctaLabel: 'Open projects',
                },
                community_first_step: {
                    title: 'Explore community challenges',
                    description: 'Join one challenge to see how VocalCentric drives consistent engagement.',
                    ctaLabel: 'Open challenges',
                },
                practice_general: {
                    title: 'Hear your part in isolation',
                    description: 'Start with your selected part so VocalCentric can guide your practice more accurately.',
                    ctaLabel: 'Play my part',
                },
                explore_first_practice: {
                    title: 'Start exploring VocalCentric',
                    description: 'Browse key modules and take one action to discover the best path for you.',
                    ctaLabel: 'Explore now',
                },
            },
        },
        upgrade: {
            takeYourChoralExperience: 'Take your choral experience',
            toNextLevel: 'to the next level',
            premiumDescription: 'Upgrading to premium unlocks a world of exclusive features designed to supercharge your vocal training and leadership skills.',
            advancedRehearsalTools: 'Advanced Rehearsal Tools:',
            advancedRehearsalDescription: 'Take control of your rehearsals with premium features like customizable warm-up exercises, section-specific tracks (soprano, alto, tenor, bass), and variable tempo control to master challenging passages.',
            directorsToolkit: "Director's Toolkit:",
            directorsToolkitDescription: 'Become a choral leader with exclusive features like score annotation tools, performance note sharing with your choir members, and advanced analytics to track individual and group progress.',
            seats: 'Seats:',
            seatsDescription: 'Add or remove seats for your choir members.',
            notDirector: 'You are not the director of this choir, hence you cannot upgrade.',
            choirBillingAdminTitle: 'Contact your choir admin',
            contactChoirAdmin: 'This choir subscription needs attention. Please contact your choir admin or billing member to update the choir plan or payment before using this feature.',
            upgrade: 'Upgrade',
            toast: {
                planNotFound: 'Plan not found',
            },
        },
        upload: {
            clickToUploadDocument: 'Click to upload Document',
            supportedFormats: 'Pdf, txt, ppt, docx  (max. 20MB)',
            upload: 'Upload',
            cancel: 'Cancel',
        },
        setRehearsalSongKey: {
            title: 'Set Song Key',
            description: 'Choose a default key for this song. You can use our auto-transpose feature to adjust parts as needed.',
            note: 'NB: This will not change the original key of the song, just the default for this rehearsal.',
            songKey: 'Song Key',
            placeholder: 'Select song key for this rehearsal',
            setKey: 'Set Key',
            toast: {
                selectSongKey: 'Please select a song key',
            },
        },
        addCustomSection: {
            title: 'Add Custom Section',
            description: 'Enter the name of the section you want to add, such as Intro, Bridge, or Tag.',
            sectionName: 'Section Name',
            placeholder: 'e.g., Bridge',
            addSection: 'Add Section',
        },
        addCustomPart: {
            title: 'Add Custom Part',
            description: 'Pick the harmony part you want to add to this section.',
            partName: 'Part Name',
            placeholder: 'Select default part',
            addPart: 'Add Part',
        },
        duplicateMidiPart: {
            title: 'Duplicate MIDI Track',
            description: 'Pick the part this MIDI duplicate should become. You can edit the duplicated part afterward in the MIDI editor.',
            targetPart: 'Target Part',
            selectPart: 'Select part',
            destinationInterval: 'Destination Interval',
            duplicate: 'Duplicate',
            intervals: {
                '0': 'Same melody',
                '4': '3rd above',
                '-4': '3rd below',
                '7': '5th above',
                '-7': '5th below',
            },
        },
        addSongToSetlist: {
            title: 'Add Song to Setlist',
            description: 'Select or create setlists to include this song.',
            createNewSetlist: '+ Create new Setlist',
            songSource: 'Song Source',
            backToExisting: '← Back to existing',
            source: {
                search: 'Search',
                manualYoutube: 'Manual (YouTube)',
                publicDomain: 'Public Domain',
            },
            selectLabelPlaceholder: 'Select a label for this song...',
            setlistNamePlaceholder: 'New Setlist Name',
            searchSongsPlaceholder: 'Search for songs...',
            noSongs: 'No songs',
            songCount: '{{count}} song(s)',
            createAndAdd: 'Create & Add',
            addToSetlists: 'Add to Setlists',
            toast: {
                setlistUpdatedSuccessfully: 'Setlist updated successfully',
                noChangesMade: 'No changes made',
                failedToUpdateSetlists: 'Failed to update setlists',
                selectLabelForSong: 'Please select a label for this song',
                completeRequiredFields: 'Please complete the required fields',
                newSetlistCreatedAndSongAdded: 'New Setlist created and song added',
                failedToCreateSetlist: 'Failed to create Setlist',
            },
        },
    },
    components: {
        autoCompleteSearch: {
            fromSpotify: 'From Spotify',
            fromLibrary: 'From Library',
            nothingFound: 'Nothing found.',
        },
        manualSongInput: {
            unlabeled: 'Unlabeled',
            youtubeLink: 'YouTube Link',
            songTitle: 'Song Title',
            artistName: 'Artist Name',
            invalidYoutubeLink: 'Invalid YouTube link.',
            youtubeThumbnailAlt: 'YouTube Thumbnail',
            selectLabelForSong: 'Select a label for the song',
        },
        publicSongInput: {
            unlabeled: 'Unlabeled',
            songTitle: 'Song Title',
            composerOrSource: 'Composer or Source',
            selectLabelForSong: 'Select a label for the song',
        },
        searchInput: {
            searchAlt: 'search',
            clearAlt: 'clear search',
            defaultPlaceholder: 'Search song, artist, score, album',
        },
        songSearchInput: {
            searchForSongs: 'Search for songs...',
            selectedSong: 'Selected: {{title}} – {{artist}}',
        },
        songArt: {
            userAlt: 'user',
        },
        passwordInput: {
            updatePassword: 'Update Password',
        },
        planCard: {
            currentPlan: 'Current Plan',
            payment: 'Payment',
            nextBillPrefix: 'Your next bill is for ',
            on: ' on ',
            perMonth: 'Per Month',
            processing: 'Processing...',
            active: 'Active',
            upgrade: 'Upgrade',
            downgrade: 'Downgrade',
        },
        floatingSupport: {
            openSupportMenuAria: 'Open support menu',
            support: 'Support',
            needHelp: 'Need help?',
            weAreHereForYou: 'We’re here for you.',
            closeSupportMenuAria: 'Close support menu',
            contactSupport: 'Contact Support',
            sendUsAMessageFromTheApp: 'Send us a message from the app',
            emailUs: 'Email us',
            accountDeletion: 'Account Deletion',
            googleAccountDeletionSteps: 'Google account deletion steps',
            tipPrefix: 'Tip: Press',
            escKey: 'Esc',
            tipSuffix: 'to close.',
        },
        switchInput: {
            useSetting: 'Use setting',
        },
        mobileSidebar: {
            search: 'Search',
        },
        customPopover: {
            menuAlt: 'menu',
            select: 'Select',
            searchPlaceholder: 'Search...',
            nothingFound: 'Nothing found',
        },
        partSelectDropdown: {
            selectPart: 'Select part',
            fromLibrary: 'From Library',
            noPartsFound: 'No parts found.',
        },
        keySelectDropdown: {
            selectSongKey: 'Select song key',
            fromLibrary: 'From library',
            noKeysFound: 'No keys found.',
        },
        creditCard: {
            cardNumberLabel: 'Card Number',
            cardNumberPlaceholder: 'Enter your card number',
            cardTypeIconAlt: '{{name}} icon',
            cvvLabel: 'CVV',
            cvvPlaceholder: 'CVV',
            expiryDateLabel: 'Expiry Date',
            expiryDatePlaceholder: 'MM/YY',
        },
        practiseCards: {
            allSectionsSubmitted: 'All sections submitted. Great job!',
            progress: 'Progress: {{submittedCount}} of {{totalSections}} sections submitted',
            nothingHereYet: 'Nothing here yet',
        },
        upgrade: {
            previewOnlyMessage: 'You can only preview the original tracks. Upgrade to our premium plan to view all tracks.',
            upgradeToSeeAllTracks: 'Upgrade to See All Tracks',
        },
        toast: {
            musicAlt: 'Music',
        },
        choirCard: {
            leaveChoir: 'Leave Choir',
            choirAlt: 'Choir',
            choirAvatarAlt: 'Choir Avatar',
            members: '{{count}} Members',
            noMembers: 'No Members',
            created: 'Created:',
            toast: {
                choirDeleted: 'Choir Deleted Successfully',
            },
        },
        audioPlayer: {
            vocalPlaying: 'Vocal Playing',
            expandToFullView: 'Expand to full View',
            fullscreenAlt: 'fullscreen',
            playbackSpeed: 'speed+',
            speed: {
                slow: 'Slow',
                normal: 'Normal',
                faster: 'Faster',
                fastest: 'Fastest',
            },
        },
        audioController: {
            repeatAlt: 'repeat',
            pauseAlt: 'pause',
            playAlt: 'play',
            micAlt: 'mic',
            speakerAlt: 'speaker',
            initialTime: '00:00',
        },
        multiTrackWaveform: {
            mute: 'Mute',
            solo: 'Solo',
            pause: 'Pause',
            play: 'Play',
            sync: 'Sync',
        },
        header: {
            userAvatarAlt: 'user avatar',
            premium: 'Premium',
            moduleHelp: {
                openHelpAria: 'Open help video for {{module}}',
                firstVisit: 'First time in {{module}}? Watch guide',
                watchGuide: 'Need help? Watch guide',
                watchGuideShort: 'Help',
                videoTitle: '{{module}} walkthrough',
                primaryAction: 'Go to {{module}}',
                openInNewTab: 'Open in new tab',
                close: 'Close',
                unavailable: 'This help video is not configured yet. Set the module help video URL to enable it.',
                guidance: {
                    community_challenges: 'Open a challenge, review the prompt, then submit or share when you are ready.',
                    community_reels: 'Browse reels to see community activity, then create or engage with a reel when you have something to share.',
                    community_contributions: 'Explore community contributions, then open one item to listen, comment, or contribute.',
                    community_requests: 'Review open requests, then contribute the part or stem that matches your skill.',
                    choirs_overview: 'Create your choir first, then move through rehearsals, setlists, schedule, members, and integrations to complete setup.',
                    choir_rehearsals: 'After rehearsals, move to setlists so your team has a clear song flow for each service.',
                    choir_setlists: 'Setlists are ready. Next, update the schedule so rehearsals and services align with your setlist plan.',
                    choir_schedule: 'Schedule is in place. Next, confirm member assignments so each part is covered.',
                    choir_members: 'Members are configured. Continue to integrations so external workflow sync is ready.',
                    choir_integrations: 'Finalize integrations, then return to rehearsals to run your live practice cycle.',
                    choir_rehearsal_runthrough: 'Complete this run-through, then review submissions to give feedback before the next rehearsal.',
                    projects_discover: 'Start by creating a project so role, budget, and collaboration settings can be managed in one flow.',
                    projects_create: 'Project created. Continue to My Projects to open and manage your active work.',
                    projects_mine: 'From My Projects, review invites and applications so contributors can be confirmed quickly.',
                    projects_invites: 'Handle project invites, then check applications to shortlist the best-fit collaborators.',
                    projects_applications: 'After applications, move into the project detail workflow to manage members and delivery.',
                    project_overview: 'Review the project summary, then continue to applications to evaluate contributors.',
                    project_applications: 'Shortlist or accept contributors here, then move to members for team coordination.',
                    project_members: 'Team is set. Continue to budget to align payouts and compensation rules.',
                    project_budget: 'Budget is configured. Continue to agreements so contributors can accept terms.',
                    project_agreements: 'Track agreement status, then move to invites to fill remaining roles.',
                    project_invites: 'Finalize outstanding invites, then return to overview to monitor overall project health.',
                    uploads: 'Upload song information and at least one useful stem, then submit it for marketplace or community processing.',
                },
                whatToDoNext: {
                    title: 'What to do next',
                    steps: {
                        choirs_overview: 'Choir overview',
                        choir_rehearsals: 'Rehearsals',
                        choir_setlists: 'Setlists',
                        choir_schedule: 'Schedule',
                        choir_members: 'Members',
                        choir_integrations: 'Integrations',
                        choir_rehearsal_runthrough: 'Run-through',
                        choir_rehearsal_review: 'Review submissions',
                    },
                    copy: {
                        choirs_overview: 'Create a choir first, then open rehearsals to start the weekly workflow.',
                        choir_rehearsals: 'Set rehearsal goals and song focus, then continue to setlists.',
                        choir_setlists: 'Finalize songs for each rehearsal or service, then set the calendar schedule.',
                        choir_schedule: 'Confirm dates and time slots, then assign members for each part.',
                        choir_members: 'Assign voices and responsibilities, then connect integrations.',
                        choir_integrations: 'Finish external integrations, then return to rehearsals for the next cycle.',
                        choir_rehearsal_runthrough: 'Complete this run-through and continue to review for feedback and approvals.',
                    },
                    cta: {
                        choirs_overview: 'Go to Create Choir',
                        choir_rehearsals: 'Go to Setlists',
                        choir_setlists: 'Go to Schedule',
                        choir_schedule: 'Go to Members',
                        choir_members: 'Go to Integrations',
                        choir_integrations: 'Go to Rehearsals',
                        choir_rehearsal_runthrough: 'Go to Review',
                    },
                },
            },
            quickStart: {
                label: 'Quick start',
                eyebrow: 'Guided adoption',
                title: 'Your next product wins',
                progress: '{{completed}} of {{total}} completed',
                primaryBadge: 'Recommended next',
                cta: 'Open step',
                header: {
                    early: {
                        badge: 'Getting started',
                        title: '{{role}} setup guide',
                        description: 'Complete the essentials first so VocalCentric can shape the right workflow around you.',
                    },
                    mid: {
                        badge: 'In progress',
                        title: '{{role}} adoption guide',
                        description: 'Your basics are in place. Finish the next recommended move, then branch into the rest of the product.',
                    },
                    mature: {
                        badge: 'Setup complete',
                        title: '{{role}} growth guide',
                        description: 'Your core setup is done. Use these next steps to deepen how you work across VocalCentric.',
                    },
                },
                phaseLabels: {
                    early: 'Early stage',
                    mid: 'Expanding',
                    mature: 'Established',
                },
                groups: {
                    completeSetup: 'Complete setup',
                    recommendedNext: 'Recommended next',
                    exploreMore: 'Explore more',
                    completed: 'Completed',
                },
                roles: {
                    choirDirector: 'Choir director',
                    choirMember: 'Choir member',
                    vocalist: 'Vocalist',
                    producer: 'Producer',
                    projectOwner: 'Project owner',
                    collaborator: 'Collaborator',
                    explorer: 'Explorer',
                },
                categories: {
                    profile: 'Profile',
                    practice: 'Practice',
                    choirs: 'Choirs',
                    projects: 'Projects',
                    community: 'Community',
                    uploads: 'Uploads',
                },
                items: {
                    chooseRole: {
                        title: 'Choose how you use VocalCentric',
                        description: 'Set your role first so the product can prioritize the right workflows.',
                    },
                    choosePart: {
                        title: 'Set your main part or instrument',
                        description: 'Use your default part to tailor practice, stems, and choir guidance.',
                    },
                    confirmUsername: {
                        title: 'Confirm your username',
                        description: 'Make your profile findable before you start collaborating across modules.',
                    },
                    hearPart: {
                        title: 'Hear your part in isolation',
                        description: 'Open the library and switch to your stem so the first listen is useful.',
                    },
                    submitPractice: {
                        title: 'Submit your first practice',
                        description: 'Complete one practice flow so your rehearsal progress becomes real work.',
                    },
                    tryGames: {
                        title: 'Try quick games',
                        description: 'Jump into short ear, pitch, and music theory games.',
                    },
                    joinChoir: {
                        title: 'Join a choir',
                        description: 'Explore available choirs and send one join request to enter a live workflow.',
                    },
                    createChoir: {
                        title: 'Create your first choir',
                        description: 'Start a choir workspace so rehearsals, members, and setlists have a home.',
                    },
                    createRehearsal: {
                        title: 'Create a rehearsal',
                        description: 'Move from choir setup into an actual rehearsal workflow your team can use.',
                    },
                    createSetlist: {
                        title: 'Create a setlist',
                        description: 'Group songs into a real service or rehearsal plan before scheduling the team.',
                    },
                    inviteMember: {
                        title: 'Invite your first member',
                        description: 'Bring one person into the choir so collaboration starts immediately.',
                    },
                    uploadFirstSong: {
                        title: 'Upload your first song or stem',
                        description: 'Open the upload flow to contribute content to the marketplace pipeline.',
                    },
                    createProject: {
                        title: 'Create your first project',
                        description: 'Start a project so roles, deliverables, and collaboration have a clear home.',
                    },
                    applyToProject: {
                        title: 'Apply to a project',
                        description: 'Explore live opportunities and submit one application that matches your role.',
                    },
                    inviteProjectCollaborator: {
                        title: 'Invite a collaborator',
                        description: 'Fill one project role so the work can move from setup into execution.',
                    },
                    joinChallenge: {
                        title: 'Join a community challenge',
                        description: 'Take one community action so the product starts opening up beyond your main path.',
                    },
                },
            },
        },
        breadcrumb: {
            home: 'Home',
            community: 'Community',
            marketplace: 'Marketplace',
            choirs: 'Choirs',
            teaching: 'Schools',
            projects: 'Projects',
            rehearsals: 'Rehearsals',
            accountInformation: 'Account Information',
            vocalContributions: 'Vocal Contributions',
            uploads: 'Uploads',
            requestSongs: 'Request Songs',
            requestRole: 'Request Role',
            notifications: 'Notifications',
            transactionHistory: 'Transaction History',
            search: 'Search',
        },
        routeItems: {
            songLibrary: 'Song Library',
            community: 'Community',
            marketplace: 'Marketplace',
            rehearsals: 'Rehearsals',
            exercises: 'Exercises',
            choirs: 'Choirs',
            projects: 'Projects',
            requestSongs: 'Request Songs',
            personal: 'Personal',
            vocalContributions: 'Vocal Contributions',
            setlists: 'Setlists',
            settings: 'Settings',
            accountInformation: 'Account Information',
            requestRole: 'Request Role',
            transactionHistory: 'Transaction History',
            notifications: 'Notifications',
        },
        memberTableItem: {
            userAlt: 'user',
            menu: {
                makeModerator: 'Make Moderator',
                markWorshipLeadCandidate: 'Mark as Auto Lead',
                removeWorshipLeadCandidate: 'Remove Auto Lead',
                removeMember: 'Remove Member',
            },
            confirmation: {
                grantModeratorPrivileges: 'This individual will have the capability to create and adjust scheduled rehearsals, manage member additions and removals. Would you like to proceed and grant moderator privileges?',
                markWorshipLeadCandidate: 'Mark this member as eligible for auto-generated lead assignments?',
                removeWorshipLeadCandidate: 'Remove this member from auto-generated lead assignments?',
                approveRequest: 'Would you like to approve this request?',
                rejectRequest: 'Would you like to reject this request?',
            },
            toast: {
                songAddedToRehearsals: 'Song Added to Rehearsals',
                memberMadeModeratorSuccessfully: 'Member made moderator successfully',
                userSuspendedSuccessfully: 'User suspended successfully',
                userBlockedSuccessfully: 'User blocked successfully',
                userRemovedSuccessfully: 'User removed successfully',
                memberMarkedWorshipLeadSuccessfully: 'Member marked as an auto lead candidate',
                memberRemovedWorshipLeadSuccessfully: 'Member removed from auto lead candidates',
                memberLeadCandidateUpdateFailed: 'Failed to update auto lead candidate',
                requestApprovedSuccessfully: 'Request approved successfully',
                requestRejectedSuccessfully: 'Request rejected successfully',
                upgradeSeatsToApproveRequest: 'No available seats. Upgrade seats to approve this request.',
            },
        },
        memberTable: {
            name: 'Name',
            email: 'Email',
            part: 'Part',
            dateJoined: 'Date Joined',
        },
        communityCard: {
            upvoteFailed: 'Failed to upvote',
            songTitle: 'Song Title',
            expandToFullView: 'Expand to full View',
            fullscreenAlt: 'fullscreen',
            songArtAlt: 'Song Art',
            missingPartAlt: 'Missing part',
            partNeeded: 'Part Needed',
            topContributors: 'Top Contributors',
            contributorFallback: 'Contributor',
            contributorAvatarAlt: 'contributor avatar',
            seeAllContributions: 'See all contributions',
        },
        communityCards: {
            loadFailed: 'Failed to load community entries',
            errorGeneric: 'Something went wrong fetching community contributions.',
            searchBySongTitle: 'Search by song title',
            all: 'All',
            errorAlt: 'Error',
            emptyAlt: 'No contributions',
            emptyDescription: 'No community entries found for your search.',
            emptyPrompt: 'Want to get help from the community?',
            requestContribution: 'Request a Contribution',
            loadMore: 'Load More',
        },
        requestCards: {
            loadFailed: 'Failed to load requests',
            searchBySongTitle: 'Search by song title',
            all: 'All',
            emptyAlt: 'No contributions',
            emptyDescription: 'No community entries found for your search.',
            emptyPrompt: 'Want to get help from the community?',
            requestContribution: 'Request a Contribution',
            loadMore: 'Load More',
        },
    },
    account: {
        currencyUpdated: 'Preferred currency updated.',
        languageUpdated: 'Preferred language updated.',
        unableToUpdateCurrency: 'Unable to update preferred currency.',
        unableToUpdateLanguage: 'Unable to update preferred language.',
    },
    accountInfo: {
        breadcrumbBase: 'Account Information',
        actions: {
            editProfile: 'Edit Profile',
            updateCard: 'Update Card',
        },
        sections: {
            subscription: 'Subscription',
            payments: 'Payments',
            marketplace: 'Marketplace',
            community: 'Community',
            privacy: 'Privacy',
            help: 'Help',
        },
        links: {
            availablePlans: 'Available Plans',
            managePlans: 'Manage Plans',
            orderHistory: 'Order History',
            savedPaymentCard: 'Saved Payment Card',
            myPurchases: 'My Purchases',
            myOrders: 'My Orders',
            uploadSong: 'Upload Song',
            myListings: 'My Listings',
            earnings: 'Earnings',
            myChallenges: 'My Challenges',
            notifications: 'Notifications',
            support: 'VocalCentric Support',
        },
        languages: {
            en: 'English',
            es: 'Spanish',
            fr: 'French',
            zh: 'Chinese',
        },
        activePlan: {
            title: 'Active Plan',
            yourPlan: 'Your Plan',
            freePlan: 'Free Plan',
            referralCopied: 'Referral link copied to clipboard',
            nextBill: 'Your next bill is for {{price}} on {{date}}',
            currentlyFree: 'You are currently on a free plan,',
            upgradeToPremium: 'upgrade to premium',
        },
        editProfile: {
            updated: 'Profile updated successfully',
            selectDefaultPart: 'Select default part',
            enterCountry: 'Enter Country',
            country: 'Country',
            update: 'Update',
        },
        payment: {
            title: 'Saved Payments Card',
            subtitle: 'Manage your payment details for one-time purchases. To manage payment details for your monthly subscription, go to Account overview.',
        },
        changePassword: {
            title: 'Change Password',
            step1Subtitle: 'To set a new VocalCentric password, you need to re-enter your old password.',
            enterOldPassword: 'Enter your old password',
            step2Subtitle: 'Please enter the OTP sent to your email.',
            step3Subtitle: 'Kindly set your preferred password',
            success: 'Congratulations, you have successfully changed your password.',
        },
        challenges: {
            subtitle: 'Manage your challenge history and complete funding for cash reward challenges.',
            create: 'Create Challenge',
            empty: 'You have not created any challenges yet.',
            explore: 'Explore Community',
            entries: 'entries',
            interests: 'interests',
            created: 'Created',
            fundingRequired: 'Funding required',
            view: 'View',
            funding: 'Funding...',
            fundNow: 'Fund now',
            updating: 'Updating...',
            moveToUpcoming: 'Move to Upcoming',
            filters: {
                all: 'All',
                active: 'Live',
                upcoming: 'Upcoming',
                closed: 'Past',
                draft: 'Draft',
            },
            sort: {
                highestReward: 'Highest Reward',
                latest: 'Latest',
                mostEntries: 'Most Entries',
            },
            table: {
                title: 'Title',
                status: 'Status',
                entries: 'Entries',
                reward: 'Reward',
                dates: 'Dates',
                action: 'Action',
            },
            status: {
                active: 'Live',
                upcoming: 'Upcoming',
                closed: 'Past',
                draft: 'Draft',
                pendingWinner: 'Pending Winner',
            },
            toast: {
                fundingWindowOpened: 'Funding window opened. Complete payment to move challenge toward live status.',
                fundingInitialized: 'Funding initialized successfully.',
                alreadyFunded: 'Challenge is already funded. Refreshed funding status.',
                fundingFailed: 'Failed to initialize challenge funding.',
                movedToUpcoming: 'Challenge moved to upcoming successfully.',
                moveToUpcomingFailed: 'Failed to move challenge to upcoming.',
            },
        },
    },
    dashboard: {
        new: 'New',
        newDescription: 'Latest songs added',
        popular: 'Popular',
        popularDescription: 'Most popular songs',
        albums: 'Albums',
        albumsDescription: 'All albums',
        artists: 'Artists',
        artistsDescription: 'All artists',
        genres: 'Genres',
        genresDescription: 'All genres',
        addSongToRehearsalPrompt: 'Are you sure you want to add this song to rehearsal?',
        gettingStarted: {
            label: 'Getting Started',
            progress: '{{completed}} of {{total}} complete',
            primaryCta: 'Open suggested module',
            resumeCta: 'Resume onboarding',
            nextActions: {
                selectRole: 'Choose your role to personalize your workspace.',
                selectPart: 'Pick your vocal part to unlock focused practice defaults.',
                confirmUsername: 'Confirm your username so collaborators can find you.',
                takeFirstAction: 'Take your first guided action to complete onboarding.',
                completed: 'You are all set.',
            },
        },
        songGuide: {
            eyebrow: 'Song library guide',
            notNow: 'Not now',
            resume: 'Resume',
            progress: 'Step {{completed}} of {{total}}',
            hint: 'Complete the highlighted action to unlock the next step',
            empty: {
                eyebrow: 'Start here',
                title: 'No songs loaded yet',
                body: 'Once songs are available, we will guide your first listening and rehearsal actions here.',
            },
            steps: {
                open_song: {
                    title: 'Play a song from the list',
                    body: 'Click any song row to open playback and start your first listening flow.',
                },
                switch_stem: {
                    title: 'Switch to an isolated stem',
                    body: 'Pick your part in the player so practice starts with the right stem.',
                },
                open_multitrack: {
                    title: 'Open multitrack full view',
                    body: 'Expand to multitrack view to focus each stem and rehearse with control.',
                },
                close_multitrack: {
                    title: 'Close multitrack full view',
                    body: 'Exit full view to return to the song list and continue the guided flow.',
                },
                add_to_rehearsal: {
                    title: 'Add a song to rehearsal',
                    body: 'Queue one song into rehearsal so this module connects to your real workflow.',
                },
                view_in_rehearsal: {
                    title: 'Open the song in rehearsal',
                    body: 'Jump into rehearsal to complete the end-to-end song workflow.',
                },
            },
        },
        search: {
            noResultForKeyword: 'No result for the keyword {{keyword}}',
        },
        albumDetail: {
            fetchFailed: 'Failed to fetch album:',
            breadcrumb: 'Albums > {{title}}',
            breadcrumbLoading: 'Albums > Loading...',
            notFound: 'Album not found ...',
            bannerAlt: 'album banner',
            albumLabel: 'Album',
            byLabel: 'By: ',
            releasedYearLabel: 'Released Year: ',
            songNumberLabel: 'Song Number: ',
            albumDurationLabel: 'Album Duration: ',
        },
        artistDetail: {
            breadcrumbNamed: 'Artist > {{name}}',
            breadcrumbLoading: 'Artist > Loading...',
            empty: 'Artist not found ...',
            label: 'Artist',
            bioLabel: 'Bio:',
            tags: {
                popular: {
                    title: 'Popular',
                    description: 'Most popular songs',
                },
                albums: {
                    title: 'Albums',
                    description: 'All albums',
                },
            },
        },
        cards: {
            songArtworkAlt: '{{title}} artwork',
            albumArtAlt: 'album art',
            albumLabel: 'Album',
            artistLabel: 'Artist',
        },
        songTable: {
            title: 'Title',
            album: 'Album',
            genre: 'Genre',
            votes: 'Votes',
            duration: 'Duration',
            action: 'Action',
            single: 'Single',
            viewInRehearsal: 'View in Rehearsal',
            addToRehearsal: 'Add to Rehearsal',
            addToSetlist: 'Add to Setlist',
            viewCommunityReels: 'View Community Reels',
            likeOrVote: 'Like or Vote',
            toast: {
                songAddedToRehearsals: 'Song Added to Rehearsals',
                songAlreadyInRehearsals: 'Song Already in Rehearsals',
            },
        },
        topHits: {
            song: 'Song',
            votes: 'Votes',
            duration: 'Duration',
            actions: 'Actions',
            rehearsed: 'Rehearsed',
            addToRehearsal: 'Add to Rehearsal',
            iconAlt: 'icon',
        },
        topResult: {
            title: 'Top result',
            artist: 'Artist',
            albumWithTitle: 'Album · {{title}}',
            songWithTitle: 'Song · {{title}}',
            choirWithBio: 'Choir · {{bio}}',
            choirFallback: 'Choir',
        },
        searchGroup: {
            artists: 'Artists',
            albums: 'Albums',
            contributionRequests: 'Contribution Requests',
            communityContributions: 'Community Contributions',
            choirs: 'Choirs',
        },
    },
    projects: {
        breadcrumbBase: 'Projects',
        discover: 'Discover',
        discoverDescription: 'Browse open projects',
        myProjects: 'My Projects',
        myProjectsDescription: 'Projects you own',
        myInvites: 'My Invites',
        myInvitesDescription: 'Roles you have been invited to',
        myApplications: 'My Applications',
        myApplicationsDescription: 'Roles you have applied for',
        headerSubtitle: 'Build temporary teams, discover live opportunities, and move accepted collaborators into a workspace when needed.',
        createProject: 'Create Project',
        discoverInner: 'Discover',
        searchPlaceholder: 'Search projects, artists, gigs, productions',
        allModes: 'All Modes',
        remote: 'Remote',
        physical: 'Physical',
        hybrid: 'Hybrid',
        compensation: 'Compensation',
        paid: 'Paid',
        unpaid: 'Unpaid',
        allRoles: 'All Roles',
        allCountries: 'All Countries',
        noOpenProjects: 'No open projects matched those filters.',
        myProjectsEmpty: 'You have not created a project yet.',
        roleFallback: 'Role',
        detailsUnavailable: 'Project details unavailable',
        locationLabel: 'Location:',
        unavailable: 'Unavailable',
        components: {
            projectCard: {
                positionsRequested: '{{count}} positions requested',
                requirementsOnDetailView: 'Project requirements available on the detail view',
                more: 'more',
                projectAlt: 'Project',
                paidOpportunity: 'Paid Opportunity',
                unpaidCollaboration: 'Unpaid Collaboration',
                projectOwnerAvatarAlt: 'Project Owner Avatar',
                byOwner: 'by {{owner}}',
                needsLabel: 'Needs:',
                openSlots: '{{count}} open slots',
            },
            userProfileHover: {
                userFollowed: 'User followed.',
                userUnfollowed: 'User unfollowed.',
                unableToFollowUser: 'Unable to follow user.',
                unableToUnfollowUser: 'Unable to unfollow user.',
                vocalcentricMember: 'VocalCentric member',
                followersCount: 'Followers: {{count}}',
                followingCount: 'Following: {{count}}',
                viewProfile: 'View Profile',
                follow: 'Follow',
                unfollow: 'Unfollow',
            },
            applicantProfileCard: {
                applicant: 'Applicant',
                unknownApplicant: 'Unknown Applicant',
                noPublicProfileSnapshotAttached: 'No public profile snapshot attached.',
                noHeadlineProvided: 'No headline provided.',
                featuredWork: 'Featured Work',
                shortlistApplication: 'Shortlist application',
                acceptApplication: 'Accept application',
                rejectApplication: 'Reject application',
                viewFullProfile: 'View full profile',
            },
        },
        table: {
            project: 'Project',
            role: 'Role',
            status: 'Status',
            actions: 'Actions',
        },
        actions: {
            viewProject: 'View Project',
            viewApplication: 'View Application',
        },
        invites: {
            empty: 'No invite-only project roles are waiting for you right now.',
            fallbackProjectTitle: 'Project Invite',
            invitedBy: 'Invited by',
            invitedByLabel: 'Invited by:',
            invitationStatus: 'Invitation Status',
            projectStatus: 'Project Status',
            visibility: 'Visibility',
            sent: 'Sent',
        },
        applications: {
            empty: 'You have not applied to any project roles yet.',
            fallbackProjectTitle: 'Project Application',
            ownedBy: 'Owned by',
            ownerLabel: 'Owner:',
            applicationStatus: 'Application Status',
            projectStatus: 'Project Status',
            visibility: 'Visibility',
            submitted: 'Submitted',
            message: 'Message',
            noNote: 'No note attached.',
        },
        createForm: {
            createProject: 'Create Project',
            editProject: 'Edit Project',
            updateProject: 'Update Project',
            saveProject: 'Save Project',
            create: 'create',
            update: 'update',
            projectNotFound: 'Project not found.',
            ownerOnlyEdit: 'Only the project owner can edit this project.',
            projectBrief: 'Project Brief',
            projectBriefDescription: 'Define the core details of the collaboration and how creatives should discover it.',
            editDescription: 'Update the project brief, availability settings, and collaboration rules.',
            createDescription: 'Publish a focused collaboration brief without forcing it into the choir structure.',
            projectTitle: 'Project Title',
            projectTitlePlaceholder: 'Sunday session band, studio BGV call, Easter production cast',
            projectDescription: 'Project Description',
            projectDescriptionPlaceholder: 'Describe the gig, the sound, rehearsal expectations, and what applicants should know.',
            projectType: 'Project Type',
            selectProjectType: 'Select project type',
            location: 'Location',
            locationPlaceholder: 'Lagos, Abuja, Remote, London',
            country: 'Country',
            countryPlaceholder: 'Nigeria, United States, United Kingdom',
            compensationDetails: 'Compensation Details',
            selectCompensationDetails: 'Select compensation details',
            projectSettings: 'Project Settings',
            projectSettingsDescription: 'Set how creatives encounter the project and the kind of commitment you expect.',
            visibility: 'Visibility',
            visibilityDescription: 'Control whether this project is publicly.',
            public: 'Public',
            private: 'Private',
            engagementMode: 'Engagement Mode',
            engagementModeDescription: 'Choose how collaborators participate.',
            remote: 'Remote',
            physical: 'Physical',
            hybrid: 'Hybrid',
            compensation: 'Compensation',
            compensationDescription: 'Set whether this opportunity is paid or unpaid.',
            paid: 'Paid',
            unpaid: 'Unpaid',
            projectBudget: 'Project Budget',
            projectBudgetDescription: 'Set an overall budget cap for paid roles so role commitments can be tracked against it.',
            totalBudget: 'Total Budget',
            applicationAccess: 'Application Access',
            applicationAccessDescription: 'Choose whether the whole project accepts open applications or invite-only responses.',
            open: 'Open',
            inviteOnly: 'Invite Only',
            projectStatus: 'Project Status',
            projectStatusDescription: 'Control whether the project starts as a draft, opens for applications, or moves into review.',
            rolesNeeded: 'Roles Needed',
            rolesNeededDescription: 'Add the parts and positions you need filled, then shape how each role should be applied for.',
            roleN: 'Role {{index}}',
            done: 'Done',
            remove: 'Remove',
            pluralSuffix: 's',
            openApplication: 'Open Application',
            edit: 'Edit',
            roleType: 'Role Type',
            selectRole: 'Select role',
            quantity: 'Quantity',
            category: 'Category',
            chooseRoleTypeHint: 'Choose a role type to derive the category',
            openApplicationDescription: 'Decide whether this role is discoverable to everyone or only to invited creatives.',
            roleBudget: 'Role Budget',
            roleBudgetDescription: 'Set the {{type}} budget for this role.',
            payment: 'payment',
            revenueSharePercentage: 'Revenue Share Percentage',
            minimumBudget: 'Minimum Budget',
            budget: 'Budget',
            maximumBudget: 'Maximum Budget',
            roleNotes: 'Role Notes',
            roleNotesPlaceholder: 'Audition expectations, stylistic requirements, gear notes, availability requirements.',
            addRole: 'Add Role',
            toast: {
                projectUpdated: 'Project updated successfully',
                projectCreated: 'Project created successfully',
                saveFailed: 'Unable to {{action}} project',
            },
        },
        projectDetail: {
            ownerOnlyBudget: 'Only the project owner can manage budget settings.',
            ownerOnlyApplications: 'Only the project owner can review applications here.',
            ownerOnlyInvites: 'Only the project owner can manage invites here.',
            common: {
                collaborator: 'Collaborator',
                compensation: 'Compensation',
                updated: 'Updated',
                view: 'View',
                submitted: 'Submitted',
                reviewed: 'Reviewed',
                message: 'Message',
                noMessage: 'No message attached.',
                portfolio: 'Portfolio',
                openPortfolio: 'Open portfolio',
                auditionMedia: 'Audition Media',
                openAuditionMedia: 'Open audition media',
                socialMedia: 'Social Media',
                openProfile: 'Open profile',
                auditionPreview: 'Audition Preview',
                reviewNotes: 'Review Notes',
            },
            budget: {
                breadcrumbTitle: 'Budget',
                onlyPaid: 'Budget management is available only for paid projects.',
                enterValidAmount: 'Enter a valid non-negative budget amount.',
                updated: 'Project budget updated.',
                updateFailed: 'Unable to update project budget',
                settingsTitle: 'Budget Settings',
                settingsSubtitle: 'Set the project budget and monitor role commitments before adding new paid roles.',
                projectBudget: 'Project Budget',
                enterTotalBudget: 'Enter total budget',
                currency: 'Currency',
                saveBudget: 'Save Budget',
                snapshotTitle: 'Budget Snapshot',
                notSet: 'Not set',
                committed: 'Committed',
                remaining: 'Remaining',
                overrun: 'Commitments exceed budget by {{amount}}.',
            },
            agreements: {
                breadcrumbTitle: 'Agreements',
                title: 'Agreements',
                ownerSubtitle: 'Manage funding in one checkout across all eligible agreements.',
                viewerSubtitle: 'Review your compensation and payout progress for this project.',
                noEligibleFunding: 'No eligible agreements available for funding.',
                mixedCurrencies: 'Eligible agreements have mixed currencies and cannot be funded in one checkout.',
                fundingInitialized: 'Project funding initialized.',
                fundingInitFailed: 'Unable to initialize project funding',
                onlyPaid: 'This project is not configured for paid compensation agreements.',
                fundEligible: 'Fund Eligible Agreements',
                openMembers: 'Open Members',
                confirmWithSummary: 'This will initialize one checkout for {{summary}}. Continue?',
                confirmDefault: 'This will initialize one checkout for all eligible agreements. Continue?',
                continue: 'Continue',
                noneAssigned: 'No compensation agreement is assigned to you yet.',
                addPayoutPrompt: 'Add a payout account to receive project transfers when this agreement is processed.',
                setupPayout: 'Set Up Payout Account',
                withdrawalLabel: 'Withdrawal:',
                withdrawalRequested: 'Requested',
                withdrawalPending: 'Pending request',
                payoutSetupRequired: 'Payout account setup required',
            },
            agreementDetail: {
                breadcrumbTitle: 'Agreement Detail',
                agreementTitle: 'Agreement #{{id}}',
                detailsLoadFailed: 'Agreement details could not be loaded.',
                payee: 'Payee',
                compensation: 'Compensation',
                escrow: 'Escrow',
                payout: 'Payout',
                financialActivity: 'Financial Activity',
                noFinancialEvents: 'No financial events recorded yet.',
                payoutSnapshot: 'Payout Snapshot',
                gross: 'Gross',
                platformFee: 'Platform Fee',
                netPayout: 'Net Payout',
                na: 'N/A',
                fundAgreement: 'Fund Agreement',
                requestRelease: 'Request Release',
                refund: 'Refund',
                ownerActionsRestricted: 'Owner actions are restricted. Payout moves only after owner release request, your withdrawal request, and admin processing.',
                payoutSetupRequired: 'Payout account setup required before admin can process transfer.',
                withdrawalRequestedOn: 'Withdrawal requested on {{date}}.',
                requestWithdrawal: 'Request Withdrawal',
                confirmReleaseTitle: 'Request release confirmation',
                confirmReleaseDescription: 'This sends the payout for admin confirmation and processing. Funds are not paid instantly to the collaborator.',
                confirmRefundTitle: 'Refund escrow funds',
                confirmRefundDescription: 'Held escrow will be marked refunded and this agreement will be cancelled.',
                toast: {
                    notEligibleForFunding: 'Agreement is not eligible for funding right now.',
                    fundingInitialized: 'Funding initialized.',
                    initializeFundingFailed: 'Unable to initialize funding',
                    releaseRequested: 'Release request submitted. Admin confirmation is required before payout.',
                    requestReleaseFailed: 'Unable to request release',
                    escrowRefundProcessed: 'Escrow refund processed.',
                    processRefundFailed: 'Unable to process refund',
                    withdrawalRequested: 'Withdrawal request submitted. Admin can now process your payout.',
                    requestWithdrawalFailed: 'Unable to request withdrawal',
                },
            },
            compensationPanel: {
                compensation: 'Compensation',
                notCreated: 'Not created',
                noAgreement: 'No agreement',
                agreement: 'Agreement',
                escrow: 'Escrow',
                payout: 'Payout',
                pendingAgreement: 'Pending agreement',
                percentageRevenueShare: '{{percentage}}% revenue share',
                pendingAmount: 'Pending amount',
                createAgreement: 'Create Agreement',
                viewAgreement: 'View Agreement',
                flowHint: {
                    noAgreement: 'No agreement has been created for this collaborator yet.',
                    completed: 'Agreement completed and payout has been processed.',
                    payoutFailed: 'Payout failed. Admin retry is required.',
                    releaseRequested: 'Release requested. Awaiting admin payout processing.',
                    escrowSyncing: 'Payment was received. Escrow record is syncing.',
                    escrowHeld: 'Funds are held in escrow and ready for release request.',
                    cancelledRefunded: 'Agreement has been cancelled and escrow was refunded.',
                    awaitingFunding: 'Awaiting funding to move this agreement into escrow.',
                },
            },
            header: {
                defaultShareTitle: 'VocalCentric Project',
                defaultShareText: 'Check out this project on VocalCentric.',
                projectLinkCopied: 'Project link copied.',
                unableToShareProjectLink: 'Unable to share project link.',
                projectArtAlt: 'Project Art',
                global: 'Global',
                positionsRequested: '{{count}} positions requested',
                shareProject: 'Share project',
                editProject: 'Edit project',
                tags: {
                    roles: {title: 'Roles', description: 'Role requirements'},
                    applications: {title: 'Applications', description: 'Project applications'},
                    members: {title: 'Members', description: 'Accepted collaborators'},
                    budget: {title: 'Budget', description: 'Project budget'},
                    invites: {title: 'Invites', description: 'Manage project invites'},
                    agreements: {title: 'Agreements', description: 'Compensation tracking'},
                },
            },
            members: {
                breadcrumbTitle: 'Members',
                title: 'Members',
                subtitle: 'Review accepted collaborators who are currently active on this project.',
                unpaidCollaboration: 'Unpaid collaboration',
                pendingAmount: 'Pending amount',
                compensationAgreementCreated: 'Compensation agreement created.',
                createAgreementFailed: 'Unable to create agreement',
                empty: 'No accepted collaborators are active on this project yet.',
                assignedCollaborator: 'Assigned collaborator',
                joined: 'Joined',
                agreement: 'Agreement',
                manage: 'Manage',
                createAgreement: 'Create Agreement',
                noAgreementAssigned: 'No agreement assigned',
                noAgreement: 'No agreement',
            },
            applications: {
                breadcrumbTitle: 'Applications',
                title: 'Applications',
                subtitle: 'Review incoming applications and focus on the stage you need right now.',
                paidHint: 'Accepted collaborators can be funded from the Members screen.',
                statusUpdated: 'Application {{status}}.',
                updateFailed: 'Unable to update application',
                loadDetailsFailed: 'Unable to load application details',
                empty: 'No applications have landed for this project yet.',
                noMatchFilter: 'No {{status}} applications matched this filter.',
                noNote: 'No note attached.',
                shortlist: 'Shortlist application',
                accept: 'Accept application',
                reject: 'Reject application',
                viewDetails: 'View application details',
                applicant: 'Applicant',
                applicantProfile: 'Applicant Profile',
                closeModalAria: 'Close application modal',
                filters: {
                    all: 'All Applications',
                    pending: 'Pending',
                    shortlisted: 'Shortlisted',
                    accepted: 'Accepted',
                    rejected: 'Rejected',
                    withdrawn: 'Withdrawn',
                },
            },
            applicationDetail: {
                breadcrumbTitle: 'Application Detail',
                notFound: 'Project application not found.',
                snapshotTitle: 'Application Snapshot',
                backToApplications: 'Back to Applications',
            },
            overview: {
                breadcrumbTitle: 'Overview',
                collaborationRolesLabel: 'Collaboration Roles',
                applyToCollaborate: 'Apply to Collaborate',
                applyToCollaborateSubtitle: 'Choose the role that matches your skills and submit your application.',
                ownerManagementLabel: 'Owner Management',
                roleManagement: 'Role Management',
                roleManagementSubtitle: 'Create and manage collaboration roles for this project.',
                addRole: 'Add Role',
                inviteOnly: 'Invite Only',
                inviteOnlyHint: 'This role accepts applications by direct owner invite only.',
                openSlots: '{{count}} open',
                overrunDriver: 'Overrun Driver',
                inviteParticipants: 'Invite Participants',
                editRole: 'Edit Role',
                deleteRole: 'Delete Role',
                selected: 'Selected',
                apply: 'Apply',
                projectSnapshot: 'Project Snapshot',
                owner: 'Owner',
                visibility: 'Visibility',
                applicationAccess: 'Application Access',
                projectType: 'Project Type',
                currentOverrun: 'Current overrun: {{amount}}',
                workspaceBridge: 'Workspace Bridge',
                workspaceBridgeSubtitle: 'Link this project to a choir workspace and sync accepted members.',
                syncFundedRequirementPrefix: 'Syncing members is available after all paid agreements are marked as',
                funded: 'funded',
                syncFundedRequirementSuffix: '.',
                useExistingChoir: 'Use existing choir',
                createChoirFromProject: 'Create choir from project',
                useProjectTitleAsChoirName: 'Use project title as choir name',
                choirName: 'Choir Name',
                enterChoirName: 'Enter choir name',
                createAndLinkChoir: 'Create and Link Choir',
                choirWorkspace: 'Choir Workspace',
                selectDirectedChoir: 'Select a choir you direct',
                linkChoir: 'Link Choir',
                membersToSync: 'Members to sync',
                noActiveMembers: 'No active members available for sync yet.',
                contributor: 'Contributor',
                selectAll: 'Select All',
                clear: 'Clear',
                syncSeatsHint: 'Available seats: {{available}} • Selected members: {{selected}}',
                syncSeatsExceededHint: 'Selected members exceed available seats ({{selected}} selected, {{available}} available). Reduce selection or upgrade seats.',
                syncSelectedMembers: 'Sync Selected Members',
                linkedChoirWorkspace: 'Linked choir workspace: {{choir}}',
                syncedMembers: 'Synced members: {{count}}',
                linkedNoSyncedYet: 'Linked successfully. No members have been synced yet.',
                noExtraNotesYet: 'No extra notes yet.',
                viewerState: {
                    label: 'Viewer State',
                    memberTitle: 'You are already on this project',
                    memberBody: 'You are currently an active collaborator in this project.',
                    applicationSubmittedTitle: 'Application submitted',
                    applicationSubmittedBody: 'Your application is in review. The owner will update you soon.',
                    inviteAccessTitle: 'Invite access granted',
                    inviteAccessBody: 'You can view and apply for invite-enabled project roles.',
                    inviteAccessPrivateBody: 'You can access this private project through your invite.',
                    limitedAccessTitle: 'Limited access',
                    limitedAccessBody: 'This project is private. You need an invite to apply for roles.',
                },
                unavailable: {
                    alreadyMember: 'You are already assigned to this project.',
                    alreadyApplied: 'You already submitted an application for this project.',
                    inviteRequired: 'This role is invite-only. Ask the owner for an invite link.',
                    notAccepting: 'Applications are currently unavailable because this project is {{status}}.',
                    roleUnavailable: 'This role is currently unavailable for applications.',
                },
                toast: {
                    invalidReelLink: 'Enter a valid reel link before loading.',
                    reelNoPlayableMedia: 'This reel has no playable media manifest.',
                    resolveReelFailed: 'Unable to load reel media right now.',
                    chooseRoleBeforeApply: 'Choose a role before submitting your application.',
                    applicationSent: 'Application sent successfully.',
                    submitApplicationFailed: 'Unable to submit your application.',
                    selectChoirToLink: 'Select a choir to link.',
                    choirLinked: 'Choir linked to project workspace.',
                    linkChoirFailed: 'Unable to link choir workspace.',
                    enterChoirName: 'Enter a choir name before creating.',
                    resolveCreatedChoirFailed: 'Unable to resolve the newly created choir.',
                    choirCreatedAndLinked: 'Choir created and linked successfully.',
                    createAndLinkFailed: 'Unable to create and link choir workspace.',
                    linkBeforeSync: 'Link a choir workspace before syncing members.',
                    selectMembersToSync: 'Select at least one member to sync.',
                    notEnoughSeatsForSelection: 'Selected members exceed available choir seats. Reduce selection or upgrade seats.',
                    membersSynced: 'Members synced successfully.',
                    membersSyncedCount: '{{count}} members synced successfully.',
                    syncChoirFailed: 'Unable to sync project members to choir workspace.',
                    chooseRoleBeforeAdd: 'Choose a role type before saving.',
                    roleAdded: 'Role added successfully.',
                    roleUpdated: 'Role updated successfully.',
                    addRoleFailed: 'Unable to save role.',
                    roleDeleted: 'Role deleted successfully.',
                    deleteRoleFailed: 'Unable to delete role.',
                    enterInviteEmail: 'Enter a valid invitee email address.',
                },
                applyModal: {
                    title: 'Apply for {{role}}',
                    description: 'Share the context that helps the owner decide quickly.',
                    openCount: '{{count}} open',
                    messageLabel: 'Message',
                    messagePlaceholder: 'Introduce yourself, your availability, and why you fit this role.',
                    portfolioLabel: 'Portfolio Link',
                    portfolioPlaceholder: 'Spotify, YouTube, website, EPK',
                    auditionMediaSource: 'Audition Media Source',
                    auditionSource: {
                        external: 'External URL',
                        upload: 'VocalCentric Upload',
                        reel: 'VocalCentric Reel URL',
                    },
                    auditionLinkLabel: 'Audition Link',
                    auditionLinkPlaceholder: 'Drive, YouTube, Dropbox',
                    uploadReady: 'Upload ready. The owner will preview this media directly in application review.',
                    uploadHint: 'Upload an audition clip. VC packaging will provide HLS/DASH playback manifests.',
                    reelUrlLabel: 'Reel URL',
                    reelUrlPlaceholder: 'https://.../reel/<reel-id>',
                    loadReel: 'Load Reel',
                    loading: 'Loading...',
                    reelLinked: 'Reel linked successfully. The owner will be able to preview this media inline.',
                    socialMediaLabel: 'Social Media Link',
                    socialMediaPlaceholder: 'Instagram, TikTok, X, Facebook',
                    submitApplication: 'Submit Application',
                    submitting: 'Submitting...',
                    applicationsUnavailable: 'Applications Unavailable',
                    close: 'Close',
                },
                addRoleModal: {
                    addTitle: 'Add Role',
                    editTitle: 'Edit Role',
                    addDescription: 'Add a new role here. Invite outreach is managed from the dedicated Invites tab.',
                    editDescription: 'Update this role here. Invite outreach is managed from the dedicated Invites tab.',
                    roleType: 'Role Type',
                    selectRole: 'Select role',
                    quantity: 'Quantity',
                    applicationAccess: 'Application Access',
                    accessOpen: 'Open',
                    accessInviteOnly: 'Invite Only',
                    category: 'Category',
                    chooseRoleTypeToDeriveCategory: 'Choose a role type to derive the category',
                    roleBudget: 'Role Budget',
                    payment: 'payment',
                    setBudgetForRole: 'Set the {{paymentType}} budget for this role.',
                    revenueSharePercentage: 'Revenue Share Percentage',
                    revenueSharePlaceholder: '15',
                    fixedBudget: 'Fixed Budget',
                    budgetRange: 'Budget Range',
                    budget: 'Budget',
                    minimumBudget: 'Minimum Budget',
                    maximumBudget: 'Maximum Budget',
                    projectedOverrunWarning: 'Warning: projected commitment exceeds budget by {{amount}}.',
                    projectedCommitmentAfterSave: 'Projected commitment after save: {{projected}} of {{budget}}.',
                    roleNotes: 'Role Notes',
                    roleNotesPlaceholder: 'Audition expectations, stylistic requirements, gear notes, availability requirements.',
                    saveChanges: 'Save Changes',
                    saveRole: 'Save Role',
                },
                inviteModal: {
                    title: 'Invite to {{role}}',
                    description: 'Send a direct email invite for this invite-only role.',
                    openCount: '{{count}} open',
                    inviteeEmail: 'Invitee Email',
                    inviteeEmailPlaceholder: 'name@example.com',
                },
                deleteModal: {
                    title: 'Delete Role',
                    description: 'Remove {{role}} from this project. This only works while the role has no filled seats.',
                    deleteRole: 'Delete Role',
                },
            },
            invites: {
                breadcrumbTitle: 'Invites',
                title: 'Invites',
                subtitle: 'Track pending role invites and manage owner outreach from one place.',
                revoked: 'Invite revoked.',
                resent: 'Invite resent.',
                updateFailed: 'Unable to update invite',
                chooseRoleAndEmail: 'Choose a role and enter an email address.',
                addRoleFirst: 'Add this role to the project before inviting collaborators to it.',
                sent: 'Invite sent successfully.',
                sendFailed: 'Unable to send invite',
                sendInvite: 'Send Invite',
                sendNewTitle: 'Send New Invite',
                sendNewDescription: 'Choose a role and send a direct invite by email.',
                chooseRole: 'Choose Role',
                emailPlaceholder: 'creator@example.com',
                noMatchFilters: 'No invites matched the current filters.',
                empty: 'No role invites have been sent for this project yet.',
                resend: 'Resend invite',
                revoke: 'Revoke invite',
                invitee: 'Invitee',
                filters: {
                    all: 'All Invites',
                    pending: 'Pending',
                    applied: 'Applied',
                    revoked: 'Revoked',
                },
            },
        },
    },
    choir: {
        belongTo: 'Belong to',
        belongToDescription: 'Choirs you are part of',
        canJoin: 'Choirs you can join',
        canJoinDescription: 'Choirs you can join',
        managedByYou: 'Choirs you manage',
        managedByYouDescription: 'Choirs you are managing',
        headerSubtitle: 'See the choirs you are part of, and one you are managing',
        createChoir: 'Create Choir',
        emptyStart: 'What are you waiting for? You can start your own thing and run the show!',
        createAChoir: 'Create a Choir',
        becomeDirector: 'Become a Director',
        emptySeenAll: 'You\'ve come a long way, you\'ve seen it all',
        join: 'Join',
        noJoinedChoirYet: 'You haven\'t joined a choir yet',
        joinAChoir: 'Join a Choir',
        requestToJoin: 'Request to join choir',
        publicPage: {
            feedTab: 'Feed',
            setlistsTab: 'Setlists',
            resourcesTab: 'Resources',
            joinTab: 'Join',
            feedTitle: 'Public feed',
            setlistsTitle: 'Public setlists',
            resourcesTitle: 'Public resources',
            joinTitle: 'Join this choir',
            joinSubtitle: 'Create an account or sign in to request access.',
            noPublicPosts: 'No public posts yet.',
            noPublicSetlists: 'No public setlists yet.',
            noPublicResources: 'No public resources yet.',
            signUpToJoin: 'Sign up to request access',
            signInToJoin: 'Already have an account? Log in',
            joinClosed: 'This choir is not accepting join requests right now.',
            resourceFrom: 'From {{name}}',
            publicProfileLoadFailed: 'Unable to load this choir page.',
            feedLoadFailed: 'Unable to load public feed.',
            setlistsLoadFailed: 'Unable to load public setlists.',
            postTopic: 'Post topic',
            postTopicPlaceholder: 'Share a topic with this choir',
            postTopicFailed: 'Unable to post your topic. Please try again.',
            newUpdates: '{{count}} new update(s)',
            follow: 'Follow',
            following: 'Following',
            followersCount: '{{count}} follower(s)',
            followed: 'Choir followed.',
            unfollowed: 'Choir unfollowed.',
            followFailed: 'Unable to update choir follow status.',
        },
        claimPage: {
            eyebrow: 'Choir directory',
            title: 'Claim this choir',
            description: 'Connect {{name}} to the internal choir you administer. A VocalCentric admin will review your evidence before anything changes.',
            unavailable: 'This public choir is unavailable or can no longer be claimed.',
            signInTitle: 'Sign in to claim this choir',
            signInDescription: 'Choose the internal choir you administer and give us evidence that you represent this choir.',
            signIn: 'Sign in',
            signUp: 'Create an account',
            noChoirs: 'Create an internal choir first, then return here to submit a claim.',
            createChoir: 'Create a choir',
            choirLabel: 'Your internal choir',
            proofUrlLabel: 'Proof URL (optional)',
            proofNoteLabel: 'Proof note (optional)',
            proofHint: 'Provide a proof URL or note. Claims are manually reviewed.',
            submit: 'Submit claim for review',
            submitting: 'Submitting claim...',
            submitFailed: 'Unable to submit this claim.',
            submittedTitle: 'Claim submitted',
            submittedDescription: 'Your claim is pending review. The directory page will redirect to your internal choir after approval.',
        },
        planBadge: {
            premium: 'Premium',
            free: 'Free',
        },
        paywall: {
            upgradeRequiredTitle: 'Choir Upgrade Required',
            upgradeRequiredBody: 'Upgrade this choir to continue.',
            rehearsalLimitReached: 'Free choirs can create up to {{limit}} rehearsals. Upgrade this choir to add more.',
            setlistLimitReached: 'Free choirs can create up to {{limit}} setlists. Upgrade this choir to add more.',
            memberLimitReached: 'Free choirs can have up to {{limit}} members. Upgrade this choir to add more members.',
            privateSetlistsRequireUpgrade: 'Free choirs cannot create private setlists. Upgrade this choir to make setlists private.',
            profileVisibilityRequiresUpgrade: 'Free choir profiles must stay public on the join choir screen. Upgrade this choir to hide the profile.',
            rehearsalResourcesExpired: 'Free choir rehearsal resources are available for {{days}} days. Upgrade this choir to keep using lyrics, custom harmonies, slides, and practice tools.',
            harmonyVisibilityRequiresUpgrade: 'Free choir custom harmonies are public. Upgrade this choir to use private or choir-only harmonies.',
            rehearsalLimitTitle: '{{current}} of {{limit}} rehearsal limit reached',
            setlistLimitTitle: '{{current}} of {{limit}} setlist limit reached',
            memberLimitTitle: '{{current}} of {{limit}} member limit reached',
            privateSetlistsTitle: 'Private Setlists Require Upgrade',
            rehearsalResourcesExpiredTitle: 'Resource Access Expired',
            profileVisibilityTitle: 'Profile Visibility Locked',
            harmonyVisibilityTitle: 'Harmony Visibility Locked',
        },
        guide: {
            eyebrow: 'Choir guide',
            resume: 'Resume guide',
            notNow: 'Not now',
            progress: 'Step {{completed}} of {{total}}',
            join: {
                title: 'Join a choir',
                body: 'Pick a choir and send your request to start participating.',
            },
            create: {
                title: 'Create a choir',
                body: 'Create a choir first, then open rehearsals to start the weekly workflow.',
            },
        },
        rehearsalGuide: {
            eyebrow: 'Rehearsal guide',
            resume: 'Resume guide',
            notNow: 'Not now',
            progress: 'Step {{completed}} of {{total}}',
            create: {
                title: 'Create your first rehearsal',
                body: 'Start one rehearsal so your choir can move from setup into a real working session.',
            },
        },
        setlistsGuide: {
            eyebrow: 'Setlist guide',
            resume: 'Resume guide',
            notNow: 'Not now',
            progress: 'Step {{completed}} of {{total}}',
            create: {
                title: 'Create your first setlist',
                body: 'Create a choir setlist so songs are grouped before you plan the wider schedule.',
            },
            rehearsal: {
                title: 'Create a rehearsal from your setlist',
                body: 'Turn that setlist into a real rehearsal so the songs move into an active choir session.',
            },
        },
        scheduleGuide: {
            eyebrow: 'Schedule guide',
            resume: 'Resume guide',
            notNow: 'Not now',
            progress: 'Step {{completed}} of {{total}}',
            serviceDays: {
                title: 'Set your service days',
                body: 'Add the service days first so VocalCentric can build a usable choir schedule.',
            },
            create: {
                title: 'Create your first schedule',
                body: 'Create the first schedule so singers can see assignments and upcoming services.',
            },
        },
        membersGuide: {
            eyebrow: 'Members guide',
            resume: 'Resume guide',
            notNow: 'Not now',
            progress: 'Step {{completed}} of {{total}}',
            invite: {
                title: 'Invite your first member',
                body: 'Invite one member so the choir moves from setup into an active team.',
            },
        },
        rehearsalSongsGuide: {
            eyebrow: 'Rehearsal songs guide',
            resume: 'Resume guide',
            notNow: 'Not now',
            progress: 'Step {{completed}} of {{total}}',
            add_song: {
                title: 'Add songs to this rehearsal',
                body: 'Pull songs into the rehearsal first so your choir has a real working list to run through.',
            },
            open: {
                title: 'Open a rehearsal song',
                body: 'Open the first song so the choir can move from planning into the actual runthrough.',
            },
        },
        rehearsalRunthroughGuide: {
            eyebrow: 'Rehearsal runthrough guide',
            resume: 'Resume guide',
            notNow: 'Not now',
            progress: 'Step {{completed}} of {{total}}',
            add_lyrics: {
                title: 'Add lyrics for this song',
                body: 'Upload or paste the lyrics first so members can rehearse against structured song content.',
            },
            upload_reference_audio: {
                title: 'Upload reference audio',
                body: 'Add one choir-ready audio reference so members rehearse against the right version of the song.',
            },
            add_custom_harmony: {
                title: 'Add a custom harmony',
                body: 'Create the choir harmony parts so singers can practise against the actual arrangement.',
            },
            review_submission: {
                title: 'Review a member submission',
                body: 'Open one member practice submission and leave direction so the rehearsal loop becomes actionable.',
            },
            open_practise: {
                title: 'Open practice for this song',
                body: 'Move into the practice tab so you can start recording your part against the choir arrangement.',
            },
            record_practice_section: {
                title: 'Record your first section',
                body: 'Record one section to begin a real rehearsal submission instead of only browsing the song.',
            },
            submit_practice: {
                title: 'Submit your practice',
                body: 'Send your first practice take so the director can review and the rehearsal loop is complete.',
            },
        },
        management: {
            detailHeader: {
                rehearsalsTitle: 'Rehearsals',
                rehearsalsDescription: 'Every Saturday',
                setlistsTitle: 'Setlists',
                setlistsDescription: 'Choir Setlists',
                setlistsExtra: 'Setlists',
                scheduleTitle: 'Schedule',
                scheduleDescription: 'Choir Schedule for a period',
                scheduleExtra: 'Schedule',
                membersTitle: 'Members',
                membersExtra: 'Members',
                feedsTitle: 'Feeds',
                feedsExtra: 'Feeds',
                integrateTitle: 'Integrate',
                integrateDescription: 'Integrate',
            },
            edit: {
                title: 'Edit Choir',
            },
            rehearsals: {
                title: 'Choir Rehearsals',
                createRehearsal: 'Create Rehearsal',
                deleteRehearsal: 'Delete Rehearsal',
                empty: 'You have not created any rehearsals for this choir',
                noPermission: 'You do not have the permission to create a rehearsal',
                toast: {
                    deleted: 'Choir rehearsal deleted',
                    deleteFailed: 'Failed to delete choir rehearsal',
                    choirIdRequired: 'Choir ID is required',
                },
            },
            createRehearsal: {
                title: 'Create Rehearsal',
                manualMode: 'Manual Mode',
                fromSetlist: 'From Setlist',
                rehearsalTitle: 'Rehearsal Title',
                rehearsalTitleOptional: 'Rehearsal Title (optional)',
                shortDescription: 'Short Description',
                shortDescriptionOptional: 'Short Description (optional)',
                pasteSetlistLink: 'Paste Setlist Link',
                pasteSetlistLinkPlaceholder: 'Paste setlist link here',
                usingSetlist: 'Using {{kind}} setlist',
                choirSetlistKind: 'choir',
                personalSetlistKind: 'personal',
                addSongsPrompt: 'Add songs to rehearsal.',
                searchSongsPlaceholder: 'Search for songs to add to rehearsal',
                removeSong: 'Remove',
                songsInSetlist: 'Songs in Setlist',
                notAvailable: '—',
                external: 'External',
                internal: 'Internal',
                addSelectedSong: 'Add Song',
                additionalResourcesOptional: 'Additional Resources (optional)',
                additionalResourcesPrompt: 'Are there additional resources you would like to add?',
                uploadResources: 'Click to upload learning resources from your computer or phone',
                date: 'Date',
                from: 'From',
                fromOptional: 'From (optional)',
                to: 'To',
                toOptional: 'To (optional)',
                virtual: 'Virtual',
                physical: 'Physical',
                setReminder: 'Set Reminder',
                setReminderOptional: 'Set Reminder (optional)',
                selectReminderTime: 'Select reminder time',
                createButton: 'Create Rehearsal',
                reminders: {
                    oneHour: '1 hour before',
                    twoHours: '2 hours before',
                    threeHours: '3 hours before',
                    fourHours: '4 hours before',
                    fiveHours: '5 hours before',
                    sixHours: '6 hours before',
                    twelveHours: '12 hours before',
                    twentyFourHours: '24 hours before',
                },
                toast: {
                    fillRequired: 'Please fill all required fields',
                    created: 'Rehearsal created successfully',
                    createFailed: 'Error creating rehearsal',
                    choirSetlistNotFound: 'Choir setlist not found',
                    loadChoirSetlistFailed: 'Failed to load choir setlist',
                    setlistNotFound: 'Setlist not found',
                    fetchSetlistFailed: 'Failed to fetch setlist',
                },
            },
            setlists: {
                title: 'Choir Setlists',
                newChoirSetlist: 'New Choir Setlist',
                empty: 'No choir setlists yet.',
                copyLink: 'Copy link',
                createRehearsalFromSetlist: 'Create rehearsal from this setlist',
                delete: 'Delete',
                linked: 'Linked',
                sharedBy: 'Shared by {{name}}',
                songCount: '{{count}} song(s)',
                updatedDate: 'Updated: {{date}}',
                searchPlaceholder: 'Search by setlist, song, or metadata',
                searchEmpty: 'No choir setlists match your search.',
                searchFailed: 'Unable to search choir setlists',
                loadFailed: 'Failed to load choir setlists',
                linkCopied: 'Link copied to clipboard!',
                deleted: 'Choir setlist deleted',
                deleteFailed: 'Failed to delete setlist',
                createModal: {
                    title: 'Create Choir Setlist',
                    description: 'Start with a name. You can add songs and turn it into a rehearsal after.',
                    nameLabel: 'Setlist Name',
                    namePlaceholder: 'Sunday rehearsal setlist',
                    confirm: 'Create Setlist',
                    creating: 'Creating...',
                    toast: {
                        created: 'Choir setlist created',
                        createFailed: 'Failed to create choir setlist',
                    },
                },
                manageModal: {
                    title: 'Manage "{{name}}"',
                    description: 'Review songs in this choir setlist, add new ones, and remove songs as needed.',
                    viewOnlyDescription: 'Review the songs in this choir setlist.',
                    viewOnlyNotice: 'Only choir directors and moderators can edit this setlist.',
                    addSongs: 'Add Songs',
                    shareLink: 'Setlist Link',
                    scheduledFor: 'Scheduled For',
                    scheduledForPlaceholder: 'Choose a future date and time',
                    saveSchedule: 'Save',
                    savingSchedule: 'Saving...',
                    scheduleRequired: 'Choose a future date and time',
                    scheduleMustBeFuture: 'Schedule must be in the future',
                    scheduleUpdated: 'Setlist schedule updated',
                    scheduleUpdateFailed: 'Failed to update setlist schedule',
                },
            },
            rehearsalSongs: {
                title: 'Rehearsal Songs',
                resourcesTitle: 'Learning resources',
                resourcesDescription: 'Sheets, documents, and audio for this set.',
                addResources: 'Add resources',
                closeResourceUpload: 'Done',
                resourceType: 'Resource type',
                documentResource: 'Document',
                scoreResource: 'Sheet music',
                resourceLabel: 'Resource label (optional)',
                resourceLabelPlaceholder: 'e.g. Sunday warm-up guide',
                uploadDocuments: 'Upload sheets or documents',
                uploadMusicXml: 'Upload MusicXML score',
                musicXmlDescription: 'MusicXML, XML, or MXL supported',
                uploadMidiOptional: 'Upload MIDI (optional)',
                uploadDryRun: 'Upload audio',
                dryRunAudio: 'Audio',
                previewResource: 'Preview resource',
                playResource: 'Play audio',
                practiceDryRun: 'Practice {{name}}',
                openLyrics: 'Open lyrics for {{title}}',
                openPractice: 'Open practice for {{title}}',
                openPracticeReview: 'Open practice review for {{title}}',
                editDryRun: 'Edit {{name}}',
                dryRunLyrics: 'Dry-run lyrics',
                dryRunLyricsPlaceholder: 'Add lyrics or rehearsal notes for this dry run...',
                listenEntireAudio: 'Listen to entire audio',
                listenSegment: 'Listen to a segment',
                segmentStart: 'Start (seconds)',
                segmentEnd: 'End (seconds)',
                invalidListenSegment: 'The segment end must be after its start.',
                deleteResource: 'Delete {{name}}',
                deleteResourceConfirm: 'Delete {{name}} from this rehearsal?',
                playYouTubeReference: 'Play {{title}} on YouTube',
                youtubePlayer: 'YouTube player',
                viewListenAudit: 'View listen audit',
                listenAudit: 'Listen audit',
                listenAuditSummary: '{{listeners}} listener(s) • {{listens}} listen(s)',
                listenAuditDescription: 'Track who has listened to this rehearsal.',
                loadingListenAudit: 'Loading listen audit...',
                listenAuditLoadFailed: 'Unable to load listen audit.',
                noListens: 'No listens recorded yet.',
                nudgeChoir: 'Nudge choir',
                nudgingChoir: 'Sending reminder...',
                nudgeSent: 'Reminder sent to {{count}} choir member(s).',
                nudgeFailed: 'Unable to send the reminder.',
                audioPlaybackUnavailable: 'Audio playback is unavailable right now.',
                openInNewTab: 'Open in new tab',
                noResources: 'No learning resources have been added yet.',
                setlistResource: 'Setlist resource',
                resourceOpenFailed: 'Unable to open this rehearsal resource.',
                addSetlists: 'Add setlists',
                addSetlistsDescription: 'Attach songs and resources from one or more choir setlists.',
                addSelectedSetlists: 'Add selected setlists',
                addingSetlists: 'Adding setlists...',
                setlistsLoadFailed: 'Unable to load choir setlists.',
                setlistsAdded: 'Setlists added to rehearsal',
                setlistsAddFailed: 'Unable to add the selected setlists.',
                noSetlistsAvailable: 'All available choir setlists are already attached.',
                additionalSongs: 'Additional songs',
                expandSetlist: 'Expand {{name}}',
                collapseSetlist: 'Collapse {{name}}',
                songsInSetlist: '{{count}} songs',
                noSongsInSetlist: 'No songs in this setlist.',
                savingResources: 'Saving resources...',
                resourcesSaved: 'Rehearsal resources updated',
                resourcesSaveFailed: 'Failed to update rehearsal resources',
                addSong: 'Add Song',
                addToRehearsal: 'Add to rehearsal',
                startRehearsal: 'Start Rehearsal',
                startingRehearsal: 'Starting Rehearsal',
                deleteConfirm: 'Delete this song from the rehearsal?',
                moveSongUp: 'Move song up',
                moveSongDown: 'Move song down',
                songActions: '{{title}} actions',
                currentSongSettings: 'Key: {{key}} • Listen target: {{target}}',
                noKeySet: 'Not set',
                entireSong: 'Entire song',
                openCustomHarmonies: 'Custom harmonies',
                songSettings: 'Song settings',
                songSettingsDescription: 'Set the rehearsal details for {{title}}.',
                songKey: 'Song key',
                songLabel: 'Rehearsal label',
                songLabelPlaceholder: 'e.g. Reprise or Offering',
                listenTarget: 'Listen target',
                youtubeReference: 'YouTube reference',
                deleteSong: 'Delete song',
                savingSettings: 'Saving settings...',
                settingsNoChanges: 'No song settings changed.',
                settingsSaved: 'Song settings updated.',
                settingsPartiallySaved: 'Some song settings could not be updated.',
                settingsSaveFailed: 'Unable to update song settings.',
                toast: {
                    removed: 'Song removed from rehearsal',
                    removeFailed: 'Failed to remove song',
                    choirIdRequired: 'Choir ID is required',
                    reorderFailed: 'Failed to reorder rehearsal songs',
                },
            },
            members: {
                title: 'Members {{seatCount}} of {{maxSeatCount}}',
                addSeats: 'Add Seats',
                manageSharedSeats: 'Share Seats',
                redeemSharedSeats: 'Redeem Seat Code',
                addMember: 'Add Member',
                addMembersPage: 'Add Members',
                searchPlaceholder: 'Search members',
                searchEmpty: 'No members match your search.',
                actions: 'Actions',
                tabs: {
                    all: {
                        title: 'All',
                        description: 'Existing members of the choir',
                    },
                    invite: {
                        title: 'Invite',
                        description: 'Members who have been invited to the choir',
                    },
                    requests: {
                        title: 'Requests',
                        description: 'Requests to join the choir',
                    },
                },
                empty: {
                    members: 'No members in your fold yet, why not get some hands onboard 🙂',
                    invitations: 'No pending requests here for now, why not get more hands onboard 🙂',
                    requests: 'No pending requests here for now, why get more hands onboard 🙂',
                    inviteCta: 'Invite a New Member',
                },
                invitations: {
                    fetchFailed: 'Failed to fetch invitations',
                    deleted: 'Invitation deleted',
                    deleteFailed: 'Failed to delete invitation',
                    resent: 'Invitation resent',
                    resendFailed: 'Failed to resend invitation',
                },
                badges: {
                    awarded: 'Badge awarded successfully',
                    awardButton: 'Award Badge',
                    confirmation: 'Are you sure you want to award this user a {{type}} Badge?',
                    typeLabel: '{{type}} Badge',
                },
            },
            integrations: {
                title: 'Tools',
                connect: 'Connect',
                platforms: {
                    whatsapp: {
                        name: 'Whatsapp',
                        description: 'A popular messaging app that allows users to send text messages, and groups.',
                        toast: 'Connect Whatsapp',
                    },
                    calendar: {
                        name: 'Calendar',
                        description: 'A popular messaging app that allows users to send text messages, and groups.',
                        toast: 'Connect Calendar',
                    },
                    telegram: {
                        name: 'Telegram',
                        description: 'A popular messaging app that allows users to send text messages, and groups.',
                        toast: 'Connect Telegram',
                    },
                },
            },
            feeds: {
                loadFailed: 'Failed to fetch choir feed:',
                emptyTitle: 'No feed yet',
                emptyDescription: 'Your folk\'s been sleeping on it, you need to get them to wake up!',
                startPractising: 'Start Practising',
                loadMore: 'Load more posts',
                newUpdates: '{{count}} new update(s)',
                log: {
                    normalizedFeeds: 'Normalized feeds:',
                },
                comment: {
                    placeholder: 'Write a comment',
                    post: 'Post',
                    logPost: 'Posting Comment',
                },
                composer: {
                    placeholder: 'Share an update with your choir',
                    internal: 'Members only',
                    public: 'Public only',
                    both: 'Members and public',
                    post: 'Post update',
                },
                mentions: {
                    loading: 'Loading people...',
                    empty: 'No one available to mention',
                },
                references: {
                    loading: 'Loading references...',
                    empty: 'No setlists or rehearsals found',
                },
                engagement: {
                    like: 'Like',
                    likeWithCount: 'Like ({{count}})',
                    comment: 'Comment',
                    noComments: 'No comments yet.',
                    loadingComments: 'Loading comments...',
                    loadingReplies: 'Loading replies...',
                    viewReplies: 'View replies ({{count}})',
                    viewMoreComments: 'View more comments',
                    viewMoreReplies: 'View more replies',
                    hideReplies: 'Hide replies',
                    reply: 'Reply',
                    replyPlaceholder: 'Write a reply',
                    posting: 'Posting...',
                    signInToEngage: 'Sign in to like or comment.',
                    loadCommentsFailed: 'Failed to load comments.',
                    postFailed: 'Failed to post comment.',
                    updateLikeFailed: 'Failed to update like.',
                    deleteFeed: 'Delete feed item',
                    editFeed: 'Edit feed item',
                    feedActions: 'Feed actions',
                    report: 'Report',
                    editFeedPrompt: 'Edit this feed item',
                    editCommentPrompt: 'Edit this comment',
                    reportFeedConfirm: 'Report this feed item to the choir moderators?',
                    reportCommentConfirm: 'Report this comment to the choir moderators?',
                    reportReasonPrompt: 'Select the reason that best describes the issue.',
                    reportReason: {
                        spam: 'Spam',
                        abuse: 'Abusive content',
                        harassment: 'Harassment',
                        other: 'Other',
                    },
                    submitReport: 'Submit report',
                    submittingReport: 'Submitting...',
                    reported: 'Report submitted.',
                    alreadyReported: 'Reported',
                    reportFailed: 'Unable to submit report.',
                    updated: 'Updated.',
                    updateFailed: 'Unable to save changes.',
                    deleteFeedConfirm: 'Delete this feed item?',
                    deleteCommentConfirm: 'Delete this comment?',
                    deleted: 'Deleted',
                    deleteFailed: 'Delete failed.',
                },
                moderation: {
                    openQueue: 'Review reports',
                    title: 'Feed reports',
                    description: 'Review reported posts and comments from this choir.',
                    pending: 'Pending',
                    resolved: 'Resolved',
                    dismissed: 'Dismissed',
                    empty: 'No reports in this queue.',
                    feed: 'Post',
                    comment: 'Comment',
                    author: 'Author: {{email}}',
                    reporter: 'Reported by: {{email}}',
                    view: 'View in feed',
                    remove: 'Remove content',
                    resolve: 'Resolve',
                    dismiss: 'Dismiss',
                    removeConfirm: 'Remove this content and resolve all reports for it?',
                    removed: 'Content removed and reports resolved.',
                    updated: 'Report updated.',
                    loadFailed: 'Unable to load feed reports.',
                    updateFailed: 'Unable to update the report.',
                    removeFailed: 'Unable to remove the reported content.',
                    previous: 'Previous',
                    next: 'Next',
                },
                card: {
                    welcome: '👋 Welcome to Refinery choir!',
                    practiceSubmitted: '🎤 {{name}} just submitted a practice for “{{song}}”',
                    practiceApproved: '✅ {{name}}’s practice was approved for “{{song}}”',
                    practiceNeedsImprovement: '❗ {{name}}’s practice for “{{song}}” needs improvement',
                    rehearsalActivity: '📝 {{name}} created a rehearsal for “{{song}}”',
                    rehearsalSongListened: '🎧 {{name}} listened to “{{song}}” for rehearsal',
                    setlistSongPlayed: '🎧 {{name}} played “{{song}}” from {{setlist}}',
                    rehearsalCreated: '📅 {{name}} created {{rehearsal}}',
                    setlistAdded: '🎼 {{name}} added {{setlist}}',
                    defaultUpdate: '{{name}} posted an update',
                    unknownTypeLog: 'Unknown feed type: {{type}}',
                    pinned: 'Pinned',
                },
            },
            schedule: {
                title: 'Choir Schedule',
                document: 'document',
                manageServiceTypes: 'Manage Service Types',
                manageServiceTypesAria: 'Create and Manage Service Types',
                setServiceDays: 'Set Service Days',
                createSchedule: 'Create Schedule',
                reshuffleAssignments: 'Re-shuffle Schedule Assignments',
                reshuffleUnavailable: 'Re-shuffle is unavailable until a schedule exists',
                shareSchedule: 'Share Schedule',
                shareUnavailable: 'Schedule sharing is unavailable until a schedule exists',
                deleteSchedule: 'Delete Schedule',
                deleteUnavailable: 'Delete Schedule is unavailable until a schedule exists',
                noServiceDays: 'No service days defined yet.',
                noScheduleYet: 'No schedule created yet',
                getStarted: 'Get started by adding your first schedule for this choir!',
                addSinger: 'Add Singer',
                replaceSinger: 'Replace Singer',
                chooseReplacementFor: 'Choose a replacement for',
                chooseSingerFor: 'Choose a singer for',
                leadSection: 'the lead section',
                deleteService: 'Delete Service',
                deleteServiceDescription: 'Delete {{service}} from the current schedule? This only removes this scheduled instance and keeps the service type itself.',
                thisService: 'this service',
                deleting: 'Deleting...',
                removeSingerPrompt: 'Remove {{singer}} from {{part}}?',
                reshufflePrompt: 'Re-shuffle all current service assignments using the existing schedule services and {{count}} singer(s) per part?',
                deleteSchedulePrompt: 'Delete the current choir schedule? This will remove all generated weeks, services, leads, and BGV assignments for this choir schedule.',
                toast: {
                    downloadPdfFailed: 'Failed to download schedule PDF. Please try again.',
                    publicLinkReady: 'Public schedule link ready',
                    publicLinkFailed: 'Failed to generate public schedule link',
                    scheduleDeleted: 'Schedule deleted',
                    deleteScheduleFailed: 'Failed to delete schedule',
                    reshuffled: 'Schedule re-shuffled',
                    reshuffleFailed: 'Failed to reshuffle schedule',
                    serviceTypeUpdated: 'Service type updated',
                    serviceTypeUpdateFailed: 'Failed to update service type',
                    serviceDeleted: 'Service deleted',
                    serviceDeleteFailed: 'Failed to delete service',
                    serviceAdded: 'Service added',
                    serviceAddFailed: 'Failed to add service',
                    scheduleUpdated: 'Schedule updated',
                    assignmentUpdateFailed: 'Failed to update assignment',
                    singerRemoved: 'Singer removed',
                    singerRemoveFailed: 'Failed to remove singer',
                },
                components: {
                    lead: 'Lead',
                    noLeadAssigned: 'No lead assigned.',
                    assignLead: 'Assign lead',
                    assignLeadComingSoon: 'Assign lead (coming soon)',
                    noSingerAssigned: 'No singer assigned.',
                    addVoiceSectionSinger: 'Add {{section}}',
                    addVoiceSectionSingerComingSoon: 'Add {{section}} (coming soon)',
                    replaceSinger: 'Replace singer',
                    replaceSingerComingSoon: 'Replace singer (coming soon)',
                    removeSinger: 'Remove singer',
                    removeSingerComingSoon: 'Remove singer (coming soon)',
                    unassignedSinger: 'Unassigned Singer',
                    assigned: 'Assigned',
                    open: 'Open',
                    total: 'Total',
                    sopranoShort: 'S',
                    altoShort: 'A',
                    tenorShort: 'T',
                    bassShort: 'B',
                    changeServiceTypeTooltip: 'Change service type for this service',
                    deleteScheduledServiceTooltip: 'Delete this scheduled service',
                    addServiceToWeek: 'Add service to this week',
                },
                modals: {
                    serviceType: 'Service Type',
                    onlyChoirServiceTypes: 'Only service types created for this choir are available here.',
                    noChoirServiceTypes: 'No choir-specific service types available.',
                    customName: 'Custom Name',
                    customNamePlaceholder: 'e.g. Special Impromptu Service',
                    customNameHint: 'Choose an existing service type or enter a one-off custom name.',
                    serviceDateTime: 'Service Date & Time',
                    selectExistingServiceType: 'Select existing service type',
                    saveChanges: 'Save Changes',
                    saving: 'Saving...',
                    adding: 'Adding...',
                    addService: {
                        title: 'Add Service',
                        description: 'Add a service to this week.',
                        descriptionWithWeek: 'Add a service to {{week}} ({{start}} – {{end}}).',
                        action: 'Add Service',
                    },
                    updateServiceType: {
                        title: 'Change Service Type',
                        description: 'Update the service type for this scheduled service.',
                    },
                    recurrence: {
                        weekly: 'Weekly on {{day}}',
                        biweekly: 'Bi-weekly on {{day}}',
                        monthlyNthWeekday: 'Monthly on the {{ordinal}} {{day}}',
                        monthlyDayOfMonth: 'Monthly on day {{day}}',
                        annual: 'Annual on {{month}} {{day}}',
                        oneTime: 'One-time on {{date}}',
                        none: 'No recurrence configured',
                    },
                    noEligibleSingers: 'No eligible singers available for this section.',
                    saveSinger: 'Save Singer',
                },
            },
        },
    },
    community: {
        breadcrumbBase: 'Community',
        challenges: 'Challenges',
        challengesDescription: 'Create, discover, and join challenge campaigns.',
        vocalPerformances: 'Vocal Performances',
        vocalPerformancesDescription: 'A collection of vocal performances from various artists.',
        reels: 'Reels',
        reelsDescription: 'A collection of short videos showcasing vocal performances.',
        welcomeTitle: 'Welcome to the Community',
        welcomeSubtitle: 'Locate your preferred in different colors',
        searchChallenges: 'Search challenges',
        noChallengesFound: 'No challenges found.',
        challengeTitleLabel: 'Challenge Title',
        entriesLabel: 'No of Entries',
        rewardLabel: 'Challenge Reward',
        challengeLinkCopied: 'Challenge link copied to clipboard',
        unableToShareChallenge: 'Unable to share challenge right now',
        challengeLeaderboard: {
            breadcrumb: 'Challenge Leaderboard',
            breadcrumbNamed: '{{title}} > Leaderboard',
            title: 'Leaderboard',
            subtitle: 'Top entries ranked by weighted moderator score, with likes as tie-breaker.',
            empty: 'No leaderboard data available yet.',
            entryAlt: 'entry',
            anonymous: 'Anonymous',
            noCaption: 'No caption',
            scoreLabel: 'Score:',
            votesLikes: '{{votes}} votes · {{likes}} likes',
        },
        challengeEditor: {
            createTitle: 'Create Challenge',
            editTitle: 'Edit Challenge',
            createSubtitle: 'Set up a new challenge for the community.',
            editSubtitle: 'Update challenge metadata and schedule.',
            ownerOnly: 'Only the challenge owner can edit this challenge.',
            form: {
                titleLabel: 'Title',
                titlePlaceholder: 'Challenge title',
                rewardTypeLabel: 'Reward Type',
                rewardTypeCash: 'Cash',
                rewardTypePrize: 'Prize',
                startDateLabel: 'Start date',
                endDateLabel: 'End date',
                rewardAmountLabel: 'Reward amount',
                rewardAmountCashPlaceholder: '100',
                rewardAmountPrizePlaceholder: '0',
                rewardCurrencyLabel: 'Reward currency',
                entryTypeLabel: 'Entry type',
                entryTypeOpen: 'Open',
                entryTypeInviteOnly: 'Invite only',
                participationTypeLabel: 'Participation type',
                participationTypeIndividual: 'Individual',
                participationTypeChoir: 'Choir',
                maxParticipantsLabel: 'Max participants',
                maxParticipantsPlaceholder: 'No limit',
                minXpLabel: 'Minimum XP',
                maxXpLabel: 'Maximum XP',
                xpPlaceholder: 'Optional',
                descriptionLabel: 'Description',
                descriptionPlaceholder: 'Describe the challenge',
                rewardDescriptionLabel: 'Reward description',
                rewardDescriptionPlaceholder: 'Optional reward details',
                rulesLabel: 'Rules and guidelines',
                rulesPlaceholder: 'Challenge rules',
            },
            uploads: {
                promoVideoLabel: 'Upload Promo Video',
                promoVideoHint: 'Required. Thumbnail can be auto-generated from this video.',
                thumbnailLabel: 'Upload Challenge Thumbnail',
                thumbnailHint: 'Optional. If omitted, we will use the thumbnail derived from your promo video.',
                thumbnailPreview: 'Thumbnail preview',
                thumbnailAlt: 'Challenge thumbnail',
            },
            actions: {
                saving: 'Saving...',
                updateChallenge: 'Update Challenge',
                createChallenge: 'Create Challenge',
            },
            toasts: {
                updated: 'Challenge updated successfully.',
                created: 'Challenge created successfully.',
                saveFailed: 'Unable to save challenge.',
            },
        },
        challengeComments: {
            title: 'Community Comments',
            empty: 'No community comments yet.',
            you: 'You',
            avatarAlt: 'avatar',
            reply: 'Reply',
            loading: 'Loading...',
            hideReplies: 'Hide replies',
            viewReplies: 'View replies{{suffix}}',
            replyPlaceholder: 'Write a reply...',
            commentPlaceholder: 'Add a comment...',
            send: 'Send',
        },
        challengeReviewsModal: {
            title: 'Reviews & Comments',
            communityMember: 'Community member',
            avatarAlt: 'avatar',
            moderatorReviewBadge: 'Moderator Review',
            noPreviewVideo: 'No preview video',
            moderatorReviews: 'Moderator Reviews',
            hide: 'Hide',
            show: 'Show',
            emptyModeratorReviews: 'No moderator reviews yet',
            voteAndReview: 'Moderator vote and review (judge feedback)',
            scoreLabel: 'Score {{score}}%',
            reviewInputPlaceholder: 'Write moderator review...',
            vote: 'Vote',
            voted: 'Voted',
            voting: 'Voting...',
        },
        reelsComments: {
            title: 'Comments',
            empty: 'No comments here yet',
            avatarAlt: 'avatar',
            loading: 'Loading...',
            hideReplies: 'Hide Replies',
            viewReplies: 'View Replies ({{count}})',
            reply: 'Reply',
            replyingTo: 'Replying to @{{username}}...',
            commentPlaceholder: 'Add a comment...',
            send: 'Send',
            follow: 'Follow',
            following: 'Following',
            profileFutureFeatures: 'Follow, View Profile (future features)',
            fetchMoreFailed: 'Failed to fetch more comments:',
            fetchFailed: 'Failed to fetch comments:',
            likeUpdateFailed: 'Failed to update like status:',
            fetchRepliesFailed: 'Failed to fetch replies:',
            replyPostFailed: 'Failed to post reply:',
            commentPostFailed: 'Failed to post comment:',
        },
        challengeDetails: {
            title: 'Challenge Details',
            notFound: 'Challenge not found.',
            reward: 'Reward',
            entries: 'Entries',
            duration: 'Duration',
            challengeTitle: 'Challenge - {{title}}',
            startsIn: 'This challenge will start in:',
            upcomingDescription: 'This challenge will be starting in {{days}} day(s). In the meantime, invite your friends to participate.',
            upcomingAlt: 'Challenge upcoming',
            mascotAlt: 'Challenge mascot',
            noCaption: 'No caption',
            noDescription: 'No description provided.',
            noEntriesYet: 'No entries yet.',
            noRulesSpecified: 'No rules specified by the challenge owner.',
            noReferenceVideo: 'No reference video provided.',
            noReviewComment: 'No review comment',
            noReviewsAvailable: 'No reviews available.',
            noModeratorsAssigned: 'No moderators assigned yet.',
            noDisqualifiedEntries: 'No disqualified entries.',
            noModerationLogs: 'No moderation logs yet.',
            noReasonProvided: 'No reason provided',
            noDetails: 'No details',
            unknown: 'Unknown',
            notResolved: 'Not resolved',
            prize: 'Prize',
            challengeMeta: 'Challenge Meta',
            creator: 'Creator: {{creator}}',
            entriesCount: 'Entries: {{count}}',
            moderatorsCount: 'Moderators: {{count}}',
            moderatorWeight: 'Moderator weight: {{weight}}%',
            moderatorWeightRow: '{{email}} · Weight {{weight}}%',
            resolvedEmail: 'Resolved email: {{email}}',
            challengeWindow: 'Challenge window: {{start}} - {{end}}',
            informationTitle: 'Challenge Information',
            referenceVideo: 'Reference Video',
            openReferenceMedia: 'Open reference media',
            engagementRules: 'Engagement rules',
            shareYourChallenge: 'Share your challenge',
            shareToGetEntries: 'Share challenge to start getting entries.',
            submitEntryTitle: 'Submit Entry',
            submitEntrySubtitle: 'Upload your challenge performance and submit it to the leaderboard.',
            uploadEntryVideo: 'Upload Challenge Entry Video',
            captionOptional: 'Caption (optional)',
            captionPlaceholder: 'Tell people about your entry',
            moderators: 'Moderators',
            moderator: 'Moderator',
            moderatorReviews: 'Moderator Reviews',
            moderatorReviewComment: 'Moderator review comment',
            entryReviews: 'Entry Reviews',
            voteScore: 'Vote Score: {{score}}%',
            optionalReviewComment: 'Optional review comment',
            makeReviewPublic: 'Make review public',
            disqualifiedEntries: 'Disqualified Entries',
            moderationActivity: 'Moderation Activity',
            activity: 'activity',
            upcomingChallenge: 'Upcoming Challenge',
            expressInterestHint: 'Express interest to stay notified and show intent to participate.',
            interestAlreadyExpressed: 'Interest already expressed.',
            interestedUsers: 'Interested users: {{count}}',
            likes: 'Likes',
            likesCount: '{{count}} likes',
            showLess: 'Show less',
            showMore: '... more',
            entryDisqualified: 'This entry is disqualified.',
            viewReviewsAndComments: 'View reviews and comments',
            assignModeratorsSubtitle: 'Assign and manage co-judges for this challenge.',
            inviteParticipants: 'Invite participants',
            inviteParticipantsSubtitle: 'Invite individuals by email or choirs by ID.',
            inviteUserEmail: 'Participant email',
            inviteChoirId: 'Choir ID',
            enterUsernameOrEmail: 'Enter username or email',
            alreadyVotedOnEntry: 'You already voted on this entry.',
            anonymous: 'Anonymous',
            communityMember: 'Community member',
            public: 'public',
            private: 'private',
            scorePrefix: 'Score {{score}}',
            shareJoinText: 'Join this challenge: {{title}}',
            you: 'You',
            actions: {
                submitting: 'Submitting...',
                expressInterest: 'Express Interest',
                interestExpressed: 'Interest Expressed',
                enterChallenge: 'Enter Challenge',
                shareChallenge: 'Share Challenge',
                vote: 'Vote',
                voted: 'Voted',
                editChallenge: 'Edit Challenge',
                manageModerators: 'Manage Moderators',
                leaderboard: 'Leaderboard',
                share: 'Share',
                resolve: 'Resolve',
                assigning: 'Assigning...',
                assignModerator: 'Assign Moderator',
                inviting: 'Inviting...',
                inviteUser: 'Invite User',
                inviteChoir: 'Invite Choir',
                remove: 'Remove',
                submitEntry: 'Submit Entry',
                submitVote: 'Submit Vote',
                disqualify: 'Disqualify',
                loadReviews: 'Load Reviews',
                submitReview: 'Submit Review',
            },
            countdown: {
                days: 'Days',
                hours: 'Hrs',
                minutes: 'Mins',
                seconds: 'Secs',
            },
            aria: {
                openLeaderboard: 'Open leaderboard',
                openChallengeInformation: 'Open challenge information',
                shareChallenge: 'Share challenge',
                copyChallengeLink: 'Copy challenge link',
                voteOnEntry: 'Vote on entry',
                alreadyVotedOnEntry: 'You already voted on this entry',
                likes: 'Likes',
                viewReviewsAndComments: 'View reviews and comments',
                previousEntry: 'Previous entry',
                nextEntry: 'Next entry',
                submitChallengeEntry: 'Submit challenge entry',
            },
            prompts: {
                disqualifyReason: 'Provide disqualification reason (min 5 characters):',
            },
            toasts: {
                entrySubmitted: 'Challenge entry submitted successfully.',
                entrySubmitFailed: 'Unable to submit challenge entry.',
                interestExpressed: 'Interest expressed successfully.',
                interestFailed: 'Unable to express interest.',
                userNotFound: 'No user found for the provided username.',
                userLookupFailed: 'Unable to find user by username.',
                resolveModeratorFirst: 'Resolve a valid moderator email first.',
                moderatorAssigned: 'Moderator assigned successfully.',
                assignModeratorFailed: 'Unable to assign moderator.',
                moderatorRemoved: 'Moderator removed successfully.',
                removeModeratorFailed: 'Unable to remove moderator.',
                inviteCreated: 'Challenge invite created successfully.',
                inviteFailed: 'Unable to create challenge invite.',
                enterInviteTarget: 'Enter a valid invite target first.',
                voteSubmitted: 'Vote submitted successfully.',
                voteFailed: 'Unable to submit vote.',
                disqualified: 'Entry disqualified successfully.',
                disqualifyFailed: 'Unable to disqualify entry.',
                reviewTooShort: 'Review comment must be at least 3 characters.',
                reviewSubmitted: 'Entry review submitted.',
                reviewFailed: 'Unable to submit review.',
                linkCopied: 'Challenge link copied to clipboard.',
                shareFailed: 'Unable to share challenge right now.',
                copyFailed: 'Unable to copy challenge link right now.',
                likeUpdateFailed: 'Unable to update like right now.',
            },
            entryAlt: 'entry',
            entryAvatarAlt: 'entry avatar',
            entryByActor: 'Entry #{{entryId}}{{actorPart}}',
            byActor: 'by {{actor}}',
            moderatorSharePrompt: 'Share this challenge to drive more entries.',
        },
        contribution: {
            breadcrumb: 'Contributions > {{title}}',
            unknownTitle: 'Unknown Title',
            filters: {
                all: 'All',
                searchByUsername: 'Search by contributor username',
                updating: 'Updating',
            },
            empty: {
                needHelp: 'No contributions for this part yet. Need help figuring it out?',
                requestHelp: 'Request Help for This Part',
                beFirst: 'No contributions for this part yet. Be the first to contribute!',
                submitContribution: 'Submit Your Contribution',
            },
            pagination: {
                prev: 'Prev',
                next: 'Next',
                pageOf: 'Page {{page}} of {{total}}',
            },
            error: {
                generic: 'Something went wrong',
            },
        },
        requests: {
            title: 'Requests',
            searchBySongTitle: 'Search by song title',
            loadMore: 'Load More',
            filters: {
                all: 'All',
            },
            empty: {
                alt: 'No requests',
                description: 'No requests found for your filters.',
                prompt: 'Want to request a part from the community?',
                cta: 'Request a Contribution',
            },
            error: {
                alt: 'Error',
                loadFailed: 'Failed to load requests',
            },
        },
        requestDetail: {
            comingSoon: 'Request details are coming soon.',
            error: {
                generic: 'Something went wrong',
            },
        },
        reelDetails: {
            title: 'Reel Details',
            invalidId: 'Invalid reel id. Use a UUID-style reel link.',
        },
        reelsFeed: {
            swipeUp: 'Swipe Up',
            clipboardUnsupported: 'Clipboard API not supported',
            linkShared: 'Link shared successfully!',
            linkShareFailed: 'Failed to share link',
            linkCopied: 'Link copied to clipboard!',
            linkCopyFailed: 'Failed to copy link',
        },
    },
    marketplace: {
        loadSongsFailed: 'Failed to load marketplace songs',
        noListings: 'No marketplace listings are available right now.',
        goBack: 'Go back',
        title: 'Title',
        artist: 'Artist',
        access: 'Access',
        price: 'Price',
        duration: 'Duration',
        action: 'Action',
        accessPremium: 'Premium',
        accessMasterOnly: 'Master Only',
        accessStems: 'Stems',
        premiumPreview: 'Premium / Preview',
        buyRent: 'Buy/Rent',
        orders: {
            loadFailed: 'Failed to load marketplace orders',
            empty: 'You do not have any marketplace orders yet.',
            browseMarketplace: 'Browse Marketplace',
            title: 'My Orders',
            subtitle: 'Review marketplace checkout activity, payment state, and direct links back to the songs you unlocked.',
            columns: {
                order: 'Order',
                status: 'Status',
                amount: 'Amount',
                date: 'Date',
            },
            unavailable: 'Unavailable',
            viewOrder: 'View Order',
        },
        purchases: {
            loadFailed: 'Failed to load marketplace purchases',
            empty: 'You do not have any active marketplace purchases yet.',
            browseMarketplace: 'Browse Marketplace',
            title: 'My Purchases',
            subtitle: 'Songs you already unlocked through direct purchase, rental access, or completed marketplace checkout.',
            paid: 'Paid',
            owned: 'Owned',
            rental: 'Rental',
            masterOnly: 'Master Only',
            stemsIncluded: 'Stems Included',
            unavailable: 'Unavailable',
            openSong: 'Open Song',
        },
        orderDetail: {
            loadFailed: 'Failed to load marketplace order',
            notFound: 'Marketplace order not found',
            marketplaceOrder: 'Marketplace Order',
            backToOrders: 'Back to Orders',
            openSong: 'Open Song',
            status: 'Status',
            entitlement: 'Entitlement:',
            expires: 'Expires:',
            payment: 'Payment',
            provider: 'Provider:',
            reference: 'Reference:',
            accessScope: 'Access Scope',
            orderSuffix: 'order',
            detailsTitle: 'Order Details',
            detailsSubtitle: 'Reference information for this marketplace transaction.',
            orderType: 'Order Type',
            created: 'Created',
            updated: 'Updated',
            song: 'Song',
        },
        orderModal: {
            referenceCopied: 'Reference copied to clipboard',
            copyPaymentReferenceAria: 'Copy payment reference',
            copyReference: 'Copy reference',
        },
        listings: {
            loadFailed: 'Failed to load marketplace listings',
            updated: 'Marketplace listing updated.',
            updateFailed: 'Failed to update marketplace listing',
            loadOrdersFailed: 'Failed to load listing orders',
            listingForfeited: 'Listing forfeited.',
            appealSubmitted: 'Appeal submitted.',
            reportResponseFailed: 'Failed to submit report response',
            empty: 'You have not created any marketplace-eligible community songs yet.',
            title: 'My Listings',
            subtitle: 'Manage marketplace visibility, pricing, and order flow for songs you created.',
            table: {
                listing: 'Listing',
                revenue: 'Revenue',
            },
            stats: {
                activeListings: 'Active Listings',
                stillDraft: '{{count}} still in draft',
                premiumEnabled: 'Premium Enabled',
                premiumHint: 'Listings that support subscription unlock',
                revenue: 'Revenue',
                zeroUsd: 'USD 0.00',
                revenueHint: 'Direct marketplace earnings tracked so far',
                loadedOrders: 'Loaded Orders',
                loadedOrdersHint: 'Seller orders viewed in this session',
            },
            buySummary: 'Buy {{price}}',
            rentSummary: 'Rent {{price}}{{duration}}',
            notConfigured: 'Not configured',
            listed: 'Listed',
            processingHint: 'Background processing is still running. Listing will unlock when ready.',
            noneYet: 'None yet',
            editListing: 'Edit Listing',
            viewOrders: 'View Orders',
            health: {
                processing: 'Processing',
                ready: 'Ready',
                reported: 'Reported',
                draft: 'Draft',
                needsAccessPath: 'Needs access path',
                premiumOnly: 'Premium only',
                missingRentalDays: 'Missing rental days',
                missingBuyPrice: 'Missing buy price',
                missingRentPrice: 'Missing rent price',
            },
            editor: {
                title: 'Edit Listing',
                byline: '{{title}} by {{artist}}',
                processingHint: 'This song is still processing in the background. Listing stays disabled until packaging is complete.',
                songReport: 'Song Report',
                reportedForReview: 'Reported for review',
                lastReported: 'Last reported {{date}}',
                open: 'open',
                adminRequestedExplanation: 'Admin requested an explanation for this listing. You can submit an appeal or forfeit the listing.',
                explanationPlaceholder: 'Explain why this listing should remain available',
                submitting: 'Submitting...',
                submitAppeal: 'Submit Appeal',
                forfeitListing: 'Forfeit Listing',
                creatorResponseStatus: 'Creator response status: {{status}}',
                listSong: 'List Song',
                premiumAccess: 'Premium Access',
                enableSale: 'Enable Sale',
                salePricePlaceholder: 'Sale price in minor units',
                enableRent: 'Enable Rent',
                rentPricePlaceholder: 'Rent price in minor units',
                rentalDurationPlaceholder: 'Rental duration in days',
                accessScope: 'Access Scope',
                currency: 'Currency',
                saving: 'Saving...',
                saveListing: 'Save Listing',
            },
            sellerOrders: {
                title: 'Seller Orders',
                fallbackSubtitle: 'Marketplace listing orders',
                orders: 'Orders',
                paidRevenue: 'Paid Revenue',
                listedScope: 'Listed Scope',
                noneYet: 'None yet',
                empty: 'No marketplace orders for this listing yet.',
            },
        },
        earnings: {
            loadFailed: 'Failed to load marketplace earnings',
            unavailable: 'Marketplace earnings are unavailable right now.',
            title: 'Earnings',
            subtitle: 'Marketplace gross revenue and creator-share royalties for your community song listings.',
            reportingWindow: 'Reporting Window',
            chart: {
                listings: 'Listings',
                active: 'Active',
                premium: 'Premium',
                paidSongs: 'Paid Songs',
                orders: 'Orders',
                royalties: 'Royalties',
                series: {
                    listingHealth: 'Listing Health',
                    marketplaceActivity: 'Marketplace Activity',
                    royaltyEvents: 'Royalty Events',
                },
            },
            filters: {
                allTime: 'All time',
                start: 'Start',
                now: 'Now',
                from: 'From',
                to: 'To',
                apply: 'Apply',
                reset: 'Reset',
            },
            cards: {
                totalEarnings: {
                    title: 'Total Earnings',
                    multiMarketValue: '{{count}} markets',
                    singleCurrencyNote: 'Gross marketplace revenue',
                    multiCurrencyNote: 'Direct revenue across currencies',
                },
                creatorRoyalty: {
                    title: 'Creator Royalty',
                    multiLedgerValue: '{{count}} ledgers',
                    singleCurrencyNote: 'Your royalty share',
                    multiCurrencyNote: 'Creator share across currencies',
                },
                totalTracks: {
                    title: 'Total Tracks',
                    note: '{{count}} active listings',
                },
                totalEngagement: {
                    title: 'Total Engagement',
                    note: '{{orders}} orders • {{events}} royalty events',
                },
                projectContribution: {
                    title: 'Project Contribution',
                    pendingValue: '{{count}} pending',
                    noPendingValue: 'No pending payout',
                    pendingNote: 'Pending payout(s) from project contributions',
                    noPendingNote: 'No project contribution payouts awaiting withdrawal/admin processing',
                },
            },
            analytics: {
                title: 'Overview analytics',
                subtitle: 'Marketplace activity, listing health, and creator royalty coverage for the selected window.',
            },
            topSongs: {
                title: 'Top Earning Music Tracks',
                subtitle: 'Gross marketplace leaders in the selected reporting window.',
                empty: 'No earning songs are available yet.',
                goToListings: 'Go to My Listings',
            },
            table: {
                revenue: 'Revenue',
                orders: 'Orders',
                openSong: 'Open {{song}}',
            },
            royalty: {
                title: 'Creator Royalty Breakdown',
                subtitle: 'Creator-share revenue from settled community-song royalty events in the selected reporting window.',
                emptyTitle: 'No royalty events yet',
                emptyDescription: 'Creator royalty entries will appear here once paid community-song sale and rent orders settle into the royalty ledger.',
                table: {
                    currency: 'Currency',
                    royalty: 'Royalty',
                    events: 'Events',
                    eventsCount: '{{count}} events',
                },
            },
            coverage: {
                title: 'Coverage',
                description: 'Creator royalty totals here already include settled community-song sale and rent events. Stream-driven royalties will appear in this same ledger once community-song playback emits stream events.',
            },
        },
        checkout: {
            successTitle: 'Payment received',
            successDescription: 'We are checking your marketplace order and refreshing access.',
            pendingTitle: 'Payment pending',
            pendingDescription: 'Your payment callback reached VocalCentric. We are waiting for the final order state.',
            failureTitle: 'Checkout not completed',
            failureDescription: 'The payment flow did not complete. You can retry from the song page or review your order history.',
            loadStatusFailed: 'Unable to load checkout status',
            canceledReason: 'You closed the payment flow before completion.',
            accessExpires: 'Access expires',
            viewOrders: 'View Orders',
            viewPurchases: 'View Purchases',
        },
        songDetail: {
            listingDetail: 'Listing Detail',
            notFound: 'Marketplace song not found',
            loadFailed: 'Failed to load marketplace song',
            checkoutInitialized: 'Checkout initialized',
            checkoutFailed: 'Unable to start checkout',
            songReported: 'Song reported',
            reportFailed: 'Failed to report song',
            marketplaceSong: 'Marketplace Song',
            premiumEligible: 'Premium Eligible',
            reported: 'Reported',
            report: 'Report',
            information: 'Information',
            licensing: 'Licensing',
            total: 'Total',
            openInPlayer: 'Open in Player',
            previewInPlayer: 'Preview in player',
            openPlayer: 'Open Player',
            upgrade: 'Upgrade',
            rentNow: 'Rent now',
            buyNow: 'Buy now',
            unlisted: 'This song is not currently listed in the marketplace.',
            purchase: 'Purchase',
            multitracks: 'MultiTracks',
            masterRental: 'Master Rental',
            daysAccess: '{{days}} days access',
            timedAccess: 'Timed access',
            masterTrack: 'Master track',
            fullMasterPreview: 'Full master preview',
            masterBuy: 'Master Buy',
            masterStemsBuy: 'Master + Stems Buy',
            permanentAccess: 'Permanent access',
            permanentFullAccess: 'Permanent full access',
            masterOnly: 'Master only',
            masterStems: 'Master + stems',
            included: 'Included',
            includedWithPremium: 'Included with Premium',
            alreadyUnlocked: 'Already unlocked',
            premiumUnlockHint: 'Premium users unlock this listing automatically.',
            usageTerms: 'Usage Terms',
            notes: 'Notes',
            marketplaceAccess: 'Marketplace access',
            unlocked: 'Unlocked',
            previewOnly: 'Preview only',
            accessExpiresOn: 'Access expires on {{date}}.',
            noTags: 'No tags added yet.',
            timed: 'Timed',
            rentalAccess: '{{days}} rental access',
            permanentLicenseAccess: 'Permanent license access',
            masterPlaybackOnly: 'Master playback only',
            masterStemsAccess: 'Master + stems access',
            premiumInclusionAvailable: 'Premium inclusion available',
            premiumInclusionDisabled: 'Premium inclusion disabled',
            sharedPlayerPreviewFlow: 'Shared player preview flow',
            artworkAlt: '{{song}} artwork',
            metadata: {
                published: 'Published',
                songTitle: 'Song Title',
                artistName: 'Artist Name',
                creator: 'Creator',
            },
            stems: {
                readyInSharedPlayer: 'Ready in shared player',
                lockedUntilAccess: 'Locked until listing access is granted',
                masterOnlyNotice: 'This listing is master-only. Buyers can preview and unlock the main track, but stems stay hidden.',
                noneAvailable: 'No additional multitracks are available on this listing yet.',
            },
            reasons: {
                creator: 'You created this song, so Marketplace playback is already unlocked on your account.',
                entitlement: 'You already unlocked this song. Open it in the shared player or revisit your purchase history.',
                premium: 'Included with your premium plan because this creator enabled premium access.',
                purchaseRequired: 'You can preview the master now, then rent or buy to unlock the full listing.',
                unlisted: 'This song is not currently listed for marketplace access.',
                default: 'Marketplace access details are available below.',
            },
        },
    },
    notifications: {
        title: 'Notifications',
        subtitle: 'Manage your notifications and stay up to date on important updates.',
        markAllAsRead: 'Mark all as read',
        empty: 'No notifications here yet',
        timeAgo: '{{time}} ago',
    },
    requestSongs: {
        title: 'Song Requests',
        subtitle: 'Vote for songs you would like to see on the platform',
        addSongRequest: 'Add Song Request',
        searchPlaceholder: 'Search for songs (e.g "Way Maker by Sinach")',
        songTitle: 'Song Title',
        enterSongTitle: 'Enter Song Title',
        artistName: 'Artist Name',
        enterArtistName: 'Enter Artist Name',
        albumName: 'Album Name',
        enterAlbumName: 'Enter Album Name',
        mediaLink: 'Media Link',
        mediaLinkPlaceholder: 'Preferably Spotify, Apple Music, AudioMac, Youtube',
        thumbnailLink: 'Thumbnail Link',
        thumbnailLinkPlaceholder: 'Song art or cover image',
        save: 'Save',
        cancel: 'Cancel',
        songRequestCreated: 'Song Request Created',
        songRequestFailed: 'Song Request Failed',
        tableTitle: 'Title',
        tableGenres: 'Genres',
        tableVotes: 'Votes',
        tableActions: 'Actions',
        songVotedSuccessfully: 'Song voted successfully',
        songVotingFailed: 'Song voting failed',
        notSpecified: 'Not Specified',
        voted: 'Voted',
        vote: 'Vote',
        iconAlt: 'icon',
    },
    requestRole: {
        title: 'Role Request',
        subtitle: 'Select your preferred role to get short listed.',
        applyWhitelisted: 'Apply to be Whitelisted',
        applyDirector: 'Apply to be Director',
        fillToApply: 'Fill appropriately to apply.',
        noApplicationFound: 'No application found',
        goBack: 'Go Back',
        goToDashboard: 'Go to Dashboard',
        reApply: 'Re-Apply',
        uploadHelp: 'Upload a video of yourself showing your skillset and competence level (teaching/playing/singing). Duration should be less than 2-5 minutes. This will be used to assess your singing ability.',
        socialMediaPlaceholder: 'Enter Social Media Profile *',
        socialMediaHandle: 'Social Media Handle',
        reasonPlaceholder: 'Why are you applying for this role? *',
        reason: 'Reason',
        websiteLink: 'Website Link',
        websiteLinkOptional: 'Website Link (Optional)',
        apply: 'Apply',
        cancel: 'Cancel',
        whitelistSubmitted: 'Whitelist Application submitted successfully',
        directorSubmitted: 'Director Application submitted successfully',
        status: {
            director: {
                approved: 'You have already been approved to be a Director',
                pending: 'Your application to be a Director is still pending',
                rejected: 'Your application to be a Director has been rejected',
            },
            whitelisted: {
                approved: 'You have already been approved to be Whitelisted',
                pending: 'Your application to be Whitelisted is still pending',
                rejected: 'Your application to be Whitelisted has been rejected',
            },
            joinChoir: {
                approved: 'Your request to join the choir has been approved',
                pending: 'Your request to join the choir is still pending',
                rejected: 'Your request to join the choir has been rejected',
            },
        },
    },
    rehearsal: {
        all: 'All',
        allDescription: 'All personal rehearsal songs',
        rehearsed: 'Rehearsed',
        rehearsedDescription: 'Rehearsed songs',
        new: 'New',
        newDescription: 'New songs in rehearsals',
        startRehearsal: 'Start Rehearsal',
        startingRehearsal: 'Starting Rehearsal',
        tableTitle: 'Title',
        tableAlbum: 'Album',
        tableGenre: 'Genre',
        tableVotes: 'Votes',
        tableDuration: 'Duration',
        tableAction: 'Action',
        empty: 'No rehearsals found for you, create one now!',
        loading: 'Loading...',
        searchToAdd: 'Search music, artist and album to add to your rehearsal',
        generateShareLink: 'Generate Share Link',
        chat: {
            noMessages: 'No chat messages',
            typeMessage: 'Type a message',
            avatarAlt: 'avatar',
            participantFallback: 'John Doe',
            participants: '{{count}} participants',
        },
        scoreSheet: 'Score Sheet',
        songSlides: {
            selectSlide: 'Select slide',
        },
        songMeta: {
            artist: 'Artist',
            writer: 'Writer',
            label: 'Label',
            theme: 'Theme',
            genre: 'Genre',
            album: 'Album',
            bpm: 'BPM',
            timeSig: 'Time Sig',
            duration: 'Duration',
            key: 'Key',
            originalKey: 'Original Key',
            rehearsalKey: 'Rehearsal Key',
            publicDomain: 'Public Domain',
            gospel: 'Gospel',
            na: '__',
            rehearsedBy: 'Rehearsed by',
            votes: '{{count}} votes',
            coverArtAlt: '{{title}} cover art',
        },
        tabs: {
            lyrics: 'Lyrics',
            lyricsDescription: 'Lyrics for this song',
            slides: 'Slides',
            slidesDescription: 'Slides for this song',
            practice: 'Practice',
            practiceDescription: 'Practice this song',
            customHarmonies: 'Custom Harmonies',
            customHarmoniesDescription: 'List of custom harmonies for this choir rehearsal',
            reviewPractice: 'Review Practise',
            reviewPracticeDescription: 'Choir rehearsal practice reviews',
            practise: 'Practise',
            practiseDescription: 'Choir rehearsal practice',
            addAnotherFile: 'Add another file',
            noSlides: 'No slides available',
            addSlidesToStart: 'Add slides to this song to get started',
        },
    },
    rehearsalPractice: {
        subtitle: 'Select a section, inspect the take, review the coaching note, and move to the next phrase without losing your place.',
        personalRehearsal: 'Personal Rehearsal',
        sectionFeedback: 'Section Feedback',
        closeFeedback: 'Close feedback',
        previousSection: 'Previous section',
        nextSection: 'Next section',
        takePreview: 'Take Preview',
        lock: 'Lock',
        submitted: 'submitted',
        countIn: 'count in',
        playbackOnly: 'Playback only',
        stopClickPreview: 'Stop click preview',
        previewClick: 'Preview click',
        click: 'Click',
        singAlongGuide: 'Sing along with the guide track',
        recordWithoutGuide: 'Record without the guide track',
        singAlongEnabled: 'Sing along enabled',
        singAlongDisabled: 'Sing along disabled',
        pauseTake: 'Pause take',
        playTake: 'Play take',
        viewFeedback: 'View feedback',
        stopRecording: 'Stop recording',
        cancelCountIn: 'Cancel count-in',
        recordTake: 'Record take',
        discardTake: 'Discard take',
        submitForAiReview: 'Submit for AI review',
        recommendedNextStep: 'Recommended Next Step',
        retryRecording: 'Retry Recording',
        aiNoteTitle: 'AI Note',
        aiNoteFallback: 'Record a take for this section to receive focused feedback from your VocalCentric coach.',
        overallScore: 'Overall Score',
        performanceBreakdown: 'Performance Breakdown',
        performanceBreakdownSubtitle: 'Open to compare the metric summary and radar analysis in one place.',
        nextStepRetryFocus: 'Retry this take with your attention on {{metric}}. {{description}}',
        nextStepDefault: 'Record or re-record this section, then review the updated note before moving to the next phrase.',
        scoreSummary: {
            strongPass: 'Strong pass',
            gettingCloser: 'Getting closer',
            needsAnotherTake: 'Needs another take',
        },
        status: {
            reviewed: 'Reviewed',
            submitted: 'Submitted',
            needsWork: 'Needs work',
            retry: 'Retry',
            awaitingReview: 'Awaiting review',
            notStarted: 'Not started',
        },
        sectionNavigator: {
            practiceProgress: 'Practice Progress',
            title: 'Section Navigator',
            subtitle: 'Jump to any section, see its status, and open feedback without leaving the grid.',
            reviewed: 'Reviewed',
            pending: 'Pending',
            needsWork: 'Needs Work',
            vocal: 'Vocal',
        },
        metrics: {
            pitch: {
                label: 'Pitch',
                description: 'Match the target note more consistently.',
            },
            rhythm: {
                label: 'Rhythm',
                description: 'Tighten your timing against the groove.',
            },
            energy: {
                label: 'Energy',
                description: 'Control volume and breath support more evenly.',
            },
            chroma: {
                label: 'Chroma',
                description: 'Keep the tonal center stable across the phrase.',
            },
            swass: {
                label: 'SWASS',
                description: 'Focus on clarity and consistency of the vocal tone.',
            },
            zcr: {
                label: 'ZCR',
                description: 'Smooth out noisy attacks and consonant sharpness.',
            },
        },
    },
    choirRehearse: {
        common: {
            loading: 'Loading...',
            goBack: 'Go Back',
        },
        unsavedLeavePrompt: 'You have unsaved harmony changes. Are you sure you want to leave this page?',
        sectionKeySaved: 'Section key updated',
        sectionKeySaveFailed: 'Failed to update the section key',
        selectedHarmonyResource: 'Selected rehearsal arrangement',
        openHarmonyResource: 'Open arrangement',
        harmonyResourceOpenFailed: 'Unable to open the selected arrangement.',
        dryRun: {
            title: 'Dry-run practice',
            backToRehearsal: 'Back to rehearsal',
            audioNotFound: 'This dry-run audio could not be found.',
            lyrics: 'Lyrics and notes',
            practiceLoadFailed: 'Unable to load dry-run practices.',
            microphoneFailed: 'Microphone access is unavailable.',
            practiceSubmitted: 'Dry-run practice submitted',
            practiceSubmitFailed: 'Unable to submit dry-run practice.',
            submissions: 'Practice submissions',
            submissionProgress: '{{submitted}} of {{total}} members submitted',
            searchMembers: 'Search members',
            filterAll: 'All',
            filterSubmitted: 'Submitted',
            filterPending: 'Pending',
            nudgeAll: 'Nudge all pending',
            nudge: 'Nudge',
            nudgeSent: 'Reminder sent to {{count}} member(s).',
            nudgeFailed: 'Unable to send the practice reminder.',
            pending: 'Pending submission',
            noMatchingMembers: 'No members match this filter.',
            takes: '{{count}} take(s)',
            playbackFailed: 'Practice playback is unavailable.',
            feedbackPlaceholder: 'Optional feedback for this take',
            needsImprovement: 'Needs improvement',
            approve: 'Approve',
            reviewSaved: 'Practice review saved',
            reviewFailed: 'Unable to save this review.',
            yourPractice: 'Your practice',
            part: 'Choir part',
            selectPart: 'Select your part',
            startRecording: 'Start recording',
            stopRecording: 'Stop recording',
            recordAgain: 'Record another take',
            discardTake: 'Discard take',
            submit: 'Submit practice',
            resubmit: 'Resubmit practice',
            submitting: 'Submitting...',
            status: {
                pending: 'Awaiting review',
                approved: 'Approved',
                needs_improvement: 'Needs improvement',
            },
        },
        metadataEditor: {
            editButton: 'Edit song metadata',
            title: 'Edit Song Metadata',
            description: 'Update song key, tempo, and time signature for this rehearsal.',
            tempoRangeError: 'Tempo must be between 40 and 240 BPM.',
            timeSignatureFormatError: 'Time signature must use a valid format like 4/4.',
            partialSaveError: 'Some song metadata fields could not be saved. Please try again.',
            saveSuccess: 'Song metadata updated',
            saveFailure: 'Failed to update song metadata',
        },
        lyrics: {
            mixed: 'Mixed',
            solfa: 'Solfa',
            solfaMain: 'Main Solfa',
            solfaTenor: 'Tenor Solfa',
            solfaAlto: 'Alto Solfa',
            solfaSoprano: 'Soprano Solfa',
            chordProgression: 'Chord Progression',
            rehearsalFallbackTitle: 'Rehearsal',
            comingSoon: 'Coming Soon!',
            referenceAudioUpload: 'Upload Audio',
            referenceAudioReplace: 'Replace Audio',
            referenceAudioRemove: 'Remove Audio',
            referenceAudioSaving: 'Saving Audio...',
            referenceAudioUploading: 'Uploading audio {{percent}}%',
            referenceAudioProcessing: 'Processing audio...',
            referenceAudioSaveSuccess: 'Reference audio updated for this choir',
            referenceAudioUploadFailed: 'Failed to upload reference audio',
            referenceAudioRemoveSuccess: 'Reference audio removed',
            referenceAudioRemoveFailed: 'Failed to remove reference audio',
        },
        lyricsEditor: {
            title: 'Edit External Song Content',
            description: 'Add section headings and lines, then generate structured sections to save this content for this rehearsal only.',
            placeholder: 'Add a heading like Verse 1, then add each lyric line below it.',
            generate: 'Generate structured sections',
            generated: 'Generated {{sectionCount}} section(s) across {{lineCount}} line(s).',
            noSections: 'No sections were found. Add headings like Verse 1 or Chorus before generating.',
            addCta: 'Add Lyrics',
            editCta: 'Edit Lyrics',
            emptyState: 'No lyrics have been added for this external song yet.',
            addTarget: 'Add {{target}}',
            editTarget: 'Edit {{target}}',
            emptyStateFor: 'No {{target}} has been added for this external song yet.',
            saveSuccess: 'Lyrics updated for this rehearsal',
            saveSuccessFor: '{{target}} updated for this rehearsal',
            saveFailure: 'Failed to update lyrics',
        },
        lyricsSync: {
            title: 'Sync External Song Timings',
            description: 'Match the rehearsal audio to your structured lyrics, then reuse those timings across solfa and chord progression for this rehearsal.',
            button: 'Sync Timings',
            recommended: 'Recommended',
            recommendedNote: 'After you time the main lyrics once, those timestamps will be applied to every other structured variant in this rehearsal.',
            synced: 'Synced',
            save: 'Save Sync',
            saveSuccess: 'Lyrics timings synced for this rehearsal',
            saveFailure: 'Failed to sync lyrics timings',
        },
        submissions: {
            goToRehearsal: 'Go to Rehearsal',
            empty: 'No submissions yet',
        },
        practicePage: {
            performanceTab: 'Performance',
            recentPerformance: 'Recent Performance',
            submitAll: 'Submit All',
            submissionAnalyzing: 'Your submission is currently being analyzed. You will be notified via your dashboard notification when it is ready.',
            performedVocalLabel: 'Performed Vocal:',
            alto: 'Alto',
            slideSheetAlt: 'Slide sheet',
            toasts: {
                noRecordings: 'No recordings to upload',
                uploadSuccess: 'Recording uploaded successfully',
                uploadError: 'Error uploading recordings',
            },
            metrics: {
                accuracyTitle: 'Accuracy Score:',
                accuracyDescription: 'How accurately you performed the vocals compared to the original recording.',
                pitchTitle: 'Pitch Accuracy:',
                pitchDescription: 'How closely you matched the pitch of the original vocals.',
                timingTitle: 'Timing and Rhythm:',
                timingDescription: 'Your ability to maintain proper timing and rhythm throughout the performance.',
                dynamicRangeTitle: 'Dynamic Range:',
                dynamicRangeDescription: 'Evaluation of your ability to express different levels of volume and intensity, essential for capturing the nuances of the original vocals.',
                noteArticulationTitle: 'Note Articulation:',
                noteArticulationDescription: 'Feedback on the clarity and precision of your notes. This is important for runs or phrasing.',
                vocalToneTitle: 'Vocal Tone Quality:',
                vocalToneDescription: 'Assessment of your ability to replicate the desired tone and timbre characteristic of the chosen vocal part.',
            },
        },
        reviewEntry: {
            userPracticeTab: "{{username}}'s Practice",
            toasts: {
                correctionSuccess: 'Correction uploaded successfully',
                correctionError: 'Error uploading correction',
            },
        },
        songListCard: {
            playAlt: 'play',
            remove: 'Remove',
            noHarmoniesForKey: 'No harmonies available for key {{key}}',
            generateHarmonies: 'Generate harmonies in {{key}} from original key {{originalKey}}',
            transposeHint: "We'll use the existing {{section}} parts from {{originalKey}} and transpose them.",
            addHarmonyPart: '+ Add Harmony Part',
            uploadAllForSection: 'Upload All Harmony Parts for {{section}}',
            moveUp: 'Move section up',
            moveDown: 'Move section down',
            expand: 'Expand {{section}}',
            collapse: 'Collapse {{section}}',
            reorderFailed: 'Unable to reorder harmony sections. Please try again.',
            deleteSectionTitle: 'Delete Section',
            deleteSectionDescription: 'Remove the entire {{section}} section and all its parts? This cannot be undone.',
            deleteSectionConfirm: 'Delete Section',
            visibility: {
                private: 'Private',
                choir_only: 'Choir only',
                public: 'Public',
                mixed: 'Mixed',
            },
        },
        reuseHarmonies: {
            entryTitle: 'Reuse existing harmonies',
            entryDescription: 'Generate missing parts for {{targetKey}} from another recorded key.',
            title: 'Reuse existing harmonies',
            description: 'Choose which recorded key should generate missing parts for {{targetKey}}.',
            targetKey: 'Working key',
            selectSource: 'Select source key',
            coverage: '{{parts}} parts across {{sections}} sections',
            generate: 'Generate harmonies',
            generating: 'Generating...',
            queued: 'Harmonies for {{targetKey}} are being generated',
            alreadyAvailable: 'All harmonies for this key are already available',
            failed: 'Failed to reuse existing harmonies',
        },
        harmonyLibrary: {
            entryTitle: 'Community harmonies',
            entryDescription: 'Browse arrangements shared by other singers and choirs.',
            title: 'Community harmonies',
            description: 'Preview and add an arrangement to this choir’s custom harmonies.',
            search: 'Search community harmonies',
            searchAction: 'Search',
            retry: 'Try loading community harmonies again',
            empty: 'No community harmonies yet',
            untitled: 'Untitled arrangement',
            community: 'Vocal Centric community',
            noKey: 'No key',
            preview: 'Preview',
            add: 'Add',
            adding: 'Adding...',
            unlock: 'Unlock',
            added: 'Harmony added to custom harmonies',
            alreadyAdded: 'This harmony is already in custom harmonies',
            addFailed: 'Failed to add the community harmony',
            unlockFailed: 'Failed to unlock this community harmony',
        },
        partRow: {
            midiTag: '• MIDI',
            pickExisting: 'Pick Existing',
            pickExistingOption: '{{name}} ({{section}}) on {{songKey}} {{midiTag}}',
            pauseAudioTooltip: 'Pause this audio part',
            playAudioTooltip: 'Play this audio part',
            pauseAudioAria: 'Pause audio part',
            playAudioAria: 'Play audio part',
            pauseMidiTooltip: 'Pause this MIDI part',
            playMidiTooltip: 'Play this MIDI part',
            pauseMidiAria: 'Pause MIDI part',
            playMidiAria: 'Play MIDI part',
            stopRecordingTooltip: 'Stop recording this harmony part',
            recordNewTakeTooltip: 'Record a new audio take',
            failedStartRecording: 'Unable to access microphone. Check browser permission and ensure you are on HTTPS.',
            stopRecordingAria: 'Stop recording',
            recordTakeAria: 'Record audio take',
            uploadTooltip: 'Upload an audio or MIDI file',
            uploadAria: 'Upload file',
            convertToMidiTooltip: 'Convert this audio take into editable MIDI',
            convertToMidiAria: 'Convert recording to MIDI',
            undoConvertTooltip: 'Restore the original audio before conversion',
            undoConvertAria: 'Undo MIDI conversion',
            duplicateMidiTooltip: 'Duplicate this MIDI into another harmony part',
            duplicateMidiAria: 'Duplicate MIDI to another part',
            closeMidiEditorTooltip: 'Close the MIDI editor',
            openMidiEditorTooltip: 'Open the piano MIDI editor',
            collapseMidiEditorAria: 'Collapse MIDI editor',
            openMidiEditorAria: 'Open MIDI editor',
            removePartTooltip: 'Remove this harmony part',
            removePartAria: 'Remove harmony part',
            renameTooltip: 'Rename this transposed harmony part',
            renameAria: 'Rename {{part}}',
            renameSuccess: 'Harmony part renamed',
            renameFailed: 'Failed to rename harmony part',
            ready: 'Ready',
            beats: 'beats',
            bpm: 'BPM',
            clickDuringTake: 'click during take',
            countInOnly: 'count-in only',
            clickOff: 'click off',
            loadingWaveform: 'Loading waveform...',
            align: 'Align',
            earlier: 'Earlier',
            later: 'Later',
            reset: 'Reset',
            uploadOrSelectBeforeSave: 'Please upload or select a file for this part before saving.',
            convertingToMidi: 'Converting audio to MIDI melody…',
            deletePartTitle: 'Delete Harmony Part',
            deletePartDescription: 'Remove {{part}} from this section? This cannot be undone.',
            deletePartConfirm: 'Delete Part',
            midiEditorTitle: 'MIDI Editor',
            midiEditorDescription: 'Edit and play the harmony part with more room.',
            closeMidiEditorAria: 'Close MIDI editor',
            saveMidiChangesTitle: 'Save MIDI changes?',
            saveMidiChangesDescription: 'You have unsaved MIDI edits. Save them before closing the editor, or discard them.',
            keepEditing: 'Keep Editing',
            discard: 'Discard',
            save: 'Save',
            recordingSetupTitle: 'Recording Setup',
            recordingSetupSubtitle: 'Set the click and count-in feel before recording this part.',
            closeRecordingSetupAria: 'Close recording setup',
            referenceTrack: 'Reference Track',
            none: 'None',
            lockTakeToReference: 'Lock take to reference',
            playReferenceInTake: 'Play reference in take',
            referenceVolume: 'Reference Volume',
            clickMode: {
                off: 'Off',
                'count-in-only': 'Count-In Only',
                'count-in-and-record': 'Count-In + Take',
            },
            timeSignature: 'Time Signature',
            tempo: 'Tempo',
            countInLabel: 'Count-In',
            bar: 'bar',
            bars: 'bars',
            countInOption: '{{bars}} {{unit}} ({{beats}} beats)',
            preview: 'Preview',
            previewClickTrack: 'Preview click track',
            compoundFeelNote: 'Compound feel active. The click groups this meter by dotted-quarter pulses for a more natural entrance.',
            referenceModeNote: 'Reference mode active. Tempo and time signature are locked to the selected reference so this take stays matched.',
            startRecording: 'Start Recording',
            uploadedPercent: '{{percent}}% uploaded',
            toasts: {
                noClearMelody: 'No clear melody was detected. Try a cleaner monophonic take.',
                convertedToMidi: 'Converted recording to MIDI',
                failedConvertToMidi: 'Failed to convert recording to MIDI',
                restoredAudio: 'Restored audio before MIDI conversion',
                duplicatedMidi: 'Duplicated MIDI to {{targetPart}} ({{intervalLabel}})',
                failedDuplicateMidi: 'Failed to duplicate MIDI part',
            },
        },
    },
    setlist: {
        yourSetlists: 'Your Setlists',
        tabs: {
            mine: 'My setlist',
            explore: 'Explore',
        },
        deletedSuccessfully: 'Setlist deleted successfully',
        linkCopied: 'Link copied to clipboard!',
        songCount: '{{count}} song(s)',
        updatedDate: 'Updated: {{date}}',
        empty: 'You haven’t created any setlists yet.',
        sharedToChoir: 'Setlist shared to choir',
        explore: {
            title: 'Explore public setlists',
            subtitle: 'Discover worship and performance lists with practical resources from other teams.',
            searchPlaceholder: 'Search setlists',
            creatorFallback: 'VocalCentric creator',
            free: 'Free',
            paid: 'Paid',
            premium: 'Premium',
            mixed: 'Mixed',
            resources: '{{count}} resource(s)',
            empty: 'No public setlists found.',
            loadFailed: 'Failed to load public setlists',
            access: {
                all: 'All access',
                free: 'Free',
                paid: 'Paid',
                premium: 'Premium',
                mixed: 'Mixed',
            },
        },
        owned: {
            back: 'Back to setlists',
            loadFailed: 'Failed to load setlist',
            notFound: 'This setlist could not be found.',
            personalEyebrow: 'Your setlist',
            choirEyebrow: 'Choir setlist',
            share: 'Share setlist',
            editDetails: 'Edit details',
            setlistName: 'Setlist name',
            nameRequired: 'Enter a setlist name with at least two characters.',
            scheduledFor: 'Scheduled for',
            editDetailsDescription: 'Keep the setlist name, visibility, schedule, and sharing details up to date.',
            detailsSaved: 'Setlist details saved',
            detailsSaveFailed: 'Failed to save setlist details',
            shareLink: 'Share link',
            loadingVideo: 'Loading video…',
            videoUnavailable: 'This video could not be loaded.',
            seekBackward15Seconds: 'Go back 15 seconds',
            seekForward15Seconds: 'Go forward 15 seconds',
            playSetlist: 'Play setlist',
            nowPlaying: 'Now playing',
            listen: 'Listen',
            songs: 'Setlist songs',
            addSong: 'Add song',
            addSongDescription: 'Choose a source, review the song, and add it to this setlist.',
            songRequired: 'Choose a song and add its setlist label.',
            songLabel: 'Setlist label',
            songSources: {
                search: 'Library',
                youtube: 'YouTube',
                public: 'Public domain',
            },
            playSong: 'Play song',
            pauseSong: 'Pause song',
            dockPlayer: 'Dock player',
            noPlayableMedia: 'No video',
            noSongs: 'No songs have been added yet.',
            deleteSongConfirm: 'Remove this song from the setlist?',
            listenTarget: 'Accountability listen target',
            listenTypes: {
                entire: 'Entire song',
                segment: 'Segment',
            },
            listenStart: 'Start',
            listenEnd: 'End',
            invalidListenSegment: 'Enter a valid listen segment.',
            entireTarget: 'Listen to the entire song',
            segmentTarget: 'Listen from {{start}} to {{end}}',
            prepare: 'Prepare',
            resources: 'Setlist resources',
            resourcesDescription: 'Charts, documents, links, stems, reference audio, and grouped vocal arrangements for this setlist.',
            noResources: 'No resources have been added yet.',
            manageResources: 'Manage resources',
            manageResourcesDescription: 'Create individual resources or grouped vocal arrangements and control how they are shared.',
            addResource: 'Add resource',
            editResource: 'Edit resource',
            deleteResource: 'Delete resource',
            deleteResourceConfirm: 'Delete this resource from the setlist?',
            resourceType: 'Resource type',
            resourceTypes: {
                document: 'Document',
                audio: 'Audio',
                stem: 'Stem',
                chart: 'Chart',
                link: 'Link',
                other: 'Other',
                vocal_arrangement: 'Vocal arrangement',
            },
            arrangementEditorDescription: 'Choose a song, add sections, and upload each vocal part separately.',
            arrangementSong: 'Song',
            selectArrangementSong: 'Select a song',
            arrangementKey: 'Song key',
            arrangementKeyOptional: 'No key selected',
            arrangementSections: 'Arrangement sections',
            sectionName: 'Section name',
            addSection: 'Add section',
            removeSection: 'Remove section',
            arrangementSectionRequired: 'Add at least one arrangement section.',
            arrangementPart: 'Vocal part',
            selectArrangementPart: 'Select a vocal part',
            addPart: 'Add part',
            removePart: 'Remove part',
            arrangementPartRequired: 'Add at least one vocal part to this section.',
            partAudio: 'Upload part audio',
            replacePartAudio: 'Replace part audio',
            arrangementRequired: 'Choose a song and add at least one section with an uploaded audio file for every part.',
            noArrangementParts: 'No playable vocal parts are available.',
            selectPart: 'Select {{part}}',
            resourceTitle: 'Title',
            resourceDescription: 'Description',
            resourceAccess: 'Access',
            premiumAccess: 'Premium subscribers',
            resourceUrl: 'Resource URL',
            uploadResource: 'Upload resource',
            audioFilesSupported: 'MP3, WAV, M4A, or AAC supported',
            documentFilesSupported: 'PDF or DOCX supported',
            resourcePrice: 'Price',
            resourceCurrency: 'Currency',
            resourcePublic: 'Show this resource on the public setlist',
            resourceRequired: 'Add a title and resource file or URL.',
            resourceSaved: 'Resource saved',
            resourceSaveFailed: 'Failed to save resource',
            resourceDeleted: 'Resource deleted',
            resourceDeleteFailed: 'Failed to delete resource',
            resourcePreview: 'Resource preview',
            audioUnavailable: 'This audio could not be loaded.',
            openResourceDescription: 'This resource opens in a new browser tab.',
            saving: 'Saving…',
            listenAudit: 'Listen audit',
            auditDescription: 'See who has listened and which songs are getting attention.',
            auditLoadFailed: 'Failed to load the listen audit',
            loadingAudit: 'Loading listen audit…',
            listeners: 'Listeners',
            listens: 'Listens',
            songsHeard: 'Songs heard',
            noAudit: 'No listening activity has been recorded yet.',
            nudgeMembers: 'Nudge members who have not listened',
            sendingNudge: 'Sending reminder…',
            nudgeSent: 'Reminder sent to {{count}} member(s)',
            nudgeFailed: 'Failed to send listen reminder',
        },
        public: {
            fallbackTitle: 'Setlist',
            ogDescription: 'A curated setlist on VocalCentric featuring {{count}} songs.',
            copyShareLink: 'Copy Share Link',
            labelPrefix: 'Label:',
            listenOnSpotify: 'Listen on Spotify',
            watchOnYoutube: 'Watch on YouTube',
            listen: 'Listen',
            resources: 'Resources',
            free: 'Free',
            paid: 'Paid',
            premium: 'Premium',
            openResource: 'Open resource',
            unlockResource: 'Unlock resource',
            upgradeForPremium: 'Upgrade to Premium',
            resourceCheckoutFailed: 'Failed to start resource checkout',
        },
    },
    transactions: {
        historyTitle: 'Transaction History',
        detailsTitle: 'Transaction Details',
        tableDate: 'Date',
        tableOrderNumber: 'Order Number',
        tablePrice: 'Price',
        viewDetails: 'View Details',
    },
    profile: {
        aboutTitle: 'About',
        noSummary: 'No profile summary added yet.',
        experience: 'Experience',
        years: '{{count}} years',
        notSpecified: 'Not specified',
        availability: 'Availability',
        availableForProjects: 'Available for projects',
        notCurrentlyAvailable: 'Not currently available',
        location: 'Location',
        timezone: 'Timezone',
        skillsTitle: 'Skills',
        addSkill: 'Add Skill',
        noSkills: 'No skills have been added yet.',
        editSkill: 'Edit skill',
        deleteSkill: 'Delete skill',
        portfolioTitle: 'Portfolio',
        addItem: 'Add Item',
        noPortfolioItems: 'No portfolio items yet.',
        previewMedia: 'Preview media',
        editPortfolioItem: 'Edit portfolio item',
        deletePortfolioItem: 'Delete portfolio item',
        previewPortfolioItem: 'Preview {{title}}',
        reviewsTitle: 'Reviews',
        reviewsSubtitle: 'Ratings and trust signals from previous collaborations.',
        rating: 'Rating',
        reviews: 'Reviews',
        completed: 'Completed',
        repeatHires: 'Repeat Hires',
        compactReviews: '({{count}} reviews)',
        uploading: 'Uploading {{progress}}%',
        uploadAvatar: 'Upload avatar',
        profileAvatar: 'Profile avatar',
        defaultHeadline: 'Creative professional on VocalCentric.',
        shareProfile: 'Share profile',
        editProfile: 'Edit Profile',
        addPortfolioItem: 'Add Portfolio Item',
        follow: 'Follow',
        unfollow: 'Unfollow',
        inviteToProject: 'Invite to Project',
        contact: 'Contact',
        locationUnavailable: 'Location unavailable',
        memberSince: 'Member since {{date}}',
        page: {
            inviteDisabledReason: 'You need at least one owned project to send invites.',
            unavailable: {
                title: 'Profile unavailable',
                needsInitialization: 'Your professional profile has not been created yet. Create it first, then update visibility if needed.',
                ineligible: 'Your account is not eligible to manage a professional profile.',
                ownerPrivate: 'Your professional profile is currently private. Make it public to view it on this route.',
                viewerPrivateOrMissing: 'This professional profile is private or does not exist.',
            },
            options: {
                profileType: {
                    creative: 'Creative',
                    owner: 'Project Owner',
                    both: 'Both',
                },
                visibility: {
                    public: 'Public',
                    private: 'Private',
                },
                skillType: {
                    voice_part: 'Voice Part',
                    instrument: 'Instrument',
                    role: 'Role',
                    specialty: 'Specialty',
                },
                proficiency: {
                    beginner: 'Beginner',
                    intermediate: 'Intermediate',
                    advanced: 'Advanced',
                    expert: 'Expert',
                },
                availability: {
                    'true': 'Available for projects',
                    'false': 'Not available',
                },
            },
            quickInfo: {
                title: 'Quick Info',
                type: 'Type',
                visibility: 'Visibility',
                defaultPart: 'Default Part',
            },
            payout: {
                title: 'Payout Accounts',
                description: 'Used for project payout transfers.',
                loading: 'Loading payout accounts...',
                empty: 'No payout account configured yet.',
                accountFallback: 'Payout account',
                defaultTag: 'default',
                noAccountNumber: 'No account number',
                noBankName: 'No bank name',
                swiftPrefix: 'SWIFT',
            },
            actions: {
                createProfile: 'Create Profile',
                makeProfilePublic: 'Make Profile Public',
                applyForWhitelist: 'Apply for Whitelist',
                add: 'Add',
                save: 'Save',
                close: 'Close',
                edit: 'Edit',
                remove: 'Remove',
                delete: 'Delete',
                copyEmail: 'Copy Email',
                openEmailApp: 'Open Email App',
                openMedia: 'Open media',
            },
            share: {
                fallbackText: 'View {{title}} on VocalCentric.',
            },
            contact: {
                mailSubject: 'VocalCentric profile contact: {{name}}',
                mailBody: 'Hi {{name}},\n\nI found your profile on VocalCentric and would like to connect.',
            },
            modals: {
                editProfile: {
                    title: 'Edit Profile',
                    description: 'Update professional identity details and portfolio preferences.',
                    headline: 'Headline',
                    displayNameOverride: 'Display Name Override',
                    displayNamePlaceholder: 'How your name should appear publicly',
                    portfolioSummary: 'Portfolio Summary',
                    summaryPlaceholder: 'Summary',
                    profileType: 'Profile Type',
                    timezone: 'Timezone',
                    selectTimezone: 'Select timezone',
                    visibility: 'Visibility',
                    availabilityForProjects: 'Availability for Projects',
                    city: 'City',
                    state: 'State',
                    country: 'Country',
                    selectCountry: 'Select country',
                    experienceYears: 'Experience (years)',
                    experiencePlaceholder: 'Years of experience',
                    websiteUrl: 'Website URL',
                    instagramUrl: 'Instagram URL',
                    youtubeUrl: 'YouTube URL',
                    tiktokUrl: 'TikTok URL',
                    linkedinUrl: 'LinkedIn URL',
                    urlPlaceholder: 'https://...',
                    primaryCategory: 'Primary Category',
                    selectCategory: 'Select category',
                    currency: 'Currency',
                    selectCurrency: 'Select currency',
                    minRate: 'Min Rate',
                    maxRate: 'Max Rate',
                    numberPlaceholder: '0',
                    rateNotes: 'Rate Notes',
                    notesPlaceholder: 'Notes',
                },
                payoutAccount: {
                    editTitle: 'Edit Payout Account',
                    addTitle: 'Add Payout Account',
                    description: 'Add your bank transfer details used for payouts.',
                    country: 'Country',
                    accountName: 'Account Name',
                    accountNamePlaceholder: 'Account holder name',
                    accountNumber: 'Account Number',
                    accountNumberPlaceholder: '0123456789',
                    bankName: 'Bank Name',
                    bankNamePlaceholder: 'Bank name',
                    loadingBanks: 'Loading banks...',
                    selectBank: 'Select bank',
                    swiftCode: 'SWIFT Code (Optional)',
                    swiftCodePlaceholder: 'For international transfers',
                    defaultLabel: 'Set as default',
                    saving: 'Saving...',
                    save: 'Save',
                    toast: {
                        requiredFields: 'Country, account name, account number, and bank name are required.',
                        selectBank: 'Please select a bank from the list.',
                        updated: 'Payout account updated.',
                        added: 'Payout account added.',
                        saveFailed: 'Unable to save payout account',
                    },
                },
                skill: {
                    editTitle: 'Edit Skill',
                    addTitle: 'Add Skill',
                    skillType: 'Skill Type',
                    skillName: 'Skill Name',
                    skillNamePlaceholder: 'Skill name',
                    proficiency: 'Proficiency',
                    yearsExperience: 'Years Experience',
                    numberPlaceholder: '0',
                    markPrimary: 'Mark as primary skill',
                },
                portfolio: {
                    editTitle: 'Edit Portfolio Item',
                    addTitle: 'Add Portfolio Item',
                    title: 'Title',
                    projectName: 'Project Name',
                    projectNamePlaceholder: 'Project name',
                    rolePerformed: 'Role Performed',
                    rolePlaceholder: 'Role',
                    category: 'Category',
                    mediaUrl: 'Media URL',
                    thumbnailUrl: 'Thumbnail URL',
                    externalUrl: 'External URL',
                    urlPlaceholder: 'https://...',
                    creditedArtist: 'Credited Artist',
                    artistNamePlaceholder: 'Artist name',
                    yearCompleted: 'Year Completed',
                    yearPlaceholder: '2026',
                    featureThisWork: 'Feature this work',
                    description: 'Description',
                    describeWorkPlaceholder: 'Describe this work',
                },
                invite: {
                    title: 'Invite to Project',
                    description: 'Choose one of your projects and an open role to send an invite.',
                    project: 'Project',
                    selectProject: 'Select project',
                    role: 'Role',
                    loadingRoles: 'Loading roles...',
                    selectRole: 'Select role',
                    noOpenRoles: 'This project has no open roles right now. Add roles or open more slots to send an invite.',
                    inviteeEmail: 'Invitee Email',
                    inviteeEmailPlaceholder: 'user@example.com',
                    sending: 'Sending...',
                    sendInvite: 'Send Invite',
                },
                contact: {
                    title: 'Contact',
                    description: 'Use the profile email below to reach out directly.',
                    emailPlaceholder: 'user@example.com',
                },
                preview: {
                    title: 'Portfolio Preview',
                    externalMediaSource: 'This item points to an external media source.',
                    noMedia: 'No previewable media is attached to this portfolio item.',
                },
                deleteSkill: {
                    title: 'Delete Skill',
                    description: 'Remove "{{name}}" from your profile? This action cannot be undone.',
                    thisSkill: 'this skill',
                },
                deletePortfolio: {
                    title: 'Delete Portfolio Item',
                    description: 'Delete "{{name}}"? This action cannot be undone.',
                    thisPortfolioItem: 'this portfolio item',
                },
            },
            toast: {
                loadPayoutAccountsFailed: 'Unable to load payout accounts',
                loadProjectRolesFailed: 'Unable to load project roles',
                profilePublic: 'Profile is now public.',
                updateVisibilityFailed: 'Unable to update profile visibility',
                profileCreated: 'Professional profile created. You can now complete your details.',
                initializeProfileFailed: 'Unable to initialize professional profile',
                profileVisibilityUpdated: 'Profile is now {{visibility}}.',
                profileUpdated: 'Profile updated.',
                updateProfileFailed: 'Unable to update profile',
                skillSaved: 'Skill saved.',
                saveSkillFailed: 'Unable to save skill',
                skillRemoved: 'Skill removed.',
                removeSkillFailed: 'Unable to remove skill',
                portfolioSaved: 'Portfolio item saved.',
                savePortfolioFailed: 'Unable to save portfolio item',
                payoutRemoved: 'Payout account removed.',
                removePayoutFailed: 'Unable to remove payout account',
                portfolioRemoved: 'Portfolio item removed.',
                removePortfolioFailed: 'Unable to remove portfolio item',
                userContactUnavailable: 'User contact is unavailable.',
                noContactEmail: 'No contact email found for this profile.',
                contactWorkflowFailed: 'Unable to open contact workflow',
                createProjectToInvite: 'Create a project to send invites from profiles.',
                userUnavailableForInvite: 'Profile user is unavailable for invites.',
                noInviteEmail: 'No invite email is available for this profile.',
                createProjectBeforeInvite: 'Create a project first before sending an invite.',
                prepareInviteFailed: 'Unable to prepare invite workflow',
                selectProjectRole: 'Select project and role before sending invite.',
                inviteSent: 'Invite sent successfully.',
                sendInviteFailed: 'Unable to send invite',
                profileFollowed: 'Profile followed.',
                profileUnfollowed: 'Profile unfollowed.',
                toggleFollowFailed: 'Unable to {{action}} profile right now.',
                emailCopied: 'Email copied.',
                copyEmailFailed: 'Unable to copy email.',
                shareProfileFailed: 'Unable to share this profile right now.',
                profileLinkCopied: 'Profile link copied.',
                shareProfileLinkFailed: 'Unable to share profile link.',
                avatarUpdated: 'Avatar updated.',
                coverUpdated: 'Cover image updated.',
                updateImageFailed: 'Unable to update image.',
                uploadImageFailed: 'Unable to upload image.',
            },
        },
    },
    error: {
        pageNotFound: 'The page you are looking for does not exist.',
        goHome: 'Go home',
        goBack: 'Go back',
        emptyDefault: 'There is nothing here, kindly return to the previous page.',
        comingSoon: 'Hold on, we are currently working on this feature',
        somethingWentWrong: 'Something went wrong',
        tryAgain: 'Try Again',
        loadPageFailed: 'Something went wrong while loading this page.',
        pageUpdateDetected: 'Page update detected',
        pageRefreshHint: 'This screen could not finish loading. A refresh usually fixes it when the app code has changed.',
        unexpectedRenderError: 'An unexpected error stopped this screen from rendering.',
        refreshApp: 'Refresh app',
    },
    public: {
        layout: {
            gotoDashboard: 'Goto Dashboard',
            createSetlistsPrompt: 'Want to create your own setlists?',
            copyright: '© {{year}} VocalCentric. All rights reserved.',
            about: 'About',
            privacy: 'Privacy',
            terms: 'Terms',
            contact: 'Contact',
        },
        cookieNotice: {
            label: 'Cookie notice',
            message: 'We use cookies to improve VocalCentric.',
            dismiss: 'Dismiss cookie notice',
        },
        resourceHandoff: {
            fallbackTitle: 'Shared resource',
            signInTitle: 'Sign in to open this resource',
            signInDescription: 'Contribution links are available to signed-in VocalCentric members.',
            signIn: 'Sign in to continue',
            unavailableTitle: 'Resource unavailable',
            unavailableDescription: 'This resource may be unpublished, deleted, or no longer shared with you.',
            eyebrow: 'Shared contribution',
            openApp: 'Open in VocalCentric',
            openAppHint: 'The VocalCentric app is required to preview or use this resource.',
        },
        communityThreadHandoff: {
            fallbackTitle: 'Community thread',
            signInTitle: 'Sign in to open this thread',
            signInDescription: 'Community thread links are available to signed-in VocalCentric members.',
            signIn: 'Sign in to continue',
            unavailableTitle: 'Thread unavailable',
            unavailableDescription: 'This thread may have been deleted, or you may need to join its room first.',
            eyebrow: 'Shared community thread',
            openApp: 'Open in VocalCentric',
            openAppHint: 'The VocalCentric app is required to view and join this discussion.',
        },
        contact: {
            title: 'Contact Us',
            subtitle: 'Need help, have feedback, or want to partner with VocalCentric? Send us a message.',
            intercomTitle: 'Chat with support',
            intercomSubtitle: 'Open Messenger to start a support conversation with the VocalCentric team.',
            openMessenger: 'Open support chat',
            intercomFallback: 'Support chat is unavailable right now. Please email support@vocalcentric.com.',
            submitted: 'Message received. We’ll respond soon.',
            namePlaceholder: 'Your name',
            emailPlaceholder: 'you@example.com',
            subjectPlaceholder: 'Select a subject…',
            messagePlaceholder: 'Write your message…',
            sending: '...Sending',
            send: 'Send message',
            preferEmail: 'Prefer email? Reach us at',
            responseTime: 'Response time: typically within 24–72 hours (weekdays).',
            subject: {
                support: 'Support / Help',
                billing: 'Billing / Subscription',
                bug: 'Bug Report',
                feedback: 'Feature Request / Feedback',
                partnership: 'Partnership / Licensing',
                accountDeletion: 'Account Deletion',
                other: 'Other',
            },
            error: {
                nameRequired: 'Name is required.',
                emailRequired: 'Email is required.',
                invalidEmail: 'Please enter a valid email address.',
                subjectRequired: 'Please select a subject.',
                messageRequired: 'Message is required.',
                messageTooShort: 'Message is too short (min 10 characters).',
                unexpected: 'An unexpected error occurred.',
            },
            toast: {
                sent: 'Message sent! We’ll get back to you shortly.',
            },
        },
        waitlist: {
            title: 'Join the waitlist',
            subtitle: 'Gain exclusive early access and stay informed about our launch.',
            submitted: 'You are officially on the waitlist!',
            firstName: 'First Name',
            lastName: 'Last Name',
            phonePlaceholder: 'Enter phone number (+212 34567890)',
            emailPlaceholder: 'you@example.com',
            loading: '...Loading',
            getNotified: 'Get Notified',
            gotEarlyAccess: 'Got Early Access?',
            logIn: 'Log in',
            joinedCount: 'Over ~1.4k+ people already joined',
            days: 'Days',
            hours: 'Hours',
            minutes: 'Minutes',
            seconds: 'Seconds',
            toast: {
                joined: 'Thank you for joining the waitlist!',
            },
            error: {
                emailRequired: 'Email is required.',
                invalidEmail: 'Please enter a valid email address.',
                invalidPhone: 'Please enter a valid phone number.',
                unexpected: 'An unexpected error occurred.',
            },
        },
        invitation: {
            pageTitle: 'Accept Choir Invitation',
            joinChoir: 'Join the {{name}} Choir',
            confirmJoin: 'Confirm and join the {{name}} on VocalCentric',
            completeRegistration: 'Complete your registration to accept the invite',
            firstName: 'First Name',
            firstNamePlaceholder: 'Enter your first name',
            lastName: 'Last Name',
            lastNamePlaceholder: 'Enter your last name',
            username: 'Username',
            usernamePlaceholder: 'Enter your preferred username',
            password: 'Password',
            passwordPlaceholder: 'Create a password',
            accept: 'Accept Invitation',
            decline: 'Decline',
            accepted: 'Invitation accepted successfully!',
            declined: 'Invitation declined successfully!',
            error: {
                firstNameRequired: 'First name is required',
                lastNameRequired: 'Last name is required',
                usernameRequired: 'Username is required',
                passwordRequired: 'Password is required',
                invalidEmail: 'Please enter a valid email address',
                usernameTaken: 'Username is already taken',
                failedUsernameCheck: 'Failed to check username',
                failedDecline: 'Failed to decline invitation',
            },
        },
        schedule: {
            loadFailed: 'Failed to load public schedule',
            missingToken: 'Missing schedule token',
            unavailable: 'Public Schedule Unavailable',
            couldNotLoad: 'This schedule could not be loaded.',
            publicChoirSchedule: 'Public Choir Schedule',
            serviceCount: '{{count}} service(s)',
        },
        harmony: {
            mixerView: 'Mixer View',
            mixerViewInfo: '(Recommended for PC) In Mixer View, you can adjust the volume and pan of individual tracks for a customized listening experience.',
            loadingWaveform: 'Loading waveform...',
            mute: 'Mute',
            unmute: 'Unmute',
            solo: 'Solo',
            unsolo: 'Unsolo',
            titleFallback: 'Harmonies',
            setlistFallback: 'Setlist',
            ogDescription: 'A curated setlist on VocalCentric featuring custom harmony for different parts and instruments.',
            playPause: 'Play or Pause',
            restart: 'Restart',
        },
        privacy: {
            gotoDashboard: 'Goto Dashboard',
            policyTitle: '{{companyName}} Privacy Policy',
            lastUpdated: 'Last Updated: {{lastUpdated}}',
            intro: 'At {{companyName}}, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and protect the data you share with us when you use our platform, mobile app, or related services.',
            section1Title: '1. Information We Collect',
            section1Intro: 'We may collect the following types of information:',
            personalInfoLabel: 'Personal Information',
            personalInfoBody: 'Name, email address, phone number, profile photo, and other details you provide when creating an account.',
            usageDataLabel: 'Usage Data',
            usageDataBody: 'Information about how you use our platform, including song plays, rehearsal participation, and interactions with other users.',
            mediaFilesLabel: 'Media Files',
            mediaFilesBody: 'Audio recordings, videos, and other content you upload or share.',
            deviceInfoLabel: 'Device Information',
            deviceInfoBody: 'IP address, browser type, device model, operating system, and app version.',
            cookiesLabel: 'Cookies and Tracking Data',
            cookiesBody: 'To improve your experience and remember preferences.',
            section2Title: '2. How We Use Your Information',
            section2Item1: 'Provide and improve our services.',
            section2Item2: 'Facilitate choir rehearsals, song playback, and community interactions.',
            section2Item3: 'Customize content and recommendations.',
            section2Item4: 'Communicate with you regarding updates, support, or promotional offers (with your consent).',
            section2Item5: 'Ensure safety, security, and compliance with our Terms of Service.',
            section3Title: '3. How We Share Your Information',
            section3Intro: 'We do not sell your personal information. We may share your data only in the following cases:',
            section3ConsentLabel: 'With Your Consent',
            section3ConsentBody: 'When you choose to share recordings or profile details with other users.',
            section3ProvidersLabel: 'Service Providers',
            section3ProvidersBody: 'With third-party vendors that help us operate the platform (e.g., cloud hosting, payment processors).',
            section3LegalLabel: 'Legal Obligations',
            section3LegalBody: 'When required by law or in response to valid legal requests.',
            section4Title: '4. Data Security',
            section4Body: 'We implement technical, administrative, and physical safeguards to protect your data from unauthorized access, use, or disclosure. However, no method of transmission over the internet or electronic storage is 100% secure.',
            section5Title: '5. Your Rights',
            section5Intro: 'Depending on your location, you may have the right to:',
            section5Item1: 'Access, update, or delete your personal information.',
            section5Item2: 'Restrict or object to how we process your data.',
            section5Item3: 'Request a copy of the data we hold about you.',
            section5ContactIntro: 'To exercise these rights, contact us at',
            section6Title: '6. Retention of Data',
            section6Body: 'We keep your personal data only as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.',
            section7Title: '7. Children’s Privacy',
            section7Body: '{{companyName}} is not intended for children under 13 (or the minimum legal age in your country). We do not knowingly collect personal information from children without parental consent.',
            section8Title: '8. Changes to This Policy',
            section8Body: 'We may update this Privacy Policy from time to time. If we make significant changes, we’ll notify you via email or in-app notice.',
            section9Title: '9. Contact Us',
            emailLabel: 'Email:',
            websiteLabel: 'Website:',
            rightsReserved: '© {{year}} VocalCentric. All rights reserved.',
            navAbout: 'About',
            navPrivacy: 'Privacy',
            navTerms: 'Terms',
            navContact: 'Contact',
        },
        accountDeletion: {
            title: 'Google Account Deletion',
            subtitle: 'This page explains how to delete your VocalCentric account (including Google Sign‑In accounts), what gets deleted, and how to contact us.',
            signedInAs: 'You are signed in as',
            instantDeleteHint: 'You can delete your account instantly from here.',
            deleting: 'Deleting…',
            deleteNow: 'Delete my account now',
            submitted: 'Your request was submitted. We\'ll email you when the deletion is completed.',
            howToRequest: 'How to request deletion',
            howToRequestItems: {
                inAppOrForm: 'Use the in-app option (Account → Settings → Delete Account), or submit the form below.',
                verifyOwnership: 'We\'ll verify ownership via your email address or in-app session.',
                deleteAfterVerification: 'Once verified, we will permanently delete your account and personal data, except where we must retain limited records for fraud prevention, security, or legal obligations.',
            },
            whatWillBeDeleted: 'What will be deleted',
            deletedItems: {
                profile: 'Account profile (name, email, username, avatar).',
                submissions: 'Practice submissions, rehearsal participation metadata, community posts tied to your identity (content may be anonymized if removal would break conversations).',
                history: 'Playback history, preferences, and device associations.',
            },
            retentionLabel: 'Retention:',
            retentionBody: 'We may retain minimal logs or financial records as required by law, and keep aggregated/anonymous analytics that cannot identify you.',
            googleUsers: 'Google Sign‑In users',
            googleUsersBody: 'If you registered via Google, deleting your VocalCentric account does not delete your Google account. We only revoke our app\'s access and delete your data on VocalCentric. You can additionally review or remove VocalCentric\'s access from your Google Account → Security → Third‑party access.',
            emailPlaceholder: 'you@example.com',
            reasonPlaceholder: 'Optional: Tell us why you\'re leaving (helps us improve)',
            confirmLabel: 'I confirm I own this email address and want to permanently delete my VocalCentric account and related data.',
            submitting: 'Submitting…',
            submitRequest: 'Submit deletion request',
            needHelp: 'Need help? Email',
            toast: {
                requestReceived: 'Your account deletion request has been received.',
                deleted: 'Your account was deleted successfully.',
            },
            error: {
                emailRequired: 'Email is required.',
                invalidEmail: 'Please enter a valid email address.',
                confirmRequired: 'You must confirm ownership and intent to delete.',
                unexpected: 'An unexpected error occurred.',
                unableDeleteNow: 'Unable to delete account right now.',
            },
        },
    },
    uploads: {
        communityUpload: 'Community Upload',
        title: 'Upload a marketplace song',
        subtitle: 'This flow mirrors the internal song setup process, but submits into the community marketplace pipeline for whitelisted creators.',
        duplicateTitle: 'You already submitted a community song with this title',
        steps: {
            information: 'Information',
            vocals: 'Vocals',
            lyrics: 'Lyrics',
            sync: 'Sync',
            files: 'Files',
        },
        actions: {
            previous: 'Previous',
            next: 'Next',
            preview: 'Preview',
        },
        toast: {
            submitted: 'Community song submitted successfully',
            submitFailed: 'Song submission failed',
        },
        submission: {
            saved: 'Submission saved',
            sentForProcessing: '{{title}} has been sent for marketplace processing.',
            processingNote: 'Your song has been created successfully. Audio packaging and marketplace readiness continue in the background, so previews or listing controls may take a little time to become available.',
            goToListings: 'Go to My Listings',
            uploadAnother: 'Upload another song',
            id: 'Submission ID: {{id}}',
        },
        stepOne: {
            songOwnershipType: 'Song Ownership Type',
            original: 'Original',
            cover: 'Cover',
            songTitle: 'Song Title',
            artistName: 'Artist Name',
            writers: 'Writers',
            album: 'Album',
            genre: 'Genre',
            label: 'Label',
            themeLabel: 'Theme',
            chooseTheme: 'Choose a theme',
            theme: {
                praise: 'Praise',
                worship: 'Worship',
                thanksgiving: 'Thanksgiving',
                prayer: 'Prayer',
                adoration: 'Adoration',
                faith: 'Faith',
                healing: 'Healing',
                salvation: 'Salvation',
                grace: 'Grace',
                mercy: 'Mercy',
            },
            bpm: 'BPM',
            timeSignature: 'Time Signature',
            chooseTimeSignature: 'Choose a time signature',
            durationSeconds: 'Duration (seconds)',
            duration: 'Duration',
            key: 'Key',
            chooseKey: 'Choose a key',
            year: 'Year',
            tags: 'Tags',
            tagPlaceholder: 'Type a tag and press space or comma',
        },
        stepTwo: {
            whichStem: 'What stem are you uploading?',
            uploadStem: 'Upload {{stem}}',
            uploadHint: 'Click to upload your pre-recorded stem from your computer or phone',
        },
        stepThree: {
            tabs: {
                lyrics: 'Lyrics',
                mainSolfa: 'Main Solfa',
                tenorSolfa: 'Tenor Solfa',
                altoSolfa: 'Alto Solfa',
                sopranoSolfa: 'Soprano Solfa',
            },
            optionalNote: 'Lyrics are optional for community songs. Add sections if you want section-based preview selection, or leave this blank and choose a custom preview interval in the next step.',
            startTyping: 'Start typing {{target}}...',
            uploadMainStemHint: 'Upload the main stem first to preview the waveform while structuring lyrics.',
        },
        stepFour: {
            previewWindow: 'Preview Window',
            previewDescription: 'Choose what buyers hear first. If your song has sections, you can use one directly. Otherwise set a custom interval.',
            previewSource: 'Preview source',
            customInterval: 'Custom interval',
            previewStart: 'Preview start',
            previewEnd: 'Preview end',
            defaultStart: '00:00',
            defaultEnd: '00:20',
            useCurrent: 'Use current',
            noLyricsNote: 'Lyrics are optional for community songs. If you skip them, just set the preview interval above.',
            adjusting: 'Adjusting',
            selectLineToSync: 'Select a lyric line to start syncing',
            uploadMainStemToSync: 'Upload the main stem to align timestamps against the waveform.',
        },
        stepFive: {
            imageUploadFailed: 'Image upload failed',
            title: 'Upload artwork and supporting files',
            subtitle: 'These visuals will be used in the marketplace card and song detail view.',
            artistImage: 'Artist image',
            uploadArtistImage: 'Click to upload artist picture',
            coverArt: 'Cover art',
            uploadCoverArt: 'Click to upload cover art',
        },
        document: {
            title: 'Upload score or supporting document',
            description: 'PDF and DOCX supported (max. 20MB)',
            timeout: 'Document upload timed out before processing finished',
            failed: 'Document upload failed',
        },
        preview: {
            customInterval: 'Custom interval',
            song: 'Song',
            album: 'Album',
            genre: 'Genre',
            theme: 'Theme',
            duration: 'Duration',
            stemCount: 'Stem count',
            previewWindow: 'Preview window',
            lyrics: 'Lyrics',
            sectionsCount: '{{count}} section(s)',
            optionalNone: 'Optional, none added',
            previewSource: 'Preview source',
            uploadedStems: 'Uploaded Stems',
            noStems: 'No stems uploaded',
            submitting: 'Submitting...',
            submit: 'Submit',
        },
    },
    exercises: {
        actions: {
            backToExercises: 'Back to exercises',
            backToSkill: 'Back to skill',
            continue: 'Continue',
            continueWithoutMic: 'Continue without mic',
            exit: 'Exit',
            gotIt: 'Got it',
            keepPracticing: 'Keep practicing',
            listenBack: 'Listen back',
            locked: 'Locked',
            pausePlayback: 'Pause playback',
            play: 'Play',
            playAgain: 'Play again',
            playExample: 'Play example',
            practice: 'Practice {{title}}',
            replay: 'Replay',
            retry: 'Retry',
            saving: 'Saving...',
            skipStep: 'Skip step',
            start: 'Start',
            startBreathCheck: 'Start breath check',
            startPlan: 'Start plan',
            startPulse: 'Start pulse',
            startRecording: 'Start recording',
            startTimer: 'Start timer',
            stopExample: 'Stop example',
            tryAgain: 'Try again',
            viewPath: 'View section',
        },
        status: {
            comingSoon: 'Coming soon',
            complete: 'Complete',
            counting: 'Counting...',
            endlessPractice: 'Endless practice',
            loadingPianoSamples: 'Loading piano samples...',
            listening: 'Listening...',
            noDrillsYet: 'No drills yet',
            recording: 'Recording...',
            recordingSaved: 'Recording saved',
            running: 'Running...',
        },
        units: {
            bpmPulse: '{{bpm}} BPM · count the pulse',
            drillCount: '{{count}} drill',
            drillCount_other: '{{count}} drills',
            drills: 'Drills',
            min: '{{count}} min',
            secondEcho: '{{count}} second echo',
            skillsDrills: '{{skills}} skills - {{drills}} drills',
            stars: 'Stars',
            stepCount: '{{count}} steps',
            steps: 'Steps',
            stepOfTotal: 'Step {{step}} of {{total}}',
            unitsDrills: '{{units}} units - {{drills}} drills',
        },
        skillTree: {
            benefits: {
                default: 'A short VocalCentric practice game.',
                warmupFoundation: 'Ease into singing with a short vocal reset before heavier work.',
                breathControl: 'Build steadier airflow so phrases feel calmer and more controlled.',
                pitchAccuracy: 'Sharpen pitch matching and make your ear react faster.',
                noteRecognition: 'Build fast note-name recall with quick, low-pressure rounds.',
                keyboardNotes: 'Find notes directly on a piano-style keyboard.',
                intervalBasics: 'Learn the distance between notes through naming and matching drills.',
                theoryTerms: 'Turn common music vocabulary into quick, confident recall.',
                earTrainingBasics: 'Listen, compare, and identify short note and interval references.',
            },
            directGames: {
                scaleChoices: {title: 'Scale choices', tag: 'Scales', purpose: 'Hear a scale pattern, then pick the answer.'},
                modeChoices: {title: 'Mode choices', tag: 'Modes', purpose: 'Hear a mode pattern, then pick the answer.'},
                listenAndChoose: {title: 'Listen and choose', tag: 'Ear', purpose: 'Hear a note, then pick the answer.'},
                hearAndPlay: {title: 'Hear and play', tag: 'Keys', purpose: 'Hear a note, then find it on the keyboard.'},
                hearChords: {title: 'Hear chords', tag: 'Ear', purpose: 'Hear a chord, then name its quality.'},
                findNotesOnKeys: {title: 'Find notes on keys', tag: 'Keys', purpose: 'Tap target notes directly on the keyboard.'},
                hearIntervals: {title: 'Hear intervals', tag: 'Ear', purpose: 'Hear two notes, then name the interval.'},
                noteNameSprint: {title: 'Note name sprint', tag: 'Notes', purpose: 'Answer fast note-name prompts.'},
                theoryQuickfire: {title: 'Theory quickfire', tag: 'Theory', purpose: 'Practice core music vocabulary.'},
            },
            empty: 'No exercise skills are available yet.',
            error: 'We could not load exercises right now.',
            fallbackModuleDescription: 'Short drills for pitch, rhythm, theory, and ear training.',
            fallbackModuleTitle: 'Core Skills',
            games: 'Games',
            gamesDescription: 'Jump straight into endless practice without changing your course progress.',
            headings: {
                browseLibrary: 'Course sections',
                choosePath: 'Music theory course',
                continuePractice: 'Continue practice',
                recommendedPractice: 'Recommended practice',
            },
            libraryDescription: 'Open a section to browse its four unit diamonds.',
            loading: 'Loading your music theory course...',
            moduleAudience: {
                course: 'Complete these four units as one course section.',
                default: 'Choose this when you want a focused practice path.',
                musicianship: 'Choose this when you want fast, replayable music games.',
                voice: 'Choose this before singing or rehearsal.',
            },
            moduleBenefit: {
                course: 'Build theory knowledge through short lessons, checks, and playable drills.',
                default: 'Choose a focused set of short VocalCentric drills.',
                musicianship: 'Train notes, intervals, rhythm, and ear instincts through quick games.',
                voice: 'Warm up, control breath, and sharpen pitch before rehearsal.',
            },
            moreStartedSkills: 'More started skills worth finishing next.',
            progress: 'Progress',
            progressCleared: '{{cleared}}/{{total}} cleared',
            prompts: {
                dailyPlan: "Today's plan: {{label}}.",
                dailyPlanFallback: "Today's plan is tuned from your recent exercise progress.",
                finishNext: 'Finish the next drill while it is fresh.',
                replaySkill: 'Replay this skill to improve speed, accuracy, and confidence.',
                startFocused: 'Start with a focused drill that builds useful musical instinct.',
            },
            quickGame: {
                buildKeyboard: 'Build keyboard recall.',
                ear: 'Ear',
                keys: 'Keys',
                notes: 'Notes',
                practice: 'Practice',
                replayImprove: 'Replay to improve.',
                runFocused: 'Run a short focused drill.',
                speed: 'Speed',
                theory: 'Theory',
                trainListening: 'Train listening instincts.',
                practiceMusicIdeas: 'Practice core music ideas.',
            },
            viewAll: 'View all {{title}}',
            showFewer: 'Show fewer {{title}}',
        },
        skillDetail: {
            exerciseNumber: 'Exercise {{number}}',
            fallbackDescription: 'A quick practice set for sharper musical instincts.',
            loadError: 'We could not load this skill.',
            loading: 'Loading skill...',
            missing: 'Skill not found.',
            shortFocused: 'Short and focused.',
            skill: 'Skill',
        },
        runner: {
            empty: 'No playable steps are available for this skill yet.',
            fallbackPrompt: 'Listen, choose, and trust your ear.',
            messages: {
                patternSeen: 'Pattern seen.',
                readyNext: 'Ready for the next move.',
                referenceHeard: 'Reference heard. Keep going.',
                resetComplete: 'Reset complete.',
                skippedForWeb: 'Skipped for web.',
            },
            result: {
                complete: 'Set complete',
                earnedStars: 'You earned {{count}} stars from this run.',
                fallback: 'Keep going and your next run will feel smoother.',
                niceRun: 'Nice run',
                savedLocally: 'Your result is saved locally for now. Try this set again when your connection is steady.',
                youFinished: 'You finished {{title}}.',
            },
        },
        generated: {
            chooseBest: 'Choose the best answer.',
            chooseHeard: 'Choose what you heard',
            namePattern: 'Name the {{label}}',
            recallTerm: 'Recall the term',
            scale: 'scale',
            mode: 'mode',
            tapNote: 'Tap {{note}}.',
            whatTermMean: 'What does {{term}} mean?',
            whichNote: 'Which note did you hear?',
            whichPattern: 'Which {{label}} did you hear?',
        },
        steps: {
            animation: {
                watchPulse: 'Watch the pulse for {{count}} beats.',
            },
            audioReference: {
                chooseHeard: 'Tap play, listen closely, then choose what you heard.',
                fallbackCopy: 'Tap play and listen for the color before moving on.',
                heading: 'Hear the reference',
                noSource: 'This reference does not include a playable web audio source yet.',
                pianoUnavailable: 'Piano playback is not available in this browser.',
                samplesLoading: 'Piano samples are still loading.',
                playFailed: 'We could not play this reference.',
            },
            meter: {
                breathSkipped: 'Breath guide skipped.',
                countPulse: 'Pulse locked.',
                exhale: 'Exhale',
                followCue: 'Follow the cue, then keep the exhale smooth.',
                inhaleSeconds: 'Inhale {{count}}s',
                holdSeconds: 'Hold {{count}}s',
                keepAirflow: 'Keep the airflow steady.',
                listeningLevel: 'Listening to your breath level.',
                micFeedback: 'Microphone feedback measures exhale steadiness.',
                micNeeded: 'Microphone permission is needed for breath feedback.',
                micUnavailable: 'Microphone input is not available in this browser.',
                phase: 'Phase',
                phaseReady: 'Ready',
                phaseInhale: 'Inhale',
                phaseHold: 'Hold',
                phaseExhale: 'Exhale',
                smoothExhale: 'The breath wobbled. Try a smoother exhale.',
                steadyAirflow: 'That airflow stayed steady.',
                liveFeedbackUnavailable: 'Live breath feedback is not available in this browser.',
            },
            multipleChoice: {
                chooseBest: 'Choose the best answer.',
                close: 'Close. Give it one more pass.',
                correct: 'That lands.',
            },
            pairs: {
                answer: 'Match the answer',
                complete: 'Pairs complete. A little cleaner next run.',
                perfect: 'Perfect pairs.',
                prompt: 'Tap a prompt',
            },
            piano: {
                missed: 'That note missed the target.',
                playHeard: 'Play what you heard on the keyboard.',
                right: 'That note is right.',
                tapTarget: 'Tap {{note}}.',
                targetNote: 'the target note',
                unavailable: 'Piano playback is not available in this browser.',
                useReference: 'Use the labeled {{note}} key as your octave reference.',
            },
            record: {
                challenge: {
                    echoMelody: 'Echo the melody',
                    holdSteady: 'Hold {{note}} steady',
                    singBack: 'Sing it back',
                },
                coaching: {
                    melody: 'Stay light and let the notes connect smoothly.',
                    steady: 'Sing lightly and keep the tone steady.',
                },
                guide: {
                    close: 'You are close to the melody.',
                    focus: 'Focus on the shape, not volume.',
                    listening: 'Listening to your voice...',
                    moveToward: 'Stay gentle and move toward the melody.',
                },
                labels: {
                    flow: 'Listen → Sing → Review',
                    melodyNotes: 'Melody notes: {{notes}}',
                },
                messages: {
                    capturedNoTarget: 'Recording captured. This step needs a clear target note before web can score it.',
                    detectFailed: 'I could not detect a steady target pitch. Try one more take.',
                    drifted: 'I heard the pitch, but it drifted from the melody.',
                    matched: 'That matched the melody shape.',
                },
                errors: {
                    micNeeded: 'Microphone permission is needed for this step.',
                    playbackUnavailable: 'Reference playback is not available in this browser.',
                    recordingUnavailable: 'Recording is not available in this browser.',
                },
            },
            text: {
                fallback: 'Take this cue, then keep going.',
            },
            timer: {
                reset: 'Breathe, reset, and come back clean.',
            },
            unsupported: {
                body: 'You can keep the run moving.',
                title: 'This exercise step is not available on web yet.',
            },
        },
        controls: {
            accidentals: 'Accidentals',
            accidentalsDescription: 'Add sharps to note choices.',
            alphabet: 'Alphabet',
            challenge: 'Challenge',
            difficulty: 'Difficulty',
            difficulty_auto: 'Auto',
            difficulty_easy: 'Easy',
            difficulty_expert: 'Expert',
            difficulty_hard: 'Hard',
            difficulty_normal: 'Normal',
            gameSettings: 'Game settings',
            mixed: 'Mixed',
            notation: 'Notation',
            pad: 'Pad {{note}}',
            solfege: 'Solfa',
            tonic: 'Tonic {{note}}',
        },
    },
    training: {
        eyebrow: 'School and choir training',
        title: 'Schools',
        subtitle: 'Organize Courses, Classes, assignments, student progress, and live training with or without a choir.',
        choirEyebrow: 'Choir development',
        choirTitle: 'Training',
        choirDescription: 'Give selected choir members structured exercises, assignments, feedback, and live coaching.',
        choirTab: 'Training',
        choirTabDescription: 'Courses and member development',
        workspaces: 'Schools',
        myCohorts: 'My classes',
        courses: 'Courses',
        courseDescription: 'Reusable curriculum that can be mapped to several Classes.',
        curriculum: 'Curriculum',
        curriculumDescription: 'Build an ordered program from configured exercises and learning materials.',
        cohorts: 'Classes',
        cohortsDescription: 'Create dated learning groups and map each Class to one or more Courses.',
        mappedCourses: 'Mapped Courses',
        courseClassMappingDescription: 'Classes belong to the School and can use multiple Courses.',
        classCurriculumDescription: 'Curriculum from every Course mapped to this Class.',
        assignments: 'Assignments',
        sessions: 'Sessions',
        progress: 'Progress',
        staff: 'Staff',
        students: 'Students',
        submissions: 'Submissions',
        submissionsDescription: 'Review every learner attempt and leave actionable feedback.',
        exerciseAssignment: 'Exercise assignment',
        exerciseAssignmentDescription: 'Complete the configured exercise. You can submit up to {{count}} attempts.',
        learningMaterials: 'Learning materials',
        myAttempts: 'My attempts',
        attemptNumber: 'Attempt {{count}}',
        previewTitle: 'Resolved exercise preview',
        pendingReviews: 'Pending reviews',
        upcomingSessions: 'Upcoming sessions',
        pendingInvitations: 'Pending invitations',
        workspaceSettings: 'School settings',
        courseSettings: 'Course settings',
        cohortSettings: 'Class settings',
        cohortStaff: 'Class staff',
        assignmentSettings: 'Assignment settings',
        actions: {
            accept: 'Accept', addCourse: 'Add Course', addCurriculum: 'Add curriculum item', addStudents: 'Add students', applyInstructor: 'Apply to create a School', approve: 'Approve', archiveCohort: 'Archive Class', archiveCourse: 'Archive Course', archiveWorkspace: 'Archive School', assignStaff: 'Assign staff', back: 'Back', backToCohort: 'Back to Class', backToTeaching: 'Back to Schools', cancel: 'Cancel session', checkIn: 'Check in', createCohort: 'Create Class', createCourse: 'Create Course', decline: 'Decline', edit: 'Edit', editCurriculum: 'Edit curriculum item', endSession: 'End session', enroll: 'Enroll existing users', inviteNewStudent: 'Invite new student', inviteStaff: 'Invite staff', join: 'Join', lowerHand: 'Lower hand', manageClasses: 'Manage Classes', manageTraining: 'Manage training', moveDown: 'Move down', moveUp: 'Move up', needsImprovement: 'Needs improvement', newCohort: 'New Class', newCourse: 'New Course', newWorkspace: 'New School', open: 'Open', openChoir: 'Open choir', openCourse: 'Open Course', openSchool: 'Open School', preview: 'Preview exercise', publishAssignment: 'Publish assignment', raiseHand: 'Raise hand', remove: 'Remove', resend: 'Resend', revoke: 'Revoke', scheduleSession: 'Schedule session', send: 'Send message', sendInvite: 'Send invitation', share: 'Share', start: 'Start', startAssignment: 'Start assignment', submit: 'Submit', submitApplication: 'Submit application',
        },
        fields: {
            assignmentSource: 'Assignment source', assignmentTitle: 'Assignment title', attemptsAllowed: 'Attempts allowed', audioResponse: 'Audio response (optional)', choirMembers: 'Choose choir members', choirPart: 'Choir part', cohortTitle: 'Class title', course: 'Course', courseTitle: 'Course title', description: 'Description', dueAt: 'Due', email: 'Email', ends: 'Ends', enrollmentMode: 'Enrollment', exerciseTemplate: 'Exercise template', feedback: 'Feedback', instructions: 'Instructions', itemType: 'Item type', level: 'Level', locationMode: 'Location', materialUrl: 'Material URL', passStars: 'Pass stars', responseNote: 'Your response', role: 'Role', sessionTitle: 'Session title', starts: 'Starts', status: 'Status', studentEmails: 'Student emails', title: 'Title', verifiedScore: 'Verified score', verifiedStars: 'Verified stars', workspaceName: 'School name',
        },
        roles: {owner: 'Owner', instructor: 'Instructor', assistant: 'Assistant', learner: 'Learner'},
        statuses: {active: 'Active', archived: 'Archived', closed: 'Closed', completed: 'Completed', draft: 'Draft', enrolling: 'Enrolling', published: 'Published'},
        enrollmentModes: {invite: 'Invitation only', open: 'Open enrollment'},
        itemTypes: {exercise: 'Exercise', material: 'Learning material'},
        assignmentSources: {curriculum: 'Course curriculum', adhoc: 'One-off configured exercise'},
        locationModes: {virtual: 'Virtual', physical: 'Physical', hybrid: 'Hybrid'},
        units: {assignments: 'assignments', attempts: 'attempts', cohorts: 'Classes', curriculumItems: 'curriculum items', learners: 'learners', pending: 'pending', submissions: 'submissions'},
        placeholders: {choirPart: 'e.g. soprano', emails: 'Separate emails with commas or spaces', level: 'e.g. Beginner', optionalCurriculumTitle: 'Optional — uses curriculum title'},
        empty: {
            assignmentsTitle: 'No assignments yet', assignmentsDescription: 'Published assignments will appear here.', attemptsTitle: 'No attempts yet', attemptsDescription: 'Start the assignment when you are ready.', choirTrainingTitle: 'Training has not been set up', choirTrainingDescription: 'A director or moderator can create the choir School.', classCurriculumDescription: 'Map a Course with curriculum to this Class.', cohortsTitle: 'No Classes yet', cohortsDescription: 'Your active Classes will appear here.', courseCohortsDescription: 'Map this Course from a Class in the School.', coursesTitle: 'No Courses yet', coursesDescription: 'Create reusable Courses, then map them to one or more Classes.', curriculumTitle: 'No curriculum yet', curriculumDescription: 'Add the first configured exercise or learning material.', invitationsDescription: 'There are no pending invitations.', noCourses: 'No Courses mapped', noDescription: 'No description provided.', noInstructions: 'No additional instructions.', progressTitle: 'No progress to show', progressDescription: 'Enroll students and publish an assignment to begin tracking progress.', sessionsTitle: 'No sessions scheduled', sessionsDescription: 'Upcoming and live training sessions will appear here.', submissionsTitle: 'No submissions yet', submissionsDescription: 'Learner attempts will appear here for review.', workspacesTitle: 'No School yet', workspacesDescription: 'Create a School for your students, academy, or studio.',
        },
        errors: {
            archiveCohort: 'Could not archive the Class.', archiveCourse: 'Could not archive the Course.', archiveWorkspace: 'Could not archive the School.', assignStaff: 'Could not assign the staff member.', attendance: 'Could not record attendance.', createAssignment: 'Could not publish the assignment.', createCohort: 'Could not create the Class.', createCourse: 'Could not create the Course.', createSession: 'Could not schedule the session.', createWorkspace: 'Could not create the School.', endSession: 'Could not end the session.', enroll: 'Could not enroll the students.', instructorApplication: 'Could not submit the instructor application.', invalidInvitation: 'This invitation link is invalid.', invitation: 'Could not respond to this invitation.', invite: 'Could not send the invitation.', inviteFirst: 'Invite these students before enrolling them:', load: 'Could not load Schools.', loadAssignment: 'Could not load the assignment.', loadChoirTraining: 'Could not load choir training.', loadCohort: 'Could not load the Class.', loadCourse: 'Could not load the Course.', loadSession: 'Could not load the session.', loadWorkspace: 'Could not load the School.', mapCourse: 'Could not update the Courses mapped to this Class.', preview: 'Could not generate the preview.', removeCurriculum: 'Could not remove the curriculum item.', removeStaff: 'Could not remove the staff member.', reorder: 'Could not reorder the curriculum.', resendInvitation: 'Could not resend the invitation.', review: 'Could not save the review.', revokeInvitation: 'Could not revoke the invitation.', saveCurriculum: 'Could not save the curriculum item.', saveWorkspace: 'Could not save the School.', startSession: 'Could not start the session.', submit: 'Could not submit the assignment.', updateAssignment: 'Could not update the assignment.', updateCohort: 'Could not update the Class.', updateCourse: 'Could not update the Course.', updateEnrollment: 'Could not update the enrollment.', updateSession: 'Could not update the session.', updateStaff: 'Could not update the staff member.',
        },
        confirm: {archiveAssignment: 'Archive this assignment? Existing submissions will be preserved.', archiveCohort: 'Archive this Class?', archiveCourse: 'Archive this Course?', archiveWorkspace: 'Archive this School?', cancelSession: 'Cancel this session?', endSession: 'End this live session?', removeCourseFromClass: 'Remove this Course from the Class?', removeCurriculum: 'Remove this curriculum item?', removeStaff: 'Remove this staff member from the Class?', removeStudent: 'Remove this student from the Class?'},
        attendanceStatuses: {present: 'Present', late: 'Late', absent: 'Absent', excused: 'Excused'},
        classroom: {
            attendance: 'Attendance', cancelledDescription: 'This session was cancelled.', cancelledTitle: 'Session cancelled', chat: 'Class chat', chatFailed: 'Could not send the message.', checkedIn: 'Checked in', clearContext: 'Clear shared context', completedDescription: 'This session has ended.', completedTitle: 'Session complete', context: 'Class context', contextFailed: 'Could not share the class context.', handRaiseFailed: 'Could not update your hand raise.', joinFailed: 'Could not join the classroom.', joinMedia: 'Join audio and video', joiningMedia: 'Joining…', leaveMedia: 'Leave audio and video', enableAudio: 'Enable audio', listenOnly: 'You are listening only. The instructor controls who can publish audio and video.', liveRoom: 'Live classroom', mediaJoinFailed: 'Could not join the live audio and video room.', mediaPresenceFailed: 'You joined the room, but attendance could not be updated.', mediaReady: 'Join when you are ready. Microphone access is requested when you speak; camera access is available to instructors.', mediaRenewFailed: 'The classroom media token could not be renewed.', message: 'Message', muteMicrophone: 'Mute microphone', noContext: 'No assignment or material is currently shared.', noMessages: 'No messages yet.', notCheckedIn: 'Not checked in', participants: 'Participants', physicalSession: 'This is an in-person session. Classroom chat, attendance, and shared assignment context remain available.', present: 'Present', selfCheckInDescription: 'Check yourself in for this Class session.', turnCameraOff: 'Turn camera off', turnCameraOn: 'Turn camera on', unmuteMicrophone: 'Unmute microphone', waitingInstructor: 'Start the session when you are ready. Enrolled learners will be notified.', waitingLearner: 'Your instructor has not started this session yet. This page will update automatically.', waitingTitle: 'Waiting for the session to start', you: 'You',
            joinStates: {idle: 'Waiting to join', joined: 'Connected to classroom controls', joining: 'Joining classroom controls'},
            mediaStates: {idle: 'Media not joined', joined: 'Audio and video connected', joining: 'Joining audio and video', reconnecting: 'Reconnecting audio and video'},
        },
        instructorApplication: {title: 'Certified instructor application', description: 'Independent Schools require instructor approval. Upload a short teaching or performance video and explain your experience.', pending: 'Your application is pending review. You can create a School after approval.', rejected: 'Your previous application was not approved. Update your evidence and submit again.', video: 'Teaching or performance video', reason: 'Why should you be approved?'},
        invitation: {eyebrow: 'School invitation', title: 'Join this School', description: 'Accept to join as a staff member or learner. Invitations are single-use and tied to your account.'},
        openEnrollment: {title: 'Join this Class', description: 'Enroll to receive assignments, feedback, and scheduled training sessions.', unavailable: 'This Class is not accepting your enrollment. Ask the instructor for an invitation.'},
    },
    vocalContributions: {
        title: 'Vocal Contributions',
        addVocal: 'Add Vocal',
        tags: {
            popular: 'Popular',
            popularDescription: 'Most popular albums',
            new: 'New',
            newDescription: 'Newly Added',
        },
        table: {
            title: 'Title',
            dateCreated: 'Date Created',
            plays: 'Plays',
            votes: 'Votes',
            vocal: 'Vocal',
            actions: 'Actions',
        },
        actions: {
            editEntry: 'Edit Entry',
            delete: 'Delete',
        },
        toast: {
            addedToRehearsals: 'Song Added to Rehearsals',
        },
    },
};

export default en;
